import React, { useState, useEffect, useCallback, useMemo, useRef, createContext, useContext } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { 
  Phone, PhoneOff, PhoneMissed, RefreshCw, Calendar, Clock, TrendingUp, Database,
  ChevronLeft, ChevronRight, Search, Filter, Download, Sun, Moon, Volume2,
  User, Users, Plus, Pencil, Trash2, X, Lock, Eye, EyeOff, LogOut, Settings,
  Columns3, CheckSquare, Square, PhoneIncoming, PhoneOutgoing, Key, Copy, RotateCw,
  FileText, Play, Pause, SkipBack, SkipForward, Volume1, VolumeX, Mic, MessageSquare,
  BarChart3, FileSpreadsheet, Smartphone, Zap, Shield, FilterX
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Constants (also exported from ./utils/constants.js)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ALL_COLUMNS = [
  { id: "tarih", label: "Tarih", default: true },
  { id: "yon", label: "Yön", default: true },
  { id: "agent_name", label: "Agent", default: true },
  { id: "arayan_numara", label: "Arayan", default: true },
  { id: "aranan_numara", label: "Aranan", default: true },
  { id: "konusma_suresi", label: "Konuşma Süresi", default: true },
  { id: "durum", label: "Durum", default: true },
  { id: "sistem", label: "Sistem", default: true },
  { id: "device_id", label: "Cihaz ID", default: true },
  { id: "did", label: "Departman", default: false },
  { id: "transkript", label: "Transkript", default: true },
  { id: "recording", label: "Ses Kaydı", default: true, requiresPermission: true }
];

// Auth Context (also exported from ./contexts/AuthContext.js)
const AuthContext = createContext(null);
const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (kullanici_adi, sifre) => {
    const response = await axios.post(`${API}/auth/login`, { kullanici_adi, sifre });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Helper functions (also exported from ./utils/helpers.js)
const formatDuration = (seconds) => {
  if (!seconds) return "0s";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return mins === 0 ? `${secs}s` : `${mins}dk ${secs}s`;
};

const getTodayStr = () => new Date().toISOString().split("T")[0];
const getYesterdayStr = () => { const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().split("T")[0]; };
const getLast7DaysStr = () => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().split("T")[0]; };
const getThisMonthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split("T")[0]; };
const getLastMonthRange = () => {
  const d = new Date();
  const start = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  const end = new Date(d.getFullYear(), d.getMonth(), 0);
  return { start: start.toISOString().split("T")[0], end: end.toISOString().split("T")[0] };
};

// Normalize Turkish phone numbers for search
// 05337342819 → 5337342819, 905337342819 → 5337342819, +905337342819 → 5337342819
const normalizeTurkishPhone = (phone) => {
  if (!phone) return phone;
  let normalized = phone.replace(/\s+/g, '').replace(/[^\d]/g, ''); // Remove spaces and non-digits
  
  // Turkish mobile numbers start with 5 (after country code)
  // Remove leading +90, 90, or 0 if followed by 5 (Turkish mobile)
  if (normalized.startsWith('90') && normalized.length > 2 && normalized[2] === '5') {
    normalized = normalized.substring(2); // Remove 90
  } else if (normalized.startsWith('0') && normalized.length > 1 && normalized[1] === '5') {
    normalized = normalized.substring(1); // Remove leading 0
  }
  
  return normalized;
};

// Common Components (also exported from ./components/common/)
const StatusBadge = ({ durum, durum_aciklama, isDark }) => {
  const getClass = () => {
    if (durum === "1") return isDark ? "status-cevaplanan" : "bg-emerald-100 text-emerald-700 ring-emerald-200";
    if (durum === "3") return isDark ? "status-mesgul" : "bg-rose-100 text-rose-700 ring-rose-200";
    return isDark ? "status-cevap-yok" : "bg-amber-100 text-amber-700 ring-amber-200";
  };
  const label = durum_aciklama === "cevaplanan" ? "Cevaplandı" : durum_aciklama === "mesgul" ? "Meşgul" : "Cevapsız";
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ring-1 ${getClass()}`}>{label}</span>;
};

// Agent color palette - each agent gets a unique color based on name
const AGENT_COLORS = [
  { bg: "bg-purple-500/20", text: "text-purple-400", bgLight: "bg-purple-100", textLight: "text-purple-700" },
  { bg: "bg-cyan-500/20", text: "text-cyan-400", bgLight: "bg-cyan-100", textLight: "text-cyan-700" },
  { bg: "bg-emerald-500/20", text: "text-emerald-400", bgLight: "bg-emerald-100", textLight: "text-emerald-700" },
  { bg: "bg-amber-500/20", text: "text-amber-400", bgLight: "bg-amber-100", textLight: "text-amber-700" },
  { bg: "bg-rose-500/20", text: "text-rose-400", bgLight: "bg-rose-100", textLight: "text-rose-700" },
  { bg: "bg-blue-500/20", text: "text-blue-400", bgLight: "bg-blue-100", textLight: "text-blue-700" },
  { bg: "bg-indigo-500/20", text: "text-indigo-400", bgLight: "bg-indigo-100", textLight: "text-indigo-700" },
  { bg: "bg-pink-500/20", text: "text-pink-400", bgLight: "bg-pink-100", textLight: "text-pink-700" },
  { bg: "bg-teal-500/20", text: "text-teal-400", bgLight: "bg-teal-100", textLight: "text-teal-700" },
  { bg: "bg-orange-500/20", text: "text-orange-400", bgLight: "bg-orange-100", textLight: "text-orange-700" },
];

const getAgentColor = (agentName, isDark) => {
  if (!agentName) return isDark ? "bg-slate-800 text-slate-400" : "bg-gray-100 text-gray-500";
  // Generate hash from agent name
  let hash = 0;
  for (let i = 0; i < agentName.length; i++) {
    hash = agentName.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % AGENT_COLORS.length;
  const color = AGENT_COLORS[index];
  return isDark ? `${color.bg} ${color.text}` : `${color.bgLight} ${color.textLight}`;
};

const StatCard = ({ icon: Icon, title, value, subtitle, color = "blue", isDark }) => {
  const colors = {
    blue: isDark ? "text-blue-400" : "text-blue-600",
    green: isDark ? "text-emerald-400" : "text-emerald-600",
    amber: isDark ? "text-amber-400" : "text-amber-600",
    red: isDark ? "text-rose-400" : "text-rose-600",
    cyan: isDark ? "text-cyan-400" : "text-cyan-600",
  };
  return (
    <Card className={`card-hover ${isDark ? "bg-slate-950/50 border-slate-800" : "bg-white border-gray-200 shadow-sm"}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-sm mb-1 ${isDark ? "text-slate-400" : "text-gray-500"}`}>{title}</p>
            <p className={`text-2xl font-bold font-mono ${colors[color]}`}>{value}</p>
            {subtitle && <p className={`text-xs mt-1 ${isDark ? "text-slate-500" : "text-gray-400"}`}>{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-lg ${isDark ? "bg-slate-800/50" : "bg-gray-100"} ${colors[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// LoginPage (also in ./components/pages/LoginPage.jsx)
const LoginPage = ({ isDark, onToggleTheme }) => {
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Kullanıcı adı ve şifre gerekli");
      return;
    }
    setLoading(true);
    try {
      await login(username, password);
      toast.success("Giriş başarılı");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Giriş başarısız");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center ${isDark ? "bg-[#020617]" : "bg-gray-50"}`}>
      <div className="absolute top-4 right-4">
        <Button variant="ghost" size="icon" onClick={onToggleTheme} className={isDark ? "text-slate-400" : "text-gray-600"}>
          {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>
      </div>
      
      <Card className={`w-full max-w-md mx-4 ${isDark ? "bg-slate-900 border-slate-800" : "bg-white border-gray-200"}`}>
        <CardHeader className="text-center pb-2">
          <div className={`w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center ${isDark ? "bg-blue-600 glow-blue" : "bg-blue-500"}`}>
            <Phone className="w-8 h-8 text-white" />
          </div>
          <CardTitle className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>E-CDR</CardTitle>
          <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>Çağrı Kayıt Yönetim Paneli</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label className={isDark ? "text-slate-300" : "text-gray-700"}>Kullanıcı Adı</Label>
              <div className="relative">
                <User className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? "text-slate-500" : "text-gray-400"}`} />
                <Input value={username} onChange={(e) => setUsername(e.target.value)} className={`pl-10 ${isDark ? "bg-slate-950 border-slate-800 text-white" : "bg-white border-gray-300"}`} placeholder="Kullanıcı adınız" data-testid="login-username-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className={isDark ? "text-slate-300" : "text-gray-700"}>Şifre</Label>
              <div className="relative">
                <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? "text-slate-500" : "text-gray-400"}`} />
                <Input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} className={`pl-10 pr-10 ${isDark ? "bg-slate-950 border-slate-800 text-white" : "bg-white border-gray-300"}`} placeholder="••••••••" data-testid="login-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="login-submit-btn">
              {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Audio Player Modal (also in ./components/modals/AudioPlayerModal.jsx)
const AudioPlayerModal = ({ isOpen, onClose, audioUrl, recordInfo, isDark, onOpenTranscript }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const audioRef = useRef(null);

  // Reset state via callback rather than useEffect
  const handleClose = useCallback(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    onClose();
  }, [onClose]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    if (audioRef.current) {
      audioRef.current.currentTime = percent * duration;
    }
  };

  const skip = (seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, Math.min(duration, audioRef.current.currentTime + seconds));
    }
  };

  const formatTime = (time) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const changePlaybackRate = () => {
    const rates = [0.5, 0.75, 1, 1.25, 1.5, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const nextRate = rates[(currentIndex + 1) % rates.length];
    setPlaybackRate(nextRate);
    if (audioRef.current) {
      audioRef.current.playbackRate = nextRate;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className={`max-w-2xl ${isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white"}`}>
        <DialogHeader>
          <DialogTitle className={isDark ? "text-white" : "text-gray-900"}>
            <Volume2 className="w-5 h-5 inline mr-2 text-blue-500" />Ses Kaydı Oynatıcı
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {recordInfo && (
            <div className={`p-4 rounded-lg ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <PhoneIncoming className={`w-4 h-4 ${isDark ? "text-green-400" : "text-green-600"}`} />
                  <span className={isDark ? "text-slate-400" : "text-gray-500"}>Arayan:</span>
                  <span className="font-mono font-medium">{recordInfo.arayan_numara}</span>
                </div>
                <div className="flex items-center gap-2">
                  <PhoneOutgoing className={`w-4 h-4 ${isDark ? "text-blue-400" : "text-blue-600"}`} />
                  <span className={isDark ? "text-slate-400" : "text-gray-500"}>Aranan:</span>
                  <span className="font-mono font-medium">{recordInfo.aranan_numara}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className={`w-4 h-4 ${isDark ? "text-slate-400" : "text-gray-500"}`} />
                  <span className={isDark ? "text-slate-400" : "text-gray-500"}>Tarih:</span>
                  <span className="font-mono">{recordInfo.tarih}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className={`w-4 h-4 ${isDark ? "text-slate-400" : "text-gray-500"}`} />
                  <span className={isDark ? "text-slate-400" : "text-gray-500"}>Süre:</span>
                  <span className="font-mono">{formatDuration(recordInfo.konusma_suresi)}</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Waveform-like Progress Bar */}
          <div className={`p-4 rounded-xl ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
            <div 
              className="h-16 relative cursor-pointer rounded-lg overflow-hidden"
              onClick={handleSeek}
              style={{
                background: isDark 
                  ? 'linear-gradient(180deg, rgba(59,130,246,0.1) 0%, rgba(59,130,246,0.05) 100%)'
                  : 'linear-gradient(180deg, rgba(59,130,246,0.1) 0%, rgba(59,130,246,0.05) 100%)'
              }}
            >
              {/* Progress */}
              <div 
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500/30 to-cyan-500/30"
                style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
              />
              {/* Fake waveform bars */}
              <div className="absolute inset-0 flex items-center justify-around px-2">
                {Array.from({ length: 50 }).map((_, i) => (
                  <div
                    key={i}
                    className={`w-1 rounded-full transition-all ${
                      i < (currentTime / duration) * 50
                        ? isDark ? "bg-blue-400" : "bg-blue-500"
                        : isDark ? "bg-slate-600" : "bg-gray-300"
                    }`}
                    style={{ 
                      height: `${20 + Math.sin(i * 0.5) * 15 + Math.sin(i * 0.3) * 20}%`,
                      opacity: i < (currentTime / duration) * 50 ? 1 : 0.5
                    }}
                  />
                ))}
              </div>
            </div>
            
            {/* Time Display */}
            <div className="flex justify-between mt-2 text-sm font-mono">
              <span className={isDark ? "text-slate-400" : "text-gray-500"}>{formatTime(currentTime)}</span>
              <span className={isDark ? "text-slate-400" : "text-gray-500"}>{formatTime(duration)}</span>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => skip(-10)} className={isDark ? "text-slate-300" : ""}>
              <SkipBack className="w-5 h-5" />
            </Button>
            <Button 
              onClick={togglePlay}
              className={`w-14 h-14 rounded-full ${isDark ? "bg-blue-600 hover:bg-blue-500" : "bg-blue-500 hover:bg-blue-600"} text-white`}
            >
              {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => skip(10)} className={isDark ? "text-slate-300" : ""}>
              <SkipForward className="w-5 h-5" />
            </Button>
          </div>

          {/* Additional Controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {volume > 0 ? <Volume1 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={(e) => {
                  setVolume(parseFloat(e.target.value));
                  if (audioRef.current) audioRef.current.volume = parseFloat(e.target.value);
                }}
                className="w-20 h-1 bg-gray-300 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            <Button variant="outline" size="sm" onClick={changePlaybackRate} className={isDark ? "border-slate-700" : ""}>
              {playbackRate}x
            </Button>
            {onOpenTranscript && recordInfo && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => { onClose(); onOpenTranscript(recordInfo); }}
                className={`${isDark ? "border-slate-700 text-cyan-400" : "text-cyan-600"}`}
              >
                <FileText className="w-4 h-4 mr-1" />Transkript
              </Button>
            )}
          </div>

          <audio 
            ref={audioRef}
            src={audioUrl}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onEnded={() => setIsPlaying(false)}
            className="hidden"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Transcript Audio Player Component
const TranscriptAudioPlayer = ({ audioUrl, isDark, duration: initialDuration }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(initialDuration || 0);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = React.useRef(null);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    if (audioRef.current) {
      audioRef.current.currentTime = percent * duration;
    }
  };

  const skip = (seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, Math.min(duration, audioRef.current.currentTime + seconds));
    }
  };

  const formatTime = (time) => {
    if (!time || isNaN(time)) return "0:00";
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const changePlaybackRate = () => {
    const rates = [0.5, 0.75, 1, 1.25, 1.5, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const nextRate = rates[(currentIndex + 1) % rates.length];
    setPlaybackRate(nextRate);
    if (audioRef.current) {
      audioRef.current.playbackRate = nextRate;
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <div className="space-y-3">
      {/* Progress Bar */}
      <div 
        className={`h-2 rounded-full cursor-pointer ${isDark ? "bg-slate-700" : "bg-gray-200"}`}
        onClick={handleSeek}
      >
        <div 
          className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all"
          style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
        />
      </div>

      {/* Time Display */}
      <div className="flex justify-between text-xs font-mono">
        <span className={isDark ? "text-slate-400" : "text-gray-500"}>{formatTime(currentTime)}</span>
        <span className={isDark ? "text-slate-400" : "text-gray-500"}>{formatTime(duration)}</span>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Skip Back */}
          <Button variant="ghost" size="sm" onClick={() => skip(-10)} className={`h-8 w-8 p-0 ${isDark ? "text-slate-300 hover:text-white" : ""}`}>
            <SkipBack className="w-4 h-4" />
          </Button>
          
          {/* Play/Pause */}
          <Button 
            onClick={togglePlay}
            className={`h-10 w-10 rounded-full p-0 ${isDark ? "bg-blue-600 hover:bg-blue-500" : "bg-blue-500 hover:bg-blue-600"} text-white`}
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
          </Button>
          
          {/* Skip Forward */}
          <Button variant="ghost" size="sm" onClick={() => skip(10)} className={`h-8 w-8 p-0 ${isDark ? "text-slate-300 hover:text-white" : ""}`}>
            <SkipForward className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex items-center gap-3">
          {/* Volume */}
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={toggleMute} className={`h-8 w-8 p-0 ${isDark ? "text-slate-300" : ""}`}>
              {isMuted || volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume1 className="w-4 h-4" />}
            </Button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={isMuted ? 0 : volume}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                setVolume(val);
                setIsMuted(val === 0);
                if (audioRef.current) audioRef.current.volume = val;
              }}
              className="w-16 h-1 bg-gray-300 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          {/* Playback Speed */}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={changePlaybackRate} 
            className={`h-8 px-2 text-xs ${isDark ? "border-slate-700 text-slate-300" : ""}`}
          >
            {playbackRate}x
          </Button>
        </div>
      </div>

      <audio 
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
        className="hidden"
      />
    </div>
  );
};

// Agent Değiştir Modal - Cihaza farklı agent atamak için
const ChangeAgentModal = ({ isOpen, onClose, device, agents, currentAgentId, isDark, onSave }) => {
  const [selectedAgentId, setSelectedAgentId] = useState(currentAgentId || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelectedAgentId(currentAgentId || "");
  }, [currentAgentId, isOpen]);

  const handleSave = async () => {
    if (!selectedAgentId) {
      toast.error("Lütfen bir agent seçin");
      return;
    }
    setSaving(true);
    try {
      await onSave(device.id, parseInt(selectedAgentId));
      toast.success("Agent değiştirildi");
      onClose();
    } catch (error) {
      console.error(error);
      toast.error("Agent değiştirilemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm("Bu cihazdan agent atamasını kaldırmak istediğinize emin misiniz?")) return;
    setSaving(true);
    try {
      await onSave(device.id, null); // null = remove assignment
      toast.success("Agent ataması kaldırıldı");
      onClose();
    } catch (error) {
      console.error(error);
      toast.error("İşlem başarısız");
    } finally {
      setSaving(false);
    }
  };

  const inputClass = isDark ? "bg-slate-950 border-slate-800 text-white" : "bg-white border-gray-300";
  const labelClass = isDark ? "text-slate-300" : "";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`max-w-md ${isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white"}`}>
        <DialogHeader>
          <DialogTitle className={isDark ? "text-white" : "text-gray-900"}>Agent Değiştir</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className={`p-3 rounded-lg ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
            <p className={`text-sm ${labelClass}`}>Cihaz: <span className="font-medium">{device?.device_name}</span></p>
            <p className={`text-xs font-mono ${isDark ? "text-cyan-400" : "text-cyan-600"}`}>{device?.device_id}</p>
            {device?.gsm && <p className={`text-xs ${isDark ? "text-amber-400" : "text-amber-600"}`}>GSM: {device.gsm}</p>}
          </div>
          
          <div>
            <Label className={labelClass}>Yeni Agent Seç</Label>
            <select
              value={selectedAgentId}
              onChange={(e) => setSelectedAgentId(e.target.value)}
              className={`w-full h-10 px-3 rounded-md border ${inputClass}`}
            >
              <option value="">-- Agent Seçin --</option>
              {agents.map(a => (
                <option key={a.id} value={a.id}>{a.ad_soyad} ({a.kullanici_adi})</option>
              ))}
            </select>
          </div>
        </div>
        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={handleRemove} disabled={saving} className={`${isDark ? "border-red-800 text-red-400 hover:bg-red-900/50" : "border-red-300 text-red-600 hover:bg-red-50"}`}>
            <Trash2 className="w-4 h-4 mr-1" /> Kaldır
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose} disabled={saving}>İptal</Button>
          <Button onClick={handleSave} disabled={saving || !selectedAgentId} className="bg-blue-600 hover:bg-blue-700 text-white">
            {saving ? "..." : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Device Call Logs Modal - Cihazın çağrı kayıtlarını göster
const DeviceCallLogsModal = ({ isOpen, onClose, device, callLogs, loading, page, totalPages, total, onPageChange, isDark, summary, startDate, endDate, startTime, endTime, onStartDateChange, onEndDateChange, onStartTimeChange, onEndTimeChange, onFilter, dateRange, onDateRangeChange, onPlayRecording }) => {
  const inputClass = isDark ? "bg-slate-800 border-slate-700 text-white" : "bg-white border-gray-300";
  
  const getCallTypeInfo = (type) => {
    switch(parseInt(type)) {
      case 1: return { label: "Gelen", icon: <PhoneIncoming className="w-3 h-3" />, color: isDark ? "text-emerald-400" : "text-emerald-600" };
      case 2: return { label: "Giden", icon: <PhoneOutgoing className="w-3 h-3" />, color: isDark ? "text-blue-400" : "text-blue-600" };
      case 3: return { label: "Cevapsız", icon: <PhoneMissed className="w-3 h-3" />, color: isDark ? "text-red-400" : "text-red-600" };
      default: return { label: "Bilinmiyor", icon: <Phone className="w-3 h-3" />, color: isDark ? "text-slate-400" : "text-gray-500" };
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "0sn";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) return `${mins}dk ${secs}sn`;
    return `${secs}sn`;
  };

  const formatTotalDuration = (seconds) => {
    if (!seconds) return "0dk";
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}sa ${mins}dk`;
    return `${mins}dk`;
  };

  const dateRangeButtons = [
    { value: "all", label: "Tümü" },
    { value: "today", label: "Bugün" },
    { value: "yesterday", label: "Dün" },
    { value: "7days", label: "7 Gün" },
    { value: "custom", label: "Özel" },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`max-w-4xl h-[90vh] sm:h-auto sm:max-h-[90vh] flex flex-col overflow-hidden ${isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white"}`}>
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className={isDark ? "text-white" : "text-gray-900"}>
            <div className="flex items-center gap-2">
              <Smartphone className="w-5 h-5" />
              Çağrı Kayıtları ({total || 0})
            </div>
          </DialogTitle>
        </DialogHeader>
        
        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto min-h-0 space-y-3 pr-1">
          {/* Device Info */}
          <div className={`p-3 rounded-lg flex-shrink-0 ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
            <div className="flex flex-wrap gap-4 text-sm">
              <div><span className={isDark ? "text-slate-400" : "text-gray-500"}>Cihaz:</span> <span className="font-medium">{device?.device_name}</span></div>
              <div><span className={isDark ? "text-slate-400" : "text-gray-500"}>Model:</span> <span className={isDark ? "text-pink-400" : "text-pink-600"}>{device?.device_model}</span></div>
              <div><span className={isDark ? "text-slate-400" : "text-gray-500"}>GSM:</span> <span className={isDark ? "text-amber-400" : "text-amber-600"}>{device?.gsm || '-'}</span></div>
            </div>
            <div className="flex flex-wrap gap-4 mt-1">
              {device?.id && (
                <div className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                  ID: <span className={`font-mono ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>{device.id}</span>
                </div>
              )}
              <div className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                Device ID: <span className={`font-mono ${isDark ? "text-cyan-400" : "text-cyan-600"}`}>{device?.device_id || '-'}</span>
              </div>
            </div>
          </div>

          {/* Date Range Buttons */}
          <div className={`p-3 rounded-lg flex-shrink-0 ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {dateRangeButtons.map((btn) => (
                <Button
                  key={btn.value}
                  size="sm"
                  variant={dateRange === btn.value ? "default" : "outline"}
                  onClick={() => onDateRangeChange(btn.value)}
                  className={`h-7 px-3 text-xs ${
                    dateRange === btn.value 
                      ? (isDark ? "bg-cyan-600 hover:bg-cyan-500 text-white" : "bg-cyan-500 hover:bg-cyan-600 text-white")
                      : (isDark ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "")
                  }`}
                >
                  {btn.label}
                </Button>
              ))}
            </div>
            
            {/* Custom Date/Time Inputs - only show when "Özel" is selected */}
            {dateRange === "custom" && (
              <div className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-t border-slate-700/50">
                <div className="relative">
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => onStartDateChange(e.target.value)}
                    className={`w-32 h-7 text-xs pl-7 ${inputClass} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
                  />
                  <Calendar className={`w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-cyan-400" : "text-blue-500"}`} />
                </div>
                <div className="relative">
                  <Input
                    type="time"
                    value={startTime}
                    onChange={(e) => onStartTimeChange(e.target.value)}
                    className={`w-24 h-7 text-xs pl-7 ${inputClass} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
                  />
                  <Clock className={`w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-cyan-400" : "text-blue-500"}`} />
                </div>
                <span className={isDark ? "text-slate-500" : "text-gray-400"}>-</span>
                <div className="relative">
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => onEndDateChange(e.target.value)}
                    className={`w-32 h-7 text-xs pl-7 ${inputClass} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
                  />
                  <Calendar className={`w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-cyan-400" : "text-blue-500"}`} />
                </div>
                <div className="relative">
                  <Input
                    type="time"
                    value={endTime}
                    onChange={(e) => onEndTimeChange(e.target.value)}
                    className={`w-24 h-7 text-xs pl-7 ${inputClass} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
                  />
                  <Clock className={`w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-cyan-400" : "text-blue-500"}`} />
                </div>
                <Button
                  size="sm"
                  onClick={onFilter}
                  className={`h-7 px-3 ${isDark ? "bg-cyan-600 hover:bg-cyan-500" : "bg-cyan-500 hover:bg-cyan-600"} text-white`}
                >
                  <Search className="w-3.5 h-3.5 mr-1" />Filtrele
                </Button>
              </div>
            )}
          </div>

          {/* Summary Stats */}
          <div className={`grid grid-cols-2 sm:grid-cols-5 gap-2 flex-shrink-0`}>
            <div className={`p-2 rounded-lg text-center ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
              <div className={`text-xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>{summary?.total_calls || 0}</div>
              <div className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Toplam</div>
            </div>
            <div className={`p-2 rounded-lg text-center ${isDark ? "bg-emerald-900/30" : "bg-emerald-50"}`}>
              <div className={`text-xl font-bold flex items-center justify-center gap-1 ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>
                <PhoneIncoming className="w-4 h-4" />{summary?.incoming || 0}
              </div>
              <div className={`text-xs ${isDark ? "text-emerald-400/70" : "text-emerald-600/70"}`}>Gelen</div>
            </div>
            <div className={`p-2 rounded-lg text-center ${isDark ? "bg-blue-900/30" : "bg-blue-50"}`}>
              <div className={`text-xl font-bold flex items-center justify-center gap-1 ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                <PhoneOutgoing className="w-4 h-4" />{summary?.outgoing || 0}
              </div>
              <div className={`text-xs ${isDark ? "text-blue-400/70" : "text-blue-600/70"}`}>Giden</div>
            </div>
            <div className={`p-2 rounded-lg text-center ${isDark ? "bg-red-900/30" : "bg-red-50"}`}>
              <div className={`text-xl font-bold flex items-center justify-center gap-1 ${isDark ? "text-red-400" : "text-red-600"}`}>
                <PhoneMissed className="w-4 h-4" />{summary?.missed || 0}
              </div>
              <div className={`text-xs ${isDark ? "text-red-400/70" : "text-red-600/70"}`}>Cevapsız</div>
            </div>
            <div className={`p-2 rounded-lg text-center ${isDark ? "bg-teal-900/30" : "bg-teal-50"}`}>
              <div className={`text-xl font-bold flex items-center justify-center gap-1 ${isDark ? "text-teal-400" : "text-teal-600"}`}>
                <Clock className="w-4 h-4" />{formatTotalDuration(summary?.total_duration || 0)}
              </div>
              <div className={`text-xs ${isDark ? "text-teal-400/70" : "text-teal-600/70"}`}>Toplam Süre</div>
            </div>
          </div>

          {/* Call Logs Table */}
          <div className="flex-shrink-0">
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <RefreshCw className={`w-6 h-6 animate-spin ${isDark ? "text-cyan-400" : "text-cyan-600"}`} />
                <span className="ml-2">Yükleniyor...</span>
              </div>
            ) : callLogs.length === 0 ? (
              <div className={`text-center py-10 ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                <Phone className="w-12 h-12 mx-auto mb-2 opacity-30" />
                Bu cihaza ait çağrı kaydı bulunamadı
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className={isDark ? "border-slate-800" : ""}>
                      <TableHead className={isDark ? "text-slate-400" : ""}>Tip</TableHead>
                      <TableHead className={isDark ? "text-slate-400" : ""}>Numara</TableHead>
                      <TableHead className={`hidden sm:table-cell ${isDark ? "text-slate-400" : ""}`}>Kişi</TableHead>
                      <TableHead className={isDark ? "text-slate-400" : ""}>Tarih</TableHead>
                      <TableHead className={isDark ? "text-slate-400" : ""}>Süre</TableHead>
                      <TableHead className={`hidden sm:table-cell ${isDark ? "text-slate-400" : ""}`}>Kayıt</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {callLogs.map((log) => {
                      const typeInfo = getCallTypeInfo(log.call_type);
                      return (
                        <TableRow key={log.id} className={isDark ? "border-slate-800/50" : ""}>
                          <TableCell>
                            <span className={`flex items-center gap-1 ${typeInfo.color}`}>
                              {typeInfo.icon}
                              <span className="text-xs hidden sm:inline">{typeInfo.label}</span>
                            </span>
                          </TableCell>
                          <TableCell className={`font-mono text-xs sm:text-sm ${isDark ? "text-amber-400" : "text-amber-600"}`}>
                            {log.phone_number || '-'}
                          </TableCell>
                          <TableCell className={`hidden sm:table-cell ${isDark ? "text-slate-300" : ""}`}>
                            {log.contact_name || '-'}
                          </TableCell>
                          <TableCell className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                            {log.call_date ? new Date(log.call_date).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                          </TableCell>
                          <TableCell className={`text-xs ${isDark ? "text-teal-400" : "text-teal-600"}`}>
                            {formatDuration(log.duration)}
                          </TableCell>
                          <TableCell className="hidden sm:table-cell">
                            {log.recording_path ? (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => onPlayRecording && onPlayRecording(log.recording_path, log)}
                                className={`p-1 h-auto ${isDark ? "text-cyan-400 hover:text-cyan-300 hover:bg-slate-800" : "text-blue-600 hover:text-blue-700 hover:bg-blue-50"}`}
                              >
                                <Volume2 className="w-4 h-4 mr-1" />
                                <span className="text-xs">Dinle</span>
                              </Button>
                            ) : (
                              <span className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>

        {/* Pagination & Close - Fixed at bottom */}
        <div className={`flex-shrink-0 flex items-center justify-between pt-3 border-t ${isDark ? "border-slate-800" : "border-gray-200"}`}>
          <div className="flex items-center gap-2">
            {totalPages > 1 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || loading}
                  onClick={() => onPageChange(page - 1)}
                  className={`h-8 ${isDark ? "border-slate-700 hover:bg-slate-800" : ""}`}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className={`text-sm px-2 ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages || loading}
                  onClick={() => onPageChange(page + 1)}
                  className={`h-8 ${isDark ? "border-slate-700 hover:bg-slate-800" : ""}`}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
          <Button 
            variant="outline" 
            onClick={onClose} 
            className={`h-8 px-4 ${isDark ? "border-slate-700 hover:bg-slate-800" : ""}`}
          >
            Kapat
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Agent Modal
const AgentModal = ({ isOpen, onClose, agent, onSave, isDark, allDevices = [], onDevicesUpdate, preSelectedDevice = null, existingAgentsCount = 0 }) => {
  const [formData, setFormData] = useState({ ad_soyad: "", kullanici_adi: "", sifre: "", gsm: "", status: 1, yetki_durumu: 1 });
  const [saving, setSaving] = useState(false);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [deviceSearch, setDeviceSearch] = useState("");
  const [deviceDropdownOpen, setDeviceDropdownOpen] = useState(false);
  const [loadingDevices, setLoadingDevices] = useState(false);

  // Türkçe karakterleri dönüştür ve kullanıcı adı oluştur
  const generateUsername = (name) => {
    if (!name) return "";
    const turkishMap = {
      'ç': 'c', 'Ç': 'c', 'ğ': 'g', 'Ğ': 'g', 'ı': 'i', 'İ': 'i',
      'ö': 'o', 'Ö': 'o', 'ş': 's', 'Ş': 's', 'ü': 'u', 'Ü': 'u'
    };
    return name
      .split('')
      .map(char => turkishMap[char] || char)
      .join('')
      .toLowerCase()
      .replace(/\s+/g, '')  // Boşlukları kaldır
      .replace(/[^a-z0-9]/g, '');  // Sadece harf ve rakam bırak
  };

  // Ad Soyad değiştiğinde otomatik kullanıcı adı oluştur (sadece yeni agent için)
  const handleAdSoyadChange = (value) => {
    const newUsername = !agent ? generateUsername(value) : formData.kullanici_adi;
    setFormData({ ...formData, ad_soyad: value, kullanici_adi: newUsername });
  };

  // Default bilgi butonu - Agent1, Agent2, ... şeklinde + şifre oluştur
  const setDefaultInfo = () => {
    const nextNumber = existingAgentsCount + 1;
    const defaultName = `Agent${nextNumber}`;
    const defaultUsername = `agent${nextNumber}`;
    // Şifre oluştur
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ 
      ...formData, 
      ad_soyad: defaultName, 
      kullanici_adi: defaultUsername,
      sifre: password
    });
    toast.success(`Default: ${defaultName} / Şifre: ${password}`);
  };

  // Şifre oluştur fonksiyonu (A-Z, a-z, 0-9)
  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, sifre: password });
    toast.success(`Şifre oluşturuldu: ${password}`);
  };

  useEffect(() => {
    if (agent) {
      setFormData({ ad_soyad: agent.ad_soyad || "", kullanici_adi: agent.kullanici_adi || "", sifre: "", gsm: agent.gsm || "", status: agent.status ?? 1, yetki_durumu: agent.yetki_durumu ?? 1 });
      // Load agent's devices
      const loadAgentDevices = async () => {
        setLoadingDevices(true);
        try {
          const res = await axios.get(`${API}/agent-devices?agent_id=${agent.id}`);
          const deviceIds = (res.data.data || []).map(d => d.device_id);
          setSelectedDevices(deviceIds);
        } catch (error) {
          console.error("Load agent devices error:", error);
        } finally {
          setLoadingDevices(false);
        }
      };
      loadAgentDevices();
    } else {
      // Yeni agent - preSelectedDevice varsa kullan
      const gsm = preSelectedDevice?.gsm || "";
      setFormData({ ad_soyad: "", kullanici_adi: "", sifre: "", gsm: gsm, status: 1, yetki_durumu: 1 });
      if (preSelectedDevice?.id) {
        setSelectedDevices([preSelectedDevice.id]);
      } else {
        setSelectedDevices([]);
      }
    }
    setDeviceSearch("");
  }, [agent, isOpen, preSelectedDevice]);

  const handleSave = async () => {
    if (!formData.ad_soyad || !formData.kullanici_adi || (!agent && !formData.sifre)) {
      toast.error("Zorunlu alanları doldurun");
      return;
    }
    setSaving(true);
    try {
      // Include device_ids in formData
      const dataToSave = { ...formData, device_ids: selectedDevices };
      await onSave(dataToSave, agent?.id);
      if (onDevicesUpdate) onDevicesUpdate();
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const toggleDevice = (deviceId) => {
    setSelectedDevices(prev => 
      prev.includes(deviceId) 
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const filteredDevices = allDevices.filter(d => 
    (d.device_id && d.device_id.toLowerCase().includes(deviceSearch.toLowerCase())) ||
    (d.gsm && d.gsm.includes(deviceSearch)) ||
    (d.device_name && d.device_name.toLowerCase().includes(deviceSearch.toLowerCase()))
  );

  const inputClass = isDark ? "bg-slate-950 border-slate-800 text-white" : "bg-white border-gray-300";
  const labelClass = isDark ? "text-slate-300" : "";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`max-w-lg ${isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white"}`}>
        <DialogHeader>
          <DialogTitle className={isDark ? "text-white" : "text-gray-900"}>{agent ? "Agent Düzenle" : "Yeni Agent"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
          {/* Default Bilgi butonu - sadece yeni agent için */}
          {!agent && (
            <div className="flex justify-end">
              <Button type="button" variant="outline" size="sm" onClick={setDefaultInfo} className={`text-xs ${isDark ? "border-emerald-700 text-emerald-400 hover:bg-emerald-900/50" : "border-emerald-300 text-emerald-600 hover:bg-emerald-50"}`}>
                <Zap className="w-3 h-3 mr-1" /> Default Bilgi
              </Button>
            </div>
          )}
          <div>
            <Label className={labelClass}>Ad Soyad *</Label>
            <Input value={formData.ad_soyad} onChange={(e) => handleAdSoyadChange(e.target.value)} className={inputClass} placeholder="Ad Soyad girin" />
          </div>
          <div>
            <Label className={labelClass}>Kullanıcı Adı *</Label>
            <Input value={formData.kullanici_adi} onChange={(e) => setFormData({...formData, kullanici_adi: e.target.value})} className={inputClass} disabled={!!agent} placeholder="Otomatik oluşturulur" />
          </div>
          <div>
            <Label className={labelClass}>{agent ? "Yeni Şifre" : "Şifre *"}</Label>
            <div className="flex gap-2">
              <Input type="text" value={formData.sifre} onChange={(e) => setFormData({...formData, sifre: e.target.value})} className={`flex-1 ${inputClass}`} placeholder="Şifre girin veya oluşturun" />
              <Button type="button" variant="outline" size="sm" onClick={generatePassword} className={`whitespace-nowrap ${isDark ? "border-slate-700 text-slate-300 hover:bg-slate-800" : ""}`}>
                <RefreshCw className="w-4 h-4 mr-1" /> Oluştur
              </Button>
            </div>
          </div>
          <div><Label className={labelClass}>GSM</Label><Input value={formData.gsm} onChange={(e) => setFormData({...formData, gsm: e.target.value})} className={inputClass} /></div>
          
          {/* Device Selection */}
          <div>
            <Label className={labelClass}>
              Cihazlar <span className={isDark ? "text-pink-400" : "text-pink-600"}>({selectedDevices.length} seçili)</span>
            </Label>
            <div className="relative mt-1">
              {/* Selected devices tags */}
              <div 
                className={`min-h-[42px] p-2 rounded-lg border cursor-text flex flex-wrap gap-1.5 items-center ${
                  isDark ? "bg-slate-950 border-slate-800" : "bg-white border-gray-300"
                } ${deviceDropdownOpen ? "ring-2 ring-pink-500" : ""}`}
                onClick={() => setDeviceDropdownOpen(true)}
              >
                {selectedDevices.slice(0, 3).map(deviceId => {
                  const device = allDevices.find(d => d.id === deviceId);
                  return device ? (
                    <span 
                      key={deviceId}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
                        isDark ? "bg-pink-600/30 text-pink-300 border border-pink-500/50" : "bg-pink-100 text-pink-700 border border-pink-300"
                      }`}
                    >
                      {device.gsm || device.device_id?.substring(0, 8)}
                      <button 
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleDevice(deviceId); }}
                        className="hover:text-red-500"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ) : null;
                })}
                {selectedDevices.length > 3 && (
                  <span className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                    +{selectedDevices.length - 3} daha
                  </span>
                )}
                <input
                  type="text"
                  placeholder={loadingDevices ? "Yükleniyor..." : "Cihaz ara (GSM, ID)..."}
                  value={deviceSearch}
                  onChange={(e) => setDeviceSearch(e.target.value)}
                  onFocus={() => setDeviceDropdownOpen(true)}
                  disabled={loadingDevices}
                  className={`flex-1 min-w-[120px] outline-none text-sm bg-transparent ${
                    isDark ? "text-slate-200 placeholder-slate-500" : "text-gray-800 placeholder-gray-400"
                  }`}
                />
              </div>
              
              {/* Dropdown */}
              {deviceDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setDeviceDropdownOpen(false)} />
                  <div className={`absolute z-50 w-full mt-1 rounded-lg border shadow-lg max-h-48 overflow-y-auto ${
                    isDark ? "bg-slate-900 border-slate-700" : "bg-white border-gray-200"
                  }`}>
                    {filteredDevices.length > 0 ? filteredDevices.map(device => (
                      <div
                        key={device.id}
                        onClick={() => toggleDevice(device.id)}
                        className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-xs ${
                          selectedDevices.includes(device.id)
                            ? isDark ? "bg-pink-600/20 text-pink-300" : "bg-pink-50 text-pink-700"
                            : isDark ? "hover:bg-slate-800 text-slate-300" : "hover:bg-gray-50 text-gray-700"
                        }`}
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                          selectedDevices.includes(device.id)
                            ? "bg-pink-600 border-pink-600"
                            : isDark ? "border-slate-600" : "border-gray-300"
                        }`}>
                          {selectedDevices.includes(device.id) && <CheckSquare className="w-3 h-3 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{device.device_name || device.device_model || '-'}</div>
                          <div className={`text-[10px] ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                            GSM: {device.gsm || '-'} | ID: {device.device_id?.substring(0, 12)}...
                          </div>
                        </div>
                      </div>
                    )) : (
                      <div className={`px-3 py-4 text-center text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                        {allDevices.length === 0 ? "Cihaz bulunamadı" : "Aramayla eşleşen cihaz yok"}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className={labelClass}>Durum</Label>
              <Select value={formData.status.toString()} onValueChange={(v) => setFormData({...formData, status: parseInt(v)})}>
                <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  <SelectItem value="1">Aktif</SelectItem>
                  <SelectItem value="0">Pasif</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={labelClass}>Yetki</Label>
              <Select value={formData.yetki_durumu.toString()} onValueChange={(v) => setFormData({...formData, yetki_durumu: parseInt(v)})}>
                <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  <SelectItem value="1">Kullanıcı</SelectItem>
                  <SelectItem value="2">Yönetici</SelectItem>
                  <SelectItem value="3">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>İptal</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">{saving ? "Kaydediliyor..." : "Kaydet"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// User Modal
const UserModal = ({ isOpen, onClose, userToEdit, onSave, isDark, agents = [] }) => {
  const [formData, setFormData] = useState({ 
    ad_soyad: "", kullanici_adi: "", sifre: "", firma_adi: "", email: "", status: 1, 
    is_admin: 0, ses_kaydi_goster: 1, agent_yonetimi: 0, excel_indirme: 1, 
    gorebilecegi_agentlar: "" 
  });
  const [saving, setSaving] = useState(false);
  const [selectedAgents, setSelectedAgents] = useState([]);

  useEffect(() => {
    if (userToEdit) {
      setFormData({ ...userToEdit, sifre: "" });
      // Parse agent IDs from comma-separated string
      if (userToEdit.gorebilecegi_agentlar) {
        setSelectedAgents(userToEdit.gorebilecegi_agentlar.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id)));
      } else {
        setSelectedAgents([]);
      }
    } else {
      setFormData({ 
        ad_soyad: "", kullanici_adi: "", sifre: "", firma_adi: "", email: "", status: 1, 
        is_admin: 0, ses_kaydi_goster: 1, agent_yonetimi: 0, excel_indirme: 1, 
        gorebilecegi_agentlar: "" 
      });
      setSelectedAgents([]);
    }
  }, [userToEdit, isOpen]);

  const toggleAgentSelection = (agentId) => {
    setSelectedAgents(prev => {
      if (prev.includes(agentId)) {
        return prev.filter(id => id !== agentId);
      } else {
        return [...prev, agentId];
      }
    });
  };

  const handleSave = async () => {
    if (!formData.ad_soyad || !formData.kullanici_adi || (!userToEdit && !formData.sifre)) {
      toast.error("Zorunlu alanları doldurun");
      return;
    }
    if (!formData.firma_adi || !formData.firma_adi.trim()) {
      toast.error("Panel/Firma adı zorunludur");
      return;
    }
    setSaving(true);
    try {
      const dataToSave = {
        ...formData,
        gorebilecegi_agentlar: selectedAgents.length > 0 ? selectedAgents.join(',') : ""
      };
      await onSave(dataToSave, userToEdit?.id);
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const inputClass = isDark ? "bg-slate-950 border-slate-800 text-white" : "bg-white border-gray-300";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`max-w-lg ${isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white"}`}>
        <DialogHeader>
          <DialogTitle className={isDark ? "text-white" : "text-gray-900"}>{userToEdit ? "Kullanıcı Düzenle" : "Yeni Kullanıcı"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
          <div><Label className={isDark ? "text-slate-300" : ""}>Ad Soyad *</Label><Input value={formData.ad_soyad} onChange={(e) => setFormData({...formData, ad_soyad: e.target.value})} className={inputClass} /></div>
          <div><Label className={isDark ? "text-slate-300" : ""}>Kullanıcı Adı *</Label><Input value={formData.kullanici_adi} onChange={(e) => setFormData({...formData, kullanici_adi: e.target.value})} className={inputClass} disabled={!!userToEdit} /></div>
          <div><Label className={isDark ? "text-slate-300" : ""}>{userToEdit ? "Yeni Şifre" : "Şifre *"}</Label><Input type="password" value={formData.sifre} onChange={(e) => setFormData({...formData, sifre: e.target.value})} className={inputClass} /></div>
          <div>
            <Label className={isDark ? "text-slate-300" : ""}>Panel/Firma Adı *</Label>
            <Input 
              value={formData.firma_adi || ""} 
              onChange={(e) => setFormData({...formData, firma_adi: e.target.value})} 
              className={inputClass} 
              placeholder="Örn: eTelekom CDR"
            />
            <p className={`text-xs mt-1 ${isDark ? "text-slate-500" : "text-gray-500"}`}>Kullanıcı giriş yaptığında header&apos;da görünecek başlık</p>
          </div>
          <div><Label className={isDark ? "text-slate-300" : ""}>Email</Label><Input value={formData.email || ""} onChange={(e) => setFormData({...formData, email: e.target.value})} className={inputClass} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className={isDark ? "text-slate-300" : ""}>Durum</Label>
              <Select value={(formData.status ?? 1).toString()} onValueChange={(v) => setFormData({...formData, status: parseInt(v)})}>
                <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  <SelectItem value="1">Aktif</SelectItem>
                  <SelectItem value="0">Pasif</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={isDark ? "text-slate-300" : ""}>Admin</Label>
              <Select value={(formData.is_admin ?? 0).toString()} onValueChange={(v) => setFormData({...formData, is_admin: parseInt(v)})}>
                <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  <SelectItem value="0">Hayır</SelectItem>
                  <SelectItem value="1">Evet</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Yetkiler */}
          <div className="space-y-3 pt-2">
            <Label className={`font-semibold ${isDark ? "text-slate-200" : "text-gray-800"}`}>Yetkiler</Label>
            <div className="flex items-center gap-2">
              <Checkbox checked={(formData.ses_kaydi_goster ?? 1) === 1} onCheckedChange={(c) => setFormData({...formData, ses_kaydi_goster: c ? 1 : 0})} />
              <span className={`text-sm ${isDark ? "text-slate-300" : ""}`}>Ses Kayıtlarını Görüntüleyebilir</span>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={(formData.arama_yetkisi ?? 1) === 1} onCheckedChange={(c) => setFormData({...formData, arama_yetkisi: c ? 1 : 0})} />
              <span className={`text-sm ${isDark ? "text-slate-300" : ""}`}>GSM Arama Yapabilir</span>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={(formData.analiz_yetkisi ?? 1) === 1} onCheckedChange={(c) => setFormData({...formData, analiz_yetkisi: c ? 1 : 0})} />
              <span className={`text-sm ${isDark ? "text-slate-300" : ""}`}>Agent Analizi Görüntüleyebilir</span>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={(formData.agent_yonetimi ?? 0) === 1} onCheckedChange={(c) => setFormData({...formData, agent_yonetimi: c ? 1 : 0})} />
              <span className={`text-sm ${isDark ? "text-slate-300" : ""}`}>Agent Yönetimi Yapabilir</span>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={(formData.excel_indirme ?? 1) === 1} onCheckedChange={(c) => setFormData({...formData, excel_indirme: c ? 1 : 0})} />
              <span className={`text-sm ${isDark ? "text-slate-300" : ""}`}>Excel İndirme Yapabilir</span>
            </div>
          </div>

          {/* Agent Seçimi */}
          <div className="space-y-3 pt-2">
            <Label className={`font-semibold ${isDark ? "text-slate-200" : "text-gray-800"}`}>Görebileceği Agentlar</Label>
            <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
              Hiçbiri seçilmezse tüm agentları görebilir
            </p>
            <div className={`max-h-40 overflow-y-auto border rounded-lg p-3 ${isDark ? "border-slate-700 bg-slate-950" : "border-gray-200 bg-gray-50"}`}>
              {agents.length === 0 ? (
                <p className={`text-sm ${isDark ? "text-slate-500" : "text-gray-400"}`}>Agent bulunamadı</p>
              ) : (
                <div className="space-y-2">
                  {agents.map(agent => (
                    <div key={agent.id} className="flex items-center gap-2">
                      <Checkbox 
                        checked={selectedAgents.includes(agent.id)} 
                        onCheckedChange={() => toggleAgentSelection(agent.id)} 
                      />
                      <span className={`text-sm ${isDark ? "text-slate-300" : ""}`}>
                        {agent.ad_soyad} <span className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>({agent.gsm})</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {selectedAgents.length > 0 && (
              <p className={`text-xs ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                {selectedAgents.length} agent seçildi
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>İptal</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">{saving ? "Kaydediliyor..." : "Kaydet"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Dashboard
const Dashboard = ({ isDark, onToggleTheme }) => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("cdr");
  
  // CDR State
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [limit, setLimit] = useState(20);
  
  // Stats Date Filter
  const [statsDateRange, setStatsDateRange] = useState("today");
  const [statsStartDate, setStatsStartDate] = useState(getTodayStr());
  const [statsEndDate, setStatsEndDate] = useState(getTodayStr());
  const [dataRange, setDataRange] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  
  // Filters - Default to today's date for last day records
  const [startDate, setStartDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [startTime, setStartTime] = useState("00:00");
  const [endTime, setEndTime] = useState("23:59");
  const [searchNumber, setSearchNumber] = useState("");
  const [arananNumara, setArananNumara] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [yonFilter, setYonFilter] = useState("all"); // all, 1 (gelen), 2 (giden)
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  
  // Reset all filters to default
  const resetFilters = () => {
    const today = new Date().toISOString().split("T")[0];
    setStartDate(today);
    setEndDate(today);
    setStartTime("00:00");
    setEndTime("23:59");
    setSearchNumber("");
    setArananNumara("");
    setStatusFilter("all");
    setYonFilter("all");
    setSelectedAgent(null);
    setPage(1);
  };
  
  const [agents, setAgents] = useState([]);
  const [users, setUsers] = useState([]);

  // GSM Search State
  const [gsmSearchNumber, setGsmSearchNumber] = useState("");
  const [gsmSearchStartDate, setGsmSearchStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split("T")[0];
  });
  const [gsmSearchEndDate, setGsmSearchEndDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [gsmSearchResults, setGsmSearchResults] = useState([]);
  const [gsmSearchLoading, setGsmSearchLoading] = useState(false);
  const [gsmSearchCount, setGsmSearchCount] = useState(0);
  const [gsmSearchPage, setGsmSearchPage] = useState(1);
  const [gsmSearchLimit, setGsmSearchLimit] = useState(20);
  const [gsmExporting, setGsmExporting] = useState(false);

  // Column visibility - load from localStorage or use defaults
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem("cdr_visible_columns");
    if (saved) return JSON.parse(saved);
    return ALL_COLUMNS.filter(c => c.default).map(c => c.id);
  });
  
  // Export columns - separate from table view
  const [exportColumns, setExportColumns] = useState(() => {
    const saved = localStorage.getItem("cdr_export_columns");
    if (saved) return JSON.parse(saved);
    return ALL_COLUMNS.filter(c => c.default && c.id !== "recording").map(c => c.id);
  });
  
  const [exportModalOpen, setExportModalOpen] = useState(false);

  // Modals
  const [audioModalOpen, setAudioModalOpen] = useState(false);
  const [selectedAudio, setSelectedAudio] = useState({ url: "", record: null });
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  // Device Agent Assignment
  const [preSelectedDeviceForAgent, setPreSelectedDeviceForAgent] = useState(null);
  const [deviceAgentMap, setDeviceAgentMap] = useState({}); // device_id -> agent info
  const [changeAgentModalOpen, setChangeAgentModalOpen] = useState(false);
  const [selectedDeviceForChange, setSelectedDeviceForChange] = useState(null);
  
  // Token Modal
  const [tokenModalOpen, setTokenModalOpen] = useState(false);
  const [selectedTokenAgent, setSelectedTokenAgent] = useState(null);
  
  // Agent Search
  const [agentSearchTerm, setAgentSearchTerm] = useState("");

  // Transcript State
  const [selectedTranscriptRecord, setSelectedTranscriptRecord] = useState(null);
  const [transcripts, setTranscripts] = useState([]);
  const [transcriptsLoading, setTranscriptsLoading] = useState(false);
  const [transcriptPage, setTranscriptPage] = useState(1);
  const [transcriptTotal, setTranscriptTotal] = useState(0);
  const [transcriptFilter, setTranscriptFilter] = useState("all"); // all, has, none
  const [transcriptSearchTerm, setTranscriptSearchTerm] = useState("");

  // Agent Analytics State
  const [analyticsAgent, setAnalyticsAgent] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsDateRange, setAnalyticsDateRange] = useState("today"); // today, yesterday, this_week, this_month, custom
  const [analyticsCustomStart, setAnalyticsCustomStart] = useState("");
  const [analyticsCustomEnd, setAnalyticsCustomEnd] = useState("");

  // Reports State
  const [reportsData, setReportsData] = useState(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsStartDate, setReportsStartDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [reportsEndDate, setReportsEndDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [reportsSelectedAgents, setReportsSelectedAgents] = useState([]);
  const [reportsYon, setReportsYon] = useState("all"); // all, 1=Gelen, 2=Giden
  const [reportsAgentSearch, setReportsAgentSearch] = useState("");
  const [reportsAgentDropdownOpen, setReportsAgentDropdownOpen] = useState(false);

  // Reports Daily Detail Modal State
  const [reportDetailAgent, setReportDetailAgent] = useState(null);
  const [reportDetailData, setReportDetailData] = useState(null);
  const [reportDetailLoading, setReportDetailLoading] = useState(false);

  // Devices State
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesSearch, setDevicesSearch] = useState("");
  const [devicesPage, setDevicesPage] = useState(1);
  const [devicesTotalPages, setDevicesTotalPages] = useState(1);
  const [devicesTotal, setDevicesTotal] = useState(0);
  const [editingDeviceGsm, setEditingDeviceGsm] = useState({}); // {deviceId: gsmValue}
  
  // Device Call Logs Modal
  const [callLogsModalOpen, setCallLogsModalOpen] = useState(false);
  const [selectedDeviceForLogs, setSelectedDeviceForLogs] = useState(null);
  const [callLogs, setCallLogs] = useState([]);
  const [callLogsLoading, setCallLogsLoading] = useState(false);
  const [callLogsPage, setCallLogsPage] = useState(1);
  const [callLogsTotalPages, setCallLogsTotalPages] = useState(1);
  const [callLogsTotal, setCallLogsTotal] = useState(0);
  const [callLogsSummary, setCallLogsSummary] = useState({ total_calls: 0, incoming: 0, outgoing: 0, missed: 0, total_duration: 0 });
  const [callLogsStartDate, setCallLogsStartDate] = useState("");
  const [callLogsEndDate, setCallLogsEndDate] = useState("");
  const [callLogsStartTime, setCallLogsStartTime] = useState("00:00");
  const [callLogsEndTime, setCallLogsEndTime] = useState("23:59");
  const [callLogsDateRange, setCallLogsDateRange] = useState("all");

  // Agent-Device State
  const [agentDeviceCounts, setAgentDeviceCounts] = useState({});
  const [allDevicesList, setAllDevicesList] = useState([]);

  // Permissions
  const canSeeRecordings = user?.ses_kaydi_goster === 1;
  const canManageAgents = user?.agent_yonetimi === 1;
  const canExport = user?.excel_indirme === 1;
  const canSearch = user?.arama_yetkisi === 1 || user?.is_admin === 1;
  const canViewAnalytics = user?.agent_analiz_yetkisi === 1 || user?.is_admin === 1;
  const isAdmin = user?.is_admin === 1;
  
  // Filter agents based on user permissions
  const visibleAgents = React.useMemo(() => {
    if (isAdmin || !user?.gorebilecegi_agentlar) {
      return agents; // Admin sees all, or no restrictions
    }
    const allowedIds = user.gorebilecegi_agentlar.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
    if (allowedIds.length === 0) return agents; // Empty means all
    return agents.filter(a => allowedIds.includes(a.id));
  }, [agents, user, isAdmin]);

  // Check if user has agent restrictions (cannot create new agents)
  const hasAgentRestrictions = !isAdmin && user?.gorebilecegi_agentlar && user.gorebilecegi_agentlar.trim() !== "";

  // Filtered agents based on search
  const filteredAgents = React.useMemo(() => {
    if (!agentSearchTerm.trim()) return visibleAgents;
    const term = agentSearchTerm.toLowerCase();
    return visibleAgents.filter(a => 
      a.ad_soyad?.toLowerCase().includes(term) ||
      a.kullanici_adi?.toLowerCase().includes(term) ||
      a.gsm?.toLowerCase().includes(term)
    );
  }, [visibleAgents, agentSearchTerm]);

  // Save column preferences to localStorage
  useEffect(() => {
    localStorage.setItem("cdr_visible_columns", JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    localStorage.setItem("cdr_export_columns", JSON.stringify(exportColumns));
  }, [exportColumns]);

  // Toggle column visibility
  const toggleColumn = (columnId) => {
    setVisibleColumns(prev => 
      prev.includes(columnId) 
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
  };

  // Toggle export column
  const toggleExportColumn = (columnId) => {
    setExportColumns(prev => 
      prev.includes(columnId) 
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
  };

  // Check if column is visible
  const isColumnVisible = (columnId) => {
    if (columnId === "recording" && !canSeeRecordings) return false;
    return visibleColumns.includes(columnId);
  };

  // Handle stats date range change
  const handleStatsDateChange = (range) => {
    // First update the range type
    setStatsDateRange(range);
    
    // Reset time to full day
    setStartTime("00:00");
    setEndTime("23:59");
    
    // Then update dates based on selection (update CDR filters)
    switch (range) {
      case "today":
        setStartDate(getTodayStr());
        setEndDate(getTodayStr());
        break;
      case "yesterday":
        setStartDate(getYesterdayStr());
        setEndDate(getYesterdayStr());
        break;
      case "last7":
        setStartDate(getLast7DaysStr());
        setEndDate(getTodayStr());
        break;
      case "thisMonth":
        setStartDate(getThisMonthStart());
        setEndDate(getTodayStr());
        break;
      case "lastMonth":
        const { start, end } = getLastMonthRange();
        setStartDate(start);
        setEndDate(end);
        break;
      case "custom":
        // Don't change dates for custom - user will set them
        break;
      default:
        break;
    }
    setPage(1);
  };

  // Separate effect for agents loading
  useEffect(() => {
    const loadAgents = async () => {
      try {
        const response = await axios.get(`${API}/agents`, { timeout: 30000 });
        setAgents(response.data || []);
      } catch (err) {
        console.error('Agents load error:', err);
      }
    };
    loadAgents();
  }, []);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const rangeRes = await axios.get(`${API}/data-range`, { timeout: 30000 });
        
        // Set data range and update default dates if no data today
        const range = rangeRes.data;
        setDataRange(range);
        
        if (range.max_date) {
          const maxDate = range.max_date;
          const today = getTodayStr();
          if (maxDate !== today) {
            setStatsStartDate(maxDate);
            setStatsEndDate(maxDate);
            setStartDate(maxDate);
            setEndDate(maxDate);
          }
        }
      } catch (error) {
        console.error('fetchOptions error:', error);
      } finally {
        setInitialLoading(false);
      }
    };
    fetchOptions();
  }, []);
  
  // Fetch agent device counts
  const fetchAgentDeviceCounts = async () => {
    try {
      const res = await axios.get(`${API}/agent-devices`);
      setAgentDeviceCounts(res.data.data || {});
    } catch (error) {
      console.error("Agent device counts error:", error);
    }
  };
  
  // Fetch all devices for selection
  const fetchAllDevicesList = async () => {
    try {
      const res = await axios.get(`${API}/devices?limit=1000`);
      setAllDevicesList(res.data.data || []);
    } catch (error) {
      console.error("All devices error:", error);
    }
  };
  
  // Fetch device -> agent mapping
  const fetchDeviceAgentMap = async () => {
    try {
      const res = await axios.get(`${API}/device-agent-map`);
      setDeviceAgentMap(res.data.data || {});
    } catch (error) {
      console.error("Device agent map error:", error);
    }
  };
  
  // Initial load of device data
  useEffect(() => {
    fetchAgentDeviceCounts();
    fetchAllDevicesList();
    fetchDeviceAgentMap();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      axios.get(`${API}/users`).then(res => setUsers(res.data || [])).catch(console.error);
    }
  }, [isAdmin]);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
      if (startDate) params.append("start_date", `${startDate} ${startTime}:00`);
      if (endDate) params.append("end_date", `${endDate} ${endTime}:59`);
      if (searchNumber) params.append("arayan_numara", normalizeTurkishPhone(searchNumber));
      if (arananNumara) params.append("aranan_numara", normalizeTurkishPhone(arananNumara));
      if (statusFilter !== "all") params.append("durum", statusFilter);
      if (yonFilter !== "all") params.append("yon", yonFilter);
      if (selectedAgent) params.append("agent_id", selectedAgent.id);
      
      const [recordsRes, countRes] = await Promise.all([
        axios.get(`${API}/cdr?${params}`, { timeout: 60000 }),
        axios.get(`${API}/cdr/count?${params}`, { timeout: 60000 })
      ]);
      
      setRecords(recordsRes.data);
      setTotalCount(countRes.data.count);
    } catch (error) {
      console.error("CDR fetch error:", error);
      if (error.code === 'ECONNABORTED') {
        toast.error("Bağlantı zaman aşımı - lütfen tekrar deneyin");
      } else {
        toast.error("Kayıtlar yüklenirken hata");
      }
    } finally {
      setLoading(false);
    }
  }, [page, limit, startDate, endDate, startTime, endTime, searchNumber, arananNumara, statusFilter, yonFilter, selectedAgent]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const params = new URLSearchParams();
      // Use CDR filter dates and times for stats too
      if (startDate) params.append("start_date", `${startDate} ${startTime}:00`);
      if (endDate) params.append("end_date", `${endDate} ${endTime}:59`);
      if (selectedAgent) params.append("agent_id", selectedAgent.id);
      // Add phone number filters with Turkish number normalization
      if (searchNumber) params.append("arayan_numara", normalizeTurkishPhone(searchNumber));
      if (arananNumara) params.append("aranan_numara", normalizeTurkishPhone(arananNumara));
      const response = await axios.get(`${API}/stats?${params}`, { timeout: 60000 });
      setStats(response.data);
    } catch (error) {
      console.error("Stats fetch error:", error);
      if (error.code === 'ECONNABORTED') {
        toast.error("İstatistik yüklemesi zaman aşımı");
      }
    } finally {
      setStatsLoading(false);
    }
  }, [startDate, endDate, startTime, endTime, selectedAgent, searchNumber, arananNumara]);

  const handleExport = async () => {
    setExporting(true);
    setExportModalOpen(false);
    setExportMenuOpen(false);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", `${startDate} ${startTime}:00`);
      if (endDate) params.append("end_date", `${endDate} ${endTime}:59`);
      if (searchNumber) params.append("arayan_numara", normalizeTurkishPhone(searchNumber));
      if (arananNumara) params.append("aranan_numara", normalizeTurkishPhone(arananNumara));
      if (statusFilter !== "all") params.append("durum", statusFilter);
      if (yonFilter !== "all") params.append("yon", yonFilter);
      if (selectedAgent) params.append("agent_id", selectedAgent.id);
      
      const response = await axios.get(`${API}/cdr/export?${params}`, { timeout: 60000 });
      const data = response.data.data;
      
      if (data.length === 0) { toast.warning("Export edilecek kayıt yok"); return; }
      
      // Build export data based on selected columns
      const exportData = data.map(r => {
        const row = {};
        if (exportColumns.includes("tarih")) row["Tarih"] = r.tarih;
        if (exportColumns.includes("yon")) row["Yön"] = r.yon === 1 ? "Gelen" : r.yon === 2 ? "Giden" : "-";
        if (exportColumns.includes("arayan_numara")) row["Arayan"] = r.arayan_numara;
        if (exportColumns.includes("aranan_numara")) row["Aranan"] = r.aranan_numara;
        if (exportColumns.includes("konusma_suresi")) row["Konuşma (sn)"] = r.konusma_suresi;
        if (exportColumns.includes("did")) row["Departman"] = r.did;
        if (exportColumns.includes("durum")) row["Durum"] = r.durum_aciklama === "cevaplanan" ? "Cevaplandı" : r.durum_aciklama === "mesgul" ? "Meşgul" : "Cevapsız";
        if (exportColumns.includes("transkript")) row["Transkript"] = r.transkript === 1 ? "Var" : "Yok";
        if (exportColumns.includes("recording") && canSeeRecordings) row["Ses Kaydı"] = r.recording || "";
        return row;
      });
      
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "CDR");
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(new Blob([buf]), `CDR_${startDate}_${endDate}.xlsx`);
      toast.success(`${data.length} kayıt export edildi`);
    } catch (error) {
      toast.error("Export hatası");
    } finally {
      setExporting(false);
    }
  };

  // Export CDR records to PDF
  const handleExportPdf = async () => {
    setExporting(true);
    setExportMenuOpen(false);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", `${startDate} ${startTime}:00`);
      if (endDate) params.append("end_date", `${endDate} ${endTime}:59`);
      if (searchNumber) params.append("arayan_numara", normalizeTurkishPhone(searchNumber));
      if (arananNumara) params.append("aranan_numara", normalizeTurkishPhone(arananNumara));
      if (statusFilter !== "all") params.append("durum", statusFilter);
      if (yonFilter !== "all") params.append("yon", yonFilter);
      if (selectedAgent) params.append("agent_id", selectedAgent.id);
      
      const response = await axios.get(`${API}/cdr/export?${params}`, { timeout: 60000 });
      const data = response.data.data;
      
      if (data.length === 0) { 
        toast.warning("Export edilecek kayıt yok"); 
        setExporting(false);
        return; 
      }
      
      // Build table rows
      const tableRows = data.slice(0, 500).map(r => `
        <tr>
          <td>${r.tarih || '-'}</td>
          <td>${r.yon === 1 ? 'Gelen' : r.yon === 2 ? 'Giden' : '-'}</td>
          <td>${r.arayan_numara || '-'}</td>
          <td>${r.aranan_numara || '-'}</td>
          <td>${formatDuration(r.konusma_suresi)}</td>
          <td>${r.durum_aciklama === "cevaplanan" ? "Cevaplandı" : r.durum_aciklama === "mesgul" ? "Meşgul" : "Cevapsız"}</td>
        </tr>
      `).join('');

      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>E-CDR Çağrı Kayıtları</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #333; font-size: 11px; }
            .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #3b82f6; padding-bottom: 15px; }
            .header h1 { color: #3b82f6; margin: 0; font-size: 20px; }
            .header p { color: #666; margin: 5px 0 0; font-size: 12px; }
            .info { margin-bottom: 15px; font-size: 11px; color: #666; }
            table { width: 100%; border-collapse: collapse; }
            th { background: #3b82f6; color: white; padding: 8px 5px; text-align: left; font-size: 10px; }
            td { padding: 6px 5px; border-bottom: 1px solid #e2e8f0; font-size: 10px; }
            tr:nth-child(even) { background: #f8fafc; }
            .footer { margin-top: 20px; text-align: center; color: #94a3b8; font-size: 10px; }
            @media print { body { padding: 10px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>E-CDR Çağrı Kayıtları</h1>
            <p>${user?.firma_adi || 'E-CDR'}</p>
          </div>
          <div class="info">
            <strong>Tarih:</strong> ${startDate} ${startTime} - ${endDate} ${endTime} | 
            <strong>Toplam:</strong> ${data.length} kayıt ${data.length > 500 ? '(ilk 500 gösteriliyor)' : ''} |
            ${selectedAgent ? `<strong>Agent:</strong> ${selectedAgent.ad_soyad}` : ''}
          </div>
          <table>
            <thead>
              <tr>
                <th>Tarih</th>
                <th>Yön</th>
                <th>Arayan</th>
                <th>Aranan</th>
                <th>Süre</th>
                <th>Durum</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
          <div class="footer">
            E-CDR Çağrı Kayıt Yönetim Sistemi | Rapor: ${new Date().toLocaleString('tr-TR')}
          </div>
        </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        toast.success("PDF yazdırma penceresi açıldı");
      }, 250);
    } catch (error) {
      toast.error("Export hatası");
    } finally {
      setExporting(false);
    }
  };

  // Export stats to Excel
  const handleExportStatsExcel = () => {
    if (!stats) {
      toast.warning("Export edilecek istatistik yok");
      return;
    }

    const dateRangeLabels = {
      today: "Bugün",
      yesterday: "Dün",
      last7: "Son 7 Gün",
      thisMonth: "Bu Ay",
      lastMonth: "Geçen Ay",
      custom: "Özel Tarih"
    };

    // Prepare stats data
    const statsData = [
      { "Bilgi": "Rapor Tarihi", "Değer": new Date().toLocaleString('tr-TR') },
      { "Bilgi": "Tarih Aralığı", "Değer": dateRangeLabels[statsDateRange] || statsDateRange },
      { "Bilgi": "Başlangıç", "Değer": `${startDate} ${startTime}` },
      { "Bilgi": "Bitiş", "Değer": `${endDate} ${endTime}` },
      ...(selectedAgent ? [{ "Bilgi": "Agent", "Değer": selectedAgent.ad_soyad }] : []),
      { "Bilgi": "", "Değer": "" },
      { "Bilgi": "Toplam Çağrı", "Değer": stats.total_calls || 0 },
      { "Bilgi": "Cevaplanan", "Değer": stats.answered_calls || 0 },
      { "Bilgi": "Cevaplanan Oranı (%)", "Değer": stats.total_calls ? ((stats.answered_calls / stats.total_calls) * 100).toFixed(1) : 0 },
      { "Bilgi": "Cevapsız", "Değer": stats.missed_calls || 0 },
      { "Bilgi": "Meşgul", "Değer": stats.busy_calls || 0 },
      { "Bilgi": "Toplam Konuşma (sn)", "Değer": stats.total_talk_time || 0 },
      { "Bilgi": "Ortalama Konuşma (sn)", "Değer": (stats.avg_talk_time || 0).toFixed(1) }
    ];

    const ws = XLSX.utils.json_to_sheet(statsData);
    
    // Set column widths
    ws['!cols'] = [{ wch: 25 }, { wch: 30 }];
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "İstatistikler");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    
    const fileName = selectedAgent 
      ? `Istatistikler_${selectedAgent.ad_soyad}_${startDate}_${endDate}.xlsx`
      : `Istatistikler_${startDate}_${endDate}.xlsx`;
    
    saveAs(new Blob([buf]), fileName);
    toast.success("İstatistikler Excel olarak indirildi");
  };

  // Export stats to PDF
  const handleExportStatsPdf = () => {
    if (!stats) {
      toast.warning("Export edilecek istatistik yok");
      return;
    }

    const dateRangeLabels = {
      today: "Bugün",
      yesterday: "Dün",
      last7: "Son 7 Gün",
      thisMonth: "Bu Ay",
      lastMonth: "Geçen Ay",
      custom: "Özel Tarih"
    };

    // Create HTML content for PDF
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>E-CDR İstatistik Raporu</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #3b82f6; padding-bottom: 20px; }
          .header h1 { color: #3b82f6; margin: 0; font-size: 24px; }
          .header p { color: #666; margin: 5px 0 0; }
          .info-section { margin-bottom: 30px; background: #f8fafc; padding: 15px; border-radius: 8px; }
          .info-section h3 { margin: 0 0 10px; color: #3b82f6; font-size: 14px; }
          .info-row { display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #e2e8f0; }
          .info-row:last-child { border-bottom: none; }
          .stats-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; }
          .stat-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 20px; text-align: center; }
          .stat-card.green { border-left: 4px solid #10b981; }
          .stat-card.amber { border-left: 4px solid #f59e0b; }
          .stat-card.red { border-left: 4px solid #ef4444; }
          .stat-card.blue { border-left: 4px solid #3b82f6; }
          .stat-card.cyan { border-left: 4px solid #06b6d4; }
          .stat-value { font-size: 28px; font-weight: bold; color: #1e293b; }
          .stat-label { font-size: 12px; color: #64748b; margin-top: 5px; }
          .footer { margin-top: 30px; text-align: center; color: #94a3b8; font-size: 11px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>E-CDR İstatistik Raporu</h1>
          <p>${user?.firma_adi || 'E-CDR'} - Çağrı Merkezi İstatistikleri</p>
        </div>
        
        <div class="info-section">
          <h3>Rapor Bilgileri</h3>
          <div class="info-row"><span>Rapor Tarihi:</span><span>${new Date().toLocaleString('tr-TR')}</span></div>
          <div class="info-row"><span>Tarih Aralığı:</span><span>${dateRangeLabels[statsDateRange] || statsDateRange}</span></div>
          <div class="info-row"><span>Başlangıç:</span><span>${startDate} ${startTime}</span></div>
          <div class="info-row"><span>Bitiş:</span><span>${endDate} ${endTime}</span></div>
          ${selectedAgent ? `<div class="info-row"><span>Agent:</span><span>${selectedAgent.ad_soyad}</span></div>` : ''}
        </div>
        
        <div class="stats-grid">
          <div class="stat-card blue">
            <div class="stat-value">${(stats.total_calls || 0).toLocaleString('tr-TR')}</div>
            <div class="stat-label">Toplam Çağrı</div>
          </div>
          <div class="stat-card green">
            <div class="stat-value">${(stats.answered_calls || 0).toLocaleString('tr-TR')}</div>
            <div class="stat-label">Cevaplanan (${stats.total_calls ? ((stats.answered_calls / stats.total_calls) * 100).toFixed(1) : 0}%)</div>
          </div>
          <div class="stat-card amber">
            <div class="stat-value">${(stats.missed_calls || 0).toLocaleString('tr-TR')}</div>
            <div class="stat-label">Cevapsız</div>
          </div>
          <div class="stat-card red">
            <div class="stat-value">${(stats.busy_calls || 0).toLocaleString('tr-TR')}</div>
            <div class="stat-label">Meşgul</div>
          </div>
          <div class="stat-card cyan">
            <div class="stat-value">${formatDuration(stats.total_talk_time || 0)}</div>
            <div class="stat-label">Toplam Konuşma</div>
          </div>
          <div class="stat-card cyan">
            <div class="stat-value">${formatDuration(Math.round(stats.avg_talk_time || 0))}</div>
            <div class="stat-label">Ortalama Konuşma</div>
          </div>
        </div>
        
        <div class="footer">
          E-CDR Çağrı Kayıt Yönetim Sistemi | ${new Date().getFullYear()}
        </div>
      </body>
      </html>
    `;

    // Open print dialog
    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    
    // Wait for content to load then print
    setTimeout(() => {
      printWindow.print();
      toast.success("PDF yazdırma penceresi açıldı");
    }, 250);
  };

  // GSM Search Functions
  const fetchGsmSearch = useCallback(async () => {
    if (!gsmSearchNumber || gsmSearchNumber.length < 3) {
      toast.error("En az 3 karakter girin");
      return;
    }
    
    setGsmSearchLoading(true);
    try {
      const params = new URLSearchParams({ 
        gsm_numara: gsmSearchNumber,
        page: gsmSearchPage.toString(), 
        limit: gsmSearchLimit.toString() 
      });
      if (gsmSearchStartDate) params.append("start_date", gsmSearchStartDate);
      if (gsmSearchEndDate) params.append("end_date", gsmSearchEndDate + " 23:59:59");
      
      // Make both requests in parallel
      const resultsPromise = axios.get(`${API}/gsm-search?${params}`, { timeout: 30000 });
      const countPromise = axios.get(`${API}/gsm-search/count?${params}`, { timeout: 30000 });
      
      const resultsRes = await resultsPromise;
      const results = resultsRes.data || [];
      
      setGsmSearchResults(results);
      setGsmSearchLoading(false);
      
      if (results.length > 0) {
        setGsmSearchCount(results.length);
        toast.success(`${results.length}+ sonuç bulundu`);
        
        try {
          const countRes = await countPromise;
          setGsmSearchCount(countRes.data?.count || results.length);
        } catch (countError) {
          // Count fetch failed, using results length
        }
      } else {
        setGsmSearchCount(0);
        toast.info("Sonuç bulunamadı");
      }
    } catch (error) {
      console.error("GSM search error:", error);
      if (error.code === 'ECONNABORTED') {
        toast.error("Arama zaman aşımı - lütfen tekrar deneyin");
      } else if (error.response?.status === 401) {
        toast.error("Oturum süresi doldu, lütfen tekrar giriş yapın");
      } else {
        toast.error("Arama sırasında hata oluştu");
      }
      setGsmSearchLoading(false);
    }
  }, [gsmSearchNumber, gsmSearchStartDate, gsmSearchEndDate, gsmSearchPage, gsmSearchLimit]);

  // Fetch Transcripts
  const fetchTranscripts = useCallback(async () => {
    if (!canSeeRecordings) return;
    
    setTranscriptsLoading(true);
    try {
      const params = new URLSearchParams({ page: transcriptPage.toString(), limit: "20" });
      if (transcriptFilter === "has") params.append("has_transcript", "true");
      if (transcriptFilter === "none") params.append("has_transcript", "false");
      
      const res = await axios.get(`${API}/transcripts?${params}`, { timeout: 30000 });
      setTranscripts(res.data.data || []);
      setTranscriptTotal(res.data.total || 0);
    } catch (error) {
      console.error("Transcripts fetch error:", error);
      toast.error("Transkriptler yüklenemedi");
    } finally {
      setTranscriptsLoading(false);
    }
  }, [transcriptPage, transcriptFilter, canSeeRecordings]);

  // Create/Start Transcript
  const handleStartTranscript = async (record) => {
    try {
      await axios.post(`${API}/transcripts`, { cdr_id: record.id, recording_url: record.recording });
      toast.success("Transkript işlemi başlatıldı");
      fetchTranscripts();
    } catch (error) {
      toast.error("Transkript başlatılamadı");
    }
  };

  // View Transcript Detail
  const handleViewTranscript = async (cdrId) => {
    try {
      const res = await axios.get(`${API}/transcripts/${cdrId}`);
      setSelectedTranscriptRecord(res.data);
    } catch (error) {
      toast.error("Transkript detayı yüklenemedi");
    }
  };

  // Agent Analytics Functions
  const getAnalyticsDateRange = useCallback(() => {
    const today = new Date();
    let start, end;
    
    switch (analyticsDateRange) {
      case "today":
        start = end = today.toISOString().split('T')[0];
        break;
      case "yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        start = end = yesterday.toISOString().split('T')[0];
        break;
      case "this_week":
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        start = weekStart.toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      case "this_month":
        start = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      case "last_month":
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        start = lastMonth.toISOString().split('T')[0];
        end = lastMonthEnd.toISOString().split('T')[0];
        break;
      case "custom":
        start = analyticsCustomStart || today.toISOString().split('T')[0];
        end = analyticsCustomEnd || today.toISOString().split('T')[0];
        break;
      default:
        start = end = today.toISOString().split('T')[0];
    }
    return { start, end };
  }, [analyticsDateRange, analyticsCustomStart, analyticsCustomEnd]);

  const fetchAgentAnalytics = useCallback(async () => {
    if (!analyticsAgent) return;
    
    setAnalyticsLoading(true);
    try {
      const { start, end } = getAnalyticsDateRange();
      const res = await axios.get(`${API}/agents/${analyticsAgent.id}/analytics?start_date=${start}&end_date=${end}`, { timeout: 60000 });
      setAnalyticsData(res.data);
    } catch (error) {
      console.error("Analytics fetch error:", error);
      toast.error("Analiz verileri yüklenemedi");
    } finally {
      setAnalyticsLoading(false);
    }
  }, [analyticsAgent, getAnalyticsDateRange]);

  useEffect(() => {
    if (analyticsAgent) {
      fetchAgentAnalytics();
    }
  }, [analyticsAgent, analyticsDateRange, analyticsCustomStart, analyticsCustomEnd, fetchAgentAnalytics]);

  const openAgentAnalytics = (agent) => {
    setAnalyticsAgent(agent);
    setAnalyticsData(null);
  };

  const closeAgentAnalytics = () => {
    setAnalyticsAgent(null);
    setAnalyticsData(null);
  };

  // Reports Functions
  const fetchReports = useCallback(async () => {
    setReportsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("start_date", reportsStartDate);
      params.append("end_date", reportsEndDate);
      if (reportsSelectedAgents.length > 0) {
        params.append("agent_ids", reportsSelectedAgents.join(","));
      } else {
        params.append("agent_ids", "all");
      }
      if (reportsYon !== "all") {
        params.append("yon", reportsYon);
      }
      
      const res = await axios.get(`${API}/agent-report?${params}`, { timeout: 60000 });
      setReportsData(res.data);
    } catch (error) {
      console.error("Reports fetch error:", error);
      toast.error("Rapor verileri yüklenemedi");
    } finally {
      setReportsLoading(false);
    }
  }, [reportsStartDate, reportsEndDate, reportsSelectedAgents, reportsYon]);

  const handleReportsSelectAllAgents = () => {
    if (reportsSelectedAgents.length === agents.length && agents.length > 0) {
      setReportsSelectedAgents([]);
    } else {
      setReportsSelectedAgents(agents.map(a => a.id));
    }
  };

  const handleReportsToggleAgent = (agentId) => {
    setReportsSelectedAgents(prev => 
      prev.includes(agentId) 
        ? prev.filter(id => id !== agentId)
        : [...prev, agentId]
    );
  };

  const formatTalkTime = (seconds) => {
    if (!seconds || seconds === 0) return "0 dk";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours} saat ${minutes} dk`;
    }
    return `${minutes} dk`;
  };

  const handleExportReportsPdf = () => {
    if (!reportsData || !reportsData.data || reportsData.data.length === 0) {
      toast.warning("Export edilecek veri yok");
      return;
    }

    const tableRows = reportsData.data.map(r => `
      <tr>
        <td>${r.agent_name}</td>
        <td>${r.period}</td>
        <td>${r.total_calls}</td>
        <td>${r.answered_calls}</td>
        <td>${r.missed_calls}</td>
        <td>${formatTalkTime(r.total_talk_time)}</td>
        <td>${r.working_days} gün</td>
        <td>${formatTalkTime(r.avg_daily_talk_time)}</td>
      </tr>
    `).join('');

    const totals = reportsData.totals;
    const footerRow = `
      <tr style="font-weight: bold; background: #e2e8f0;">
        <td colspan="2">TOPLAM</td>
        <td>${totals.total_calls}</td>
        <td>${totals.answered_calls}</td>
        <td>${totals.missed_calls}</td>
        <td>${formatTalkTime(totals.total_talk_time)}</td>
        <td>${totals.total_working_days} gün</td>
        <td>${formatTalkTime(totals.avg_daily_talk_time)}</td>
      </tr>
    `;

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>E-CDR Agent Görüşme Raporu</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; color: #333; font-size: 11px; }
          .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #3b82f6; padding-bottom: 15px; }
          .header h1 { color: #3b82f6; margin: 0; font-size: 18px; }
          .header p { color: #666; margin: 5px 0 0; font-size: 12px; }
          .info { margin-bottom: 15px; font-size: 11px; color: #666; }
          table { width: 100%; border-collapse: collapse; }
          th { background: #3b82f6; color: white; padding: 8px 5px; text-align: left; font-size: 10px; }
          td { padding: 6px 5px; border-bottom: 1px solid #e2e8f0; font-size: 10px; }
          tr:nth-child(even) { background: #f8fafc; }
          .footer { margin-top: 20px; text-align: center; color: #94a3b8; font-size: 10px; }
          @media print { body { padding: 10px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Agent Görüşme Raporu</h1>
          <p>${user?.firma_adi || 'E-CDR'}</p>
        </div>
        <div class="info">
          <strong>Tarih:</strong> ${reportsStartDate} - ${reportsEndDate} | 
          <strong>Yön:</strong> ${reportsYon === '1' ? 'Gelen' : reportsYon === '2' ? 'Giden' : 'Tümü'} |
          <strong>Agent Sayısı:</strong> ${reportsData.data.length}
        </div>
        <table>
          <thead>
            <tr>
              <th>Agent İsmi</th>
              <th>Tarih Aralığı</th>
              <th>Toplam Çağrı</th>
              <th>Cevaplanan</th>
              <th>Cevapsız</th>
              <th>Toplam Konuşma</th>
              <th>Çalışma Günü</th>
              <th>Günlük Ort.</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
            ${footerRow}
          </tbody>
        </table>
        <div class="footer">
          E-CDR Çağrı Kayıt Yönetim Sistemi | Rapor: ${new Date().toLocaleString('tr-TR')}
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      toast.success("PDF yazdırma penceresi açıldı");
    }, 250);
  };

  const handleExportReportsExcel = () => {
    if (!reportsData || !reportsData.data || reportsData.data.length === 0) {
      toast.warning("Export edilecek veri yok");
      return;
    }

    const excelData = reportsData.data.map(r => ({
      "Agent İsmi": r.agent_name,
      "Tarih Aralığı": r.period,
      "Toplam Çağrı": r.total_calls,
      "Cevaplanan": r.answered_calls,
      "Cevapsız": r.missed_calls,
      "Toplam Konuşma (sn)": r.total_talk_time,
      "Toplam Konuşma": formatTalkTime(r.total_talk_time),
      "Çalışma Günü": r.working_days,
      "Günlük Ortalama (sn)": r.avg_daily_talk_time,
      "Günlük Ortalama": formatTalkTime(r.avg_daily_talk_time)
    }));

    // Add totals row
    const totals = reportsData.totals;
    excelData.push({
      "Agent İsmi": "TOPLAM",
      "Tarih Aralığı": "",
      "Toplam Çağrı": totals.total_calls,
      "Cevaplanan": totals.answered_calls,
      "Cevapsız": totals.missed_calls,
      "Toplam Konuşma (sn)": totals.total_talk_time,
      "Toplam Konuşma": formatTalkTime(totals.total_talk_time),
      "Çalışma Günü": totals.total_working_days,
      "Günlük Ortalama (sn)": totals.avg_daily_talk_time,
      "Günlük Ortalama": formatTalkTime(totals.avg_daily_talk_time)
    });

    const ws = XLSX.utils.json_to_sheet(excelData);
    ws['!cols'] = [
      { wch: 20 }, { wch: 25 }, { wch: 12 }, { wch: 12 }, { wch: 10 },
      { wch: 18 }, { wch: 18 }, { wch: 12 }, { wch: 18 }, { wch: 15 }
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Agent Raporu");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf]), `Agent_Gorusme_Raporu_${reportsStartDate}_${reportsEndDate}.xlsx`);
    toast.success("Excel dosyası indirildi");
  };

  // Fetch daily detail for a specific agent in reports
  const fetchReportDailyDetail = async (agentRow) => {
    setReportDetailAgent(agentRow);
    setReportDetailLoading(true);
    setReportDetailData(null);
    
    try {
      // Find the agent to get GSM
      const agent = agents.find(a => a.id === agentRow.agent_id);
      if (!agent) {
        toast.error("Agent bulunamadı");
        return;
      }
      
      // Use agent-analytics endpoint
      const res = await axios.get(`${API}/agents/${agentRow.agent_id}/analytics?start_date=${reportsStartDate}&end_date=${reportsEndDate}`, { timeout: 60000 });
      setReportDetailData(res.data);
    } catch (error) {
      console.error("Report detail fetch error:", error);
      toast.error("Günlük detay yüklenemedi");
    } finally {
      setReportDetailLoading(false);
    }
  };

  const closeReportDetail = () => {
    setReportDetailAgent(null);
    setReportDetailData(null);
  };

  // Fetch devices
  const fetchDevices = async () => {
    setDevicesLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", devicesPage.toString());
      params.append("limit", "20");
      if (devicesSearch) {
        params.append("search", devicesSearch);
      }
      
      const res = await axios.get(`${API}/devices?${params}`, { timeout: 30000 });
      setDevices(res.data.data || []);
      setDevicesTotalPages(res.data.total_pages || 1);
      setDevicesTotal(res.data.total || 0);
    } catch (error) {
      console.error("Devices fetch error:", error);
      toast.error("Cihazlar yüklenemedi");
    }
    setDevicesLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab === "devices") {
      fetchDevices();
    }
  }, [activeTab, devicesPage]);

  const handleDevicesSearch = (e) => {
    e.preventDefault();
    setDevicesPage(1);
    fetchDevices();
  };

  // Cihaz GSM güncelle
  const handleUpdateDeviceGsm = async (deviceId, gsm) => {
    try {
      await axios.put(`${API}/devices/${deviceId}`, { gsm });
      toast.success("GSM güncellendi");
      setEditingDeviceGsm(prev => {
        const newState = { ...prev };
        delete newState[deviceId];
        return newState;
      });
      fetchDevices();
    } catch (error) {
      console.error("Update device GSM error:", error);
      toast.error("GSM güncellenemedi");
    }
  };

  // Cihaz sil
  const handleDeleteDevice = async (deviceId) => {
    if (!window.confirm("Bu cihazı silmek istediğinize emin misiniz?")) return;
    try {
      await axios.delete(`${API}/devices/${deviceId}`);
      toast.success("Cihaz silindi");
      fetchDevices();
      fetchDeviceAgentMap();
    } catch (error) {
      console.error("Delete device error:", error);
      toast.error("Cihaz silinemedi");
    }
  };

  // Cihaz çağrı kayıtlarını getir
  const fetchCallLogs = async (deviceId, page = 1, startDate = null, endDate = null, startTime = null, endTime = null) => {
    setCallLogsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("device_id", deviceId);
      params.append("page", page.toString());
      params.append("limit", "15");
      
      // Add date/time filters only if they exist
      const sDate = startDate !== undefined ? startDate : callLogsStartDate;
      const eDate = endDate !== undefined ? endDate : callLogsEndDate;
      const sTime = startTime || callLogsStartTime;
      const eTime = endTime || callLogsEndTime;
      
      if (sDate) params.append("start_date", `${sDate} ${sTime}:00`);
      if (eDate) params.append("end_date", `${eDate} ${eTime}:59`);
      
      const res = await axios.get(`${API}/call-logs?${params}`);
      setCallLogs(res.data.data || []);
      setCallLogsTotalPages(res.data.total_pages || 1);
      setCallLogsTotal(res.data.total || 0);
      setCallLogsPage(page);
      setCallLogsSummary(res.data.summary || { total_calls: 0, incoming: 0, outgoing: 0, missed: 0, total_duration: 0 });
    } catch (error) {
      console.error("Call logs error:", error);
      toast.error("Çağrı kayıtları yüklenemedi");
    }
    setCallLogsLoading(false);
  };

  const handleCallLogsDateRangeChange = (range) => {
    setCallLogsDateRange(range);
    const today = new Date();
    let newStartDate = "";
    let newEndDate = "";
    
    if (range === "all") {
      newStartDate = "";
      newEndDate = "";
    } else if (range === "today") {
      newStartDate = today.toISOString().split('T')[0];
      newEndDate = today.toISOString().split('T')[0];
    } else if (range === "yesterday") {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      newStartDate = yesterday.toISOString().split('T')[0];
      newEndDate = yesterday.toISOString().split('T')[0];
    } else if (range === "7days") {
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      newStartDate = weekAgo.toISOString().split('T')[0];
      newEndDate = today.toISOString().split('T')[0];
    } else if (range === "custom") {
      // Custom - set default to today
      newStartDate = today.toISOString().split('T')[0];
      newEndDate = today.toISOString().split('T')[0];
    }
    
    setCallLogsStartDate(newStartDate);
    setCallLogsEndDate(newEndDate);
    setCallLogsStartTime("00:00");
    setCallLogsEndTime("23:59");
    
    if (selectedDeviceForLogs && range !== "custom") {
      fetchCallLogs(selectedDeviceForLogs.id, 1, newStartDate, newEndDate, "00:00", "23:59");
    }
  };

  // Cihaz çağrı kayıtları modalını aç
  const openCallLogsModal = (device) => {
    setSelectedDeviceForLogs(device);
    setCallLogsModalOpen(true);
    setCallLogsPage(1);
    fetchCallLogs(device.id, 1);
  };

  // Cihaza agent ata veya değiştir
  const handleChangeDeviceAgent = async (deviceId, newAgentId) => {
    try {
      if (newAgentId === null) {
        // Remove device from all agents
        // First get which agent has this device
        const currentAgent = deviceAgentMap[deviceId];
        if (currentAgent) {
          // Get current agent's devices and remove this one
          const res = await axios.get(`${API}/agent-devices?agent_id=${currentAgent.agent_id}`);
          const currentDevices = (res.data.data || []).map(d => d.device_id).filter(id => id !== deviceId);
          await axios.post(`${API}/agent-devices/${currentAgent.agent_id}`, { device_ids: currentDevices });
        }
      } else {
        // Add device to new agent
        // First remove from old agent if exists
        const currentAgent = deviceAgentMap[deviceId];
        if (currentAgent && currentAgent.agent_id !== newAgentId) {
          const oldRes = await axios.get(`${API}/agent-devices?agent_id=${currentAgent.agent_id}`);
          const oldDevices = (oldRes.data.data || []).map(d => d.device_id).filter(id => id !== deviceId);
          await axios.post(`${API}/agent-devices/${currentAgent.agent_id}`, { device_ids: oldDevices });
        }
        
        // Add to new agent
        const newRes = await axios.get(`${API}/agent-devices?agent_id=${newAgentId}`);
        const newDevices = (newRes.data.data || []).map(d => d.device_id);
        if (!newDevices.includes(deviceId)) {
          newDevices.push(deviceId);
        }
        await axios.post(`${API}/agent-devices/${newAgentId}`, { device_ids: newDevices });
      }
      
      // Refresh data
      fetchDeviceAgentMap();
      fetchAgentDeviceCounts();
    } catch (error) {
      console.error("Change device agent error:", error);
      throw error;
    }
  };

  // Cihazdan yeni agent oluştur
  const handleCreateAgentFromDevice = (device) => {
    setPreSelectedDeviceForAgent(device);
    setEditingAgent(null);
    setAgentModalOpen(true);
  };

  useEffect(() => {
    if (activeTab === "transcripts" && canSeeRecordings) {
      fetchTranscripts();
    }
  }, [activeTab, fetchTranscripts, canSeeRecordings]);

  const handleGsmExport = async () => {
    if (!gsmSearchNumber || gsmSearchNumber.length < 3) {
      toast.error("Önce arama yapın");
      return;
    }
    
    setGsmExporting(true);
    try {
      const params = new URLSearchParams({ gsm_numara: gsmSearchNumber });
      if (gsmSearchStartDate) params.append("start_date", gsmSearchStartDate);
      if (gsmSearchEndDate) params.append("end_date", gsmSearchEndDate + " 23:59:59");
      
      const response = await axios.get(`${API}/gsm-search/export?${params}`, { timeout: 60000 });
      const data = response.data.data;
      
      if (data.length === 0) { 
        toast.warning("Export edilecek kayıt yok"); 
        return; 
      }
      
      // Build export data with new fields: agent, sistem, device_id
      const exportData = data.map(r => ({
        "Tarih": r.tarih,
        "Yön": r.yon === 1 ? "Gelen" : r.yon === 2 ? "Giden" : "-",
        "Agent": r.agent_name || "-",
        "Arayan": r.arayan_numara,
        "Aranan": r.aranan_numara,
        "Konuşma (sn)": r.konusma_suresi,
        "Çalma (sn)": r.calma_suresi,
        "Durum": r.durum_aciklama === "cevaplanan" ? "Cevaplandı" : r.durum_aciklama === "mesgul" ? "Meşgul" : "Cevapsız",
        "Sistem": r.sistem === 1 ? "GSM" : "Sabit Hat",
        "Cihaz ID": r.device_id || "-",
        ...(canSeeRecordings && { "Ses Kaydı": r.recording || "" })
      }));
      
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "GSM Arama");
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(new Blob([buf]), `GSM_Arama_${gsmSearchNumber}_${gsmSearchStartDate}_${gsmSearchEndDate}.xlsx`);
      toast.success(`${data.length} kayıt export edildi`);
    } catch (error) {
      toast.error("Export hatası");
    } finally {
      setGsmExporting(false);
    }
  };

  const fetchAgents = async () => {
    const res = await axios.get(`${API}/agents`);
    setAgents(res.data || []);
  };

  const handleSaveAgent = async (formData, agentId) => {
    try {
      // Extract device_ids from formData
      const deviceIds = formData.device_ids || [];
      const agentData = { ...formData };
      delete agentData.device_ids; // Remove from agent data
      
      let savedAgentId = agentId;
      
      if (agentId) {
        // Update existing agent
        if (!agentData.sifre) delete agentData.sifre;
        delete agentData.kullanici_adi;
        await axios.put(`${API}/agents/${agentId}`, agentData);
        toast.success("Agent güncellendi");
      } else {
        // Create new agent
        const response = await axios.post(`${API}/agents`, agentData);
        savedAgentId = response.data?.id;
        toast.success("Agent oluşturuldu");
      }
      
      // Save device associations if we have an agent ID
      if (savedAgentId && deviceIds.length >= 0) {
        try {
          await axios.post(`${API}/agent-devices/${savedAgentId}`, { device_ids: deviceIds });
        } catch (deviceError) {
          console.error("Device association error:", deviceError);
          // Don't throw - agent was still saved successfully
        }
      }
      
      // Refresh data
      fetchAgents();
      fetchAgentDeviceCounts();
      fetchDeviceAgentMap();
      
      // Return the agent id for modal
      return { id: savedAgentId };
    } catch (error) {
      toast.error(error.response?.data?.detail || "Hata");
      throw error;
    }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm("Agent silinecek, emin misiniz?")) return;
    try {
      await axios.delete(`${API}/agents/${agentId}`);
      toast.success("Agent silindi");
      fetchAgents();
      if (selectedAgent?.id === agentId) setSelectedAgent(null);
    } catch (error) {
      toast.error("Silinemedi");
    }
  };

  const fetchUsers = async () => {
    const res = await axios.get(`${API}/users`);
    setUsers(res.data || []);
  };

  const handleSaveUser = async (formData, userId) => {
    try {
      if (userId) {
        const updateData = { ...formData };
        if (!updateData.sifre) delete updateData.sifre;
        delete updateData.kullanici_adi;
        await axios.put(`${API}/users/${userId}`, updateData);
        toast.success("Kullanıcı güncellendi");
      } else {
        await axios.post(`${API}/users`, formData);
        toast.success("Kullanıcı oluşturuldu");
      }
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Hata");
      throw error;
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Kullanıcıyı silmek istediğinize emin misiniz?")) return;
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success("Kullanıcı silindi");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Silinemedi");
    }
  };

  // Fetch data when filters change (after initial load)
  const [isReady, setIsReady] = useState(false);
  
  useEffect(() => {
    if (!initialLoading) {
      setIsReady(true);
    }
  }, [initialLoading]);
  
  useEffect(() => { 
    if (isReady) {
      fetchRecords(); 
    }
  }, [isReady, startDate, endDate, startTime, endTime, searchNumber, arananNumara, statusFilter, yonFilter, selectedAgent, page, limit]);
  
  useEffect(() => { 
    if (isReady) {
      fetchStats(); 
    }
  }, [isReady, startDate, endDate, startTime, endTime, selectedAgent, searchNumber, arananNumara]);
  
  // Re-fetch GSM search when page changes (only if there's already a search)
  useEffect(() => {
    if (gsmSearchNumber && gsmSearchNumber.length >= 3 && gsmSearchResults.length > 0) {
      fetchGsmSearch();
    }
  }, [gsmSearchPage, gsmSearchLimit]);

  const totalPages = Math.ceil(totalCount / limit);
  const cardClass = isDark ? "bg-slate-950/50 border-slate-800" : "bg-white border-gray-200 shadow-sm";
  const inputClass = isDark ? "bg-slate-950 border-slate-800 text-white" : "bg-white border-gray-300";
  const labelClass = isDark ? "text-slate-400" : "text-gray-600";
  const textClass = isDark ? "text-slate-100" : "text-gray-900";

  return (
    <div className={`min-h-screen ${isDark ? "bg-[#020617]" : "bg-gray-50"}`}>
      <AudioPlayerModal 
        isOpen={audioModalOpen} 
        onClose={() => setAudioModalOpen(false)} 
        audioUrl={selectedAudio.url} 
        recordInfo={selectedAudio.record} 
        isDark={isDark}
        onOpenTranscript={(record) => {
          setSelectedTranscriptRecord(record);
          setActiveTab("transcripts");
        }}
      />
      <AgentModal 
        isOpen={agentModalOpen} 
        onClose={() => { setAgentModalOpen(false); setEditingAgent(null); setPreSelectedDeviceForAgent(null); }} 
        agent={editingAgent} 
        onSave={handleSaveAgent} 
        isDark={isDark}
        allDevices={allDevicesList}
        preSelectedDevice={preSelectedDeviceForAgent}
        existingAgentsCount={agents.length}
        onDevicesUpdate={async () => {
          try {
            const res = await axios.get(`${API}/agent-devices`);
            setAgentDeviceCounts(res.data.data || {});
            fetchDeviceAgentMap();
          } catch (e) {
            console.error("Error updating devices:", e);
          }
        }}
      />
      <ChangeAgentModal
        isOpen={changeAgentModalOpen}
        onClose={() => { setChangeAgentModalOpen(false); setSelectedDeviceForChange(null); }}
        device={selectedDeviceForChange}
        agents={agents}
        currentAgentId={selectedDeviceForChange ? deviceAgentMap[selectedDeviceForChange.id]?.agent_id : null}
        isDark={isDark}
        onSave={handleChangeDeviceAgent}
      />
      <DeviceCallLogsModal
        isOpen={callLogsModalOpen}
        onClose={() => { setCallLogsModalOpen(false); setSelectedDeviceForLogs(null); setCallLogs([]); setCallLogsSummary({ total_calls: 0, incoming: 0, outgoing: 0, missed: 0, total_duration: 0 }); setCallLogsDateRange("all"); }}
        device={selectedDeviceForLogs}
        callLogs={callLogs}
        loading={callLogsLoading}
        page={callLogsPage}
        totalPages={callLogsTotalPages}
        total={callLogsTotal}
        summary={callLogsSummary}
        onPageChange={(newPage) => fetchCallLogs(selectedDeviceForLogs?.id, newPage, callLogsStartDate, callLogsEndDate, callLogsStartTime, callLogsEndTime)}
        isDark={isDark}
        startDate={callLogsStartDate}
        endDate={callLogsEndDate}
        startTime={callLogsStartTime}
        endTime={callLogsEndTime}
        onStartDateChange={setCallLogsStartDate}
        onEndDateChange={setCallLogsEndDate}
        onStartTimeChange={setCallLogsStartTime}
        onEndTimeChange={setCallLogsEndTime}
        onFilter={() => fetchCallLogs(selectedDeviceForLogs?.id, 1, callLogsStartDate, callLogsEndDate, callLogsStartTime, callLogsEndTime)}
        dateRange={callLogsDateRange}
        onDateRangeChange={handleCallLogsDateRangeChange}
        onPlayRecording={(url, log) => {
          setSelectedAudio({ url: url, record: log });
          setAudioModalOpen(true);
        }}
      />
      <UserModal isOpen={userModalOpen} onClose={() => { setUserModalOpen(false); setEditingUser(null); }} userToEdit={editingUser} onSave={handleSaveUser} isDark={isDark} agents={agents} />
      
      {/* Header */}
      <header className={`sticky top-0 z-30 px-4 sm:px-6 py-3 sm:py-4 border-b ${isDark ? "glass border-white/5" : "bg-white/80 backdrop-blur-lg border-gray-200"}`}>
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center ${isDark ? "bg-blue-600 glow-blue" : "bg-blue-500"}`}>
                <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div>
                <h1 className={`text-lg sm:text-xl font-bold tracking-tight ${textClass}`}>{user?.firma_adi || "eTelekom CDR"}</h1>
                <p className={`text-xs ${labelClass} hidden sm:block`}>Çağrı Kayıt Yönetim Paneli</p>
              </div>
            </div>
            {/* Mobile: Theme and Logout */}
            <div className="flex sm:hidden items-center gap-2">
              <Button variant="ghost" size="icon" onClick={onToggleTheme} className={`h-8 w-8 ${isDark ? "text-slate-400" : "text-gray-600"}`}>
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={logout} className={`h-8 w-8 ${isDark ? "text-slate-400 hover:text-red-400" : "text-gray-600 hover:text-red-600"}`}>
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-4 overflow-x-auto">
            {selectedAgent && (
              <div className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm ${isDark ? "bg-blue-600/20 text-blue-400" : "bg-blue-100 text-blue-700"}`}>
                <User className="w-3 h-3 sm:w-4 sm:h-4" /><span className="font-medium truncate max-w-20 sm:max-w-none">{selectedAgent.ad_soyad}</span>
                <button onClick={() => setSelectedAgent(null)} className="ml-1"><X className="w-3 h-3 sm:w-4 sm:h-4" /></button>
              </div>
            )}
            {isAdmin && (
              <div className={`hidden sm:flex items-center gap-2 text-sm ${labelClass}`}>
                <Database className="w-4 h-4" /><span className="font-mono">{totalCount.toLocaleString('tr-TR')}</span>
              </div>
            )}
            <div className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full ${isDark ? "bg-slate-800 text-slate-300" : "bg-gray-100 text-gray-700"}`}>
              <User className="w-3 h-3 sm:w-4 sm:h-4" /><span className="text-xs sm:text-sm truncate max-w-20 sm:max-w-none">{user?.ad_soyad}</span>
              {isAdmin && <Badge className="ml-1 bg-purple-500/20 text-purple-400 text-xs hidden sm:inline">Admin</Badge>}
            </div>
            {/* Desktop: Theme and Logout */}
            <div className="hidden sm:flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={onToggleTheme} className={isDark ? "text-slate-400" : "text-gray-600"}>
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={logout} className={isDark ? "text-slate-400 hover:text-red-400" : "text-gray-600 hover:text-red-600"}>
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-6">
          {/* Modern Navigation Bar */}
          <div className={`rounded-2xl overflow-hidden ${isDark ? "bg-slate-900/80 border border-slate-800" : "bg-white border border-gray-200 shadow-sm"}`}>
            <div className="flex flex-wrap sm:flex-nowrap">
              {/* Çağrı Kayıtları */}
              <button
                onClick={() => setActiveTab("cdr")}
                className={`flex-1 min-w-[120px] relative flex items-center justify-center gap-2 px-3 sm:px-6 py-3 sm:py-4 font-medium transition-all duration-200 border-b-2 ${
                  activeTab === "cdr"
                    ? isDark 
                      ? "bg-blue-600/10 text-blue-400 border-blue-500" 
                      : "bg-blue-50 text-blue-600 border-blue-500"
                    : isDark
                      ? "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 border-transparent"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-transparent"
                }`}
              >
                <Phone className={`w-4 h-4 sm:w-5 sm:h-5 ${activeTab === "cdr" ? "" : isDark ? "text-blue-400" : "text-blue-500"}`} />
                <span className="text-xs sm:text-sm">Çağrılar</span>
              </button>
              
              {/* GSM Arama */}
              {canSearch && (
                <button
                  onClick={() => setActiveTab("search")}
                  className={`flex-1 min-w-[120px] relative flex items-center justify-center gap-2 px-3 sm:px-6 py-3 sm:py-4 font-medium transition-all duration-200 border-b-2 ${
                    activeTab === "search"
                      ? isDark 
                        ? "bg-cyan-600/10 text-cyan-400 border-cyan-500" 
                        : "bg-cyan-50 text-cyan-600 border-cyan-500"
                      : isDark
                        ? "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 border-transparent"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-transparent"
                  }`}
                >
                  <Search className={`w-4 h-4 sm:w-5 sm:h-5 ${activeTab === "search" ? "" : isDark ? "text-cyan-400" : "text-cyan-500"}`} />
                  <span className="text-xs sm:text-sm">Arama</span>
                </button>
              )}
              
              {/* Transkript (Ses kaydı görebilen kullanıcılar için) */}
              {canSeeRecordings && (
                <button
                  onClick={() => setActiveTab("transcripts")}
                  className={`flex-1 min-w-[120px] relative flex items-center justify-center gap-2 px-3 sm:px-6 py-3 sm:py-4 font-medium transition-all duration-200 border-b-2 ${
                    activeTab === "transcripts"
                      ? isDark 
                        ? "bg-emerald-600/10 text-emerald-400 border-emerald-500" 
                        : "bg-emerald-50 text-emerald-600 border-emerald-500"
                      : isDark
                        ? "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 border-transparent"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-transparent"
                  }`}
                >
                  <FileText className={`w-4 h-4 sm:w-5 sm:h-5 ${activeTab === "transcripts" ? "" : isDark ? "text-emerald-400" : "text-emerald-500"}`} />
                  <span className="text-xs sm:text-sm">Transkript</span>
                </button>
              )}
              
              {/* Agentlar */}
              <button
                onClick={() => setActiveTab("agents")}
                className={`flex-1 min-w-[120px] relative flex items-center justify-center gap-2 px-3 sm:px-6 py-3 sm:py-4 font-medium transition-all duration-200 border-b-2 ${
                  activeTab === "agents"
                    ? isDark 
                      ? "bg-purple-600/10 text-purple-400 border-purple-500" 
                      : "bg-purple-50 text-purple-600 border-purple-500"
                    : isDark
                      ? "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 border-transparent"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-transparent"
                }`}
              >
                <Users className={`w-4 h-4 sm:w-5 sm:h-5 ${activeTab === "agents" ? "" : isDark ? "text-purple-400" : "text-purple-500"}`} />
                <span className="text-xs sm:text-sm">Agentlar</span>
              </button>
              
              {/* Raporlar */}
              <button
                onClick={() => setActiveTab("reports")}
                className={`flex-1 min-w-[120px] relative flex items-center justify-center gap-2 px-3 sm:px-6 py-3 sm:py-4 font-medium transition-all duration-200 border-b-2 ${
                  activeTab === "reports"
                    ? isDark 
                      ? "bg-teal-600/10 text-teal-400 border-teal-500" 
                      : "bg-teal-50 text-teal-600 border-teal-500"
                    : isDark
                      ? "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 border-transparent"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-transparent"
                }`}
              >
                <BarChart3 className={`w-4 h-4 sm:w-5 sm:h-5 ${activeTab === "reports" ? "" : isDark ? "text-teal-400" : "text-teal-500"}`} />
                <span className="text-xs sm:text-sm">Raporlar</span>
              </button>
              
              {/* Devices */}
              <button
                onClick={() => setActiveTab("devices")}
                className={`flex-1 min-w-[120px] relative flex items-center justify-center gap-2 px-3 sm:px-6 py-3 sm:py-4 font-medium transition-all duration-200 border-b-2 ${
                  activeTab === "devices"
                    ? isDark 
                      ? "bg-pink-600/10 text-pink-400 border-pink-500" 
                      : "bg-pink-50 text-pink-600 border-pink-500"
                    : isDark
                      ? "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 border-transparent"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-transparent"
                }`}
              >
                <Smartphone className={`w-4 h-4 sm:w-5 sm:h-5 ${activeTab === "devices" ? "" : isDark ? "text-pink-400" : "text-pink-500"}`} />
                <span className="text-xs sm:text-sm">Cihazlar</span>
              </button>
              
              {/* Kullanıcılar (Admin only) */}
              {isAdmin && (
                <button
                  onClick={() => setActiveTab("users")}
                  className={`flex-1 min-w-[120px] relative flex items-center justify-center gap-2 px-3 sm:px-6 py-3 sm:py-4 font-medium transition-all duration-200 border-b-2 ${
                    activeTab === "users"
                      ? isDark 
                        ? "bg-amber-600/10 text-amber-400 border-amber-500" 
                        : "bg-amber-50 text-amber-600 border-amber-500"
                      : isDark
                        ? "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 border-transparent"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-transparent"
                  }`}
                >
                  <Settings className={`w-4 h-4 sm:w-5 sm:h-5 ${activeTab === "users" ? "" : isDark ? "text-amber-400" : "text-amber-500"}`} />
                  <span className="text-xs sm:text-sm">Kullanıcılar</span>
                </button>
              )}
            </div>
          </div>

          {/* CDR Tab */}
          <TabsContent value="cdr" className="space-y-4 sm:space-y-6">
            {/* Stats with Date Filter */}
            <Card className={cardClass}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex flex-col gap-3 sm:gap-4 mb-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div>
                      <h3 className={`text-sm sm:text-base font-semibold ${textClass}`}>
                        <TrendingUp className="w-4 h-4 inline mr-2 text-blue-500" />İstatistikler
                      </h3>
                      {dataRange && dataRange.max_date && (
                        <p className={`text-xs mt-1 ${labelClass}`}>
                          Veri: {dataRange.min_date} - {dataRange.max_date}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {statsLoading && <div className="spinner w-4 h-4" />}
                      {/* Stats Export Buttons - PDF and Excel */}
                      {canExport && (
                        <>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={handleExportStatsPdf}
                            disabled={!stats || statsLoading}
                            className={`h-8 ${isDark ? "border-slate-700 text-red-400 hover:bg-slate-800 hover:text-red-300" : "text-red-600 hover:text-red-700 hover:bg-red-50"}`}
                            title="PDF olarak indir"
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={handleExportStatsExcel}
                            disabled={!stats || statsLoading}
                            className={`h-8 ${isDark ? "border-slate-700 text-emerald-400 hover:bg-slate-800 hover:text-emerald-300" : "text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"}`}
                            title="Excel olarak indir"
                          >
                            <FileSpreadsheet className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                    {[
                      { id: "today", label: "Bugün" },
                      { id: "yesterday", label: "Dün" },
                      { id: "last7", label: "7 Gün" },
                      { id: "thisMonth", label: "Bu Ay" },
                      { id: "lastMonth", label: "Geçen Ay" },
                      { id: "custom", label: "Özel" }
                    ].map(opt => (
                      <button
                        key={opt.id}
                        onClick={() => handleStatsDateChange(opt.id)}
                        className={`px-2 sm:px-3 py-1 sm:py-1.5 text-xs font-medium rounded-lg transition-all whitespace-nowrap ${
                          statsDateRange === opt.id
                            ? (isDark ? "bg-blue-600 text-white" : "bg-blue-500 text-white")
                            : (isDark ? "bg-slate-800 text-slate-400 hover:bg-slate-700" : "bg-gray-100 text-gray-600 hover:bg-gray-200")
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {statsDateRange === "custom" && (
                    <div className="flex flex-wrap items-center gap-1.5">
                      <div className="relative">
                        <Input
                          type="date"
                          value={statsStartDate}
                          onChange={(e) => setStatsStartDate(e.target.value)}
                          className={`w-32 h-7 text-xs pl-7 ${inputClass} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
                        />
                        <Calendar className={`w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-cyan-400" : "text-blue-500"}`} />
                      </div>
                      <div className="relative">
                        <Input
                          type="time"
                          value={startTime}
                          onChange={(e) => setStartTime(e.target.value)}
                          className={`w-24 h-7 text-xs pl-7 ${inputClass} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
                        />
                        <Clock className={`w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-cyan-400" : "text-blue-500"}`} />
                      </div>
                      <span className={`${labelClass}`}>-</span>
                      <div className="relative">
                        <Input
                          type="date"
                          value={statsEndDate}
                          onChange={(e) => setStatsEndDate(e.target.value)}
                          className={`w-32 h-7 text-xs pl-7 ${inputClass} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
                        />
                        <Calendar className={`w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-cyan-400" : "text-blue-500"}`} />
                      </div>
                      <div className="relative">
                        <Input
                          type="time"
                          value={endTime}
                          onChange={(e) => setEndTime(e.target.value)}
                          className={`w-24 h-7 text-xs pl-7 ${inputClass} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
                        />
                        <Clock className={`w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-cyan-400" : "text-blue-500"}`} />
                      </div>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-4">
                  <StatCard icon={Phone} title="Toplam Çağrı" value={stats?.total_calls?.toLocaleString('tr-TR') || "0"} color="blue" isDark={isDark} />
                  <StatCard icon={TrendingUp} title="Cevaplanan" value={stats?.answered_calls?.toLocaleString('tr-TR') || "0"} subtitle={stats?.total_calls ? `%${((stats.answered_calls / stats.total_calls) * 100).toFixed(1)}` : ""} color="green" isDark={isDark} />
                  <StatCard icon={PhoneMissed} title="Cevapsız" value={stats?.missed_calls?.toLocaleString('tr-TR') || "0"} color="amber" isDark={isDark} />
                  <StatCard icon={PhoneOff} title="Meşgul" value={stats?.busy_calls?.toLocaleString('tr-TR') || "0"} color="red" isDark={isDark} />
                  <StatCard icon={Clock} title="Toplam Konuşma" value={formatDuration(stats?.total_talk_time || 0)} subtitle={`Ort: ${formatDuration(Math.round(stats?.avg_talk_time || 0))}`} color="cyan" isDark={isDark} />
                </div>
              </CardContent>
            </Card>

            {/* Filters */}
            <Card className={cardClass}>
              <CardContent className="p-2 sm:p-3">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-10 gap-1.5 sm:gap-2 items-end">
                  <div>
                    <label className={`block text-xs mb-0.5 ${labelClass}`}>Başlangıç</label>
                    <div className="relative">
                      <Input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }} className={`h-7 text-xs pl-7 ${inputClass} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`} />
                      <Calendar className={`w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-cyan-400" : "text-blue-500"}`} />
                    </div>
                  </div>
                  <div>
                    <label className={`block text-xs mb-0.5 ${labelClass}`}>Saat</label>
                    <div className="relative">
                      <Input type="time" value={startTime} onChange={(e) => { setStartTime(e.target.value); setPage(1); }} className={`h-7 text-xs pl-7 ${inputClass} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`} />
                      <Clock className={`w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-cyan-400" : "text-blue-500"}`} />
                    </div>
                  </div>
                  <div>
                    <label className={`block text-xs mb-0.5 ${labelClass}`}>Bitiş</label>
                    <div className="relative">
                      <Input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }} className={`h-7 text-xs pl-7 ${inputClass} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`} />
                      <Calendar className={`w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-cyan-400" : "text-blue-500"}`} />
                    </div>
                  </div>
                  <div>
                    <label className={`block text-xs mb-0.5 ${labelClass}`}>Saat</label>
                    <div className="relative">
                      <Input type="time" value={endTime} onChange={(e) => { setEndTime(e.target.value); setPage(1); }} className={`h-7 text-xs pl-7 ${inputClass} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`} />
                      <Clock className={`w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-cyan-400" : "text-blue-500"}`} />
                    </div>
                  </div>
                  <div>
                    <label className={`block text-xs mb-0.5 ${labelClass}`}>Yön</label>
                    <Select value={yonFilter} onValueChange={(v) => { setYonFilter(v); setPage(1); }}>
                      <SelectTrigger className={`h-7 text-xs ${inputClass}`}><SelectValue /></SelectTrigger>
                      <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                        <SelectItem value="all">Tümü</SelectItem>
                        <SelectItem value="1">Gelen</SelectItem>
                        <SelectItem value="2">Giden</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className={`block text-xs mb-0.5 ${labelClass}`}>Agent</label>
                    <Select value={selectedAgent?.id?.toString() || "all"} onValueChange={(v) => { setSelectedAgent(v === "all" ? null : visibleAgents.find(a => a.id.toString() === v)); setPage(1); }}>
                      <SelectTrigger className={`h-7 text-xs ${inputClass}`}><SelectValue placeholder="Tümü" /></SelectTrigger>
                      <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                        <SelectItem value="all">Tümü</SelectItem>
                        {visibleAgents.filter(a => a.gsm).map(a => <SelectItem key={a.id} value={a.id.toString()}>{a.ad_soyad}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className={`block text-xs mb-0.5 ${labelClass}`}>Arayan</label>
                    <div className="relative">
                      <Input placeholder="05xx..." value={searchNumber} onChange={(e) => { setSearchNumber(e.target.value); setPage(1); }} className={`h-7 text-xs pl-7 ${inputClass} font-mono`} />
                      <Search className={`w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-slate-500" : "text-gray-400"}`} />
                    </div>
                  </div>
                  <div>
                    <label className={`block text-xs mb-0.5 ${labelClass}`}>Aranan</label>
                    <div className="relative">
                      <Input placeholder="850..." value={arananNumara} onChange={(e) => { setArananNumara(e.target.value); setPage(1); }} className={`h-7 text-xs pl-7 ${inputClass} font-mono`} />
                      <Search className={`w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-slate-500" : "text-gray-400"}`} />
                    </div>
                  </div>
                  <div>
                    <label className={`block text-xs mb-0.5 ${labelClass}`}>Durum</label>
                    <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                      <SelectTrigger className={`h-7 text-xs ${inputClass}`}><SelectValue /></SelectTrigger>
                      <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                        <SelectItem value="all">Tümü</SelectItem>
                        <SelectItem value="1">Cevaplanan</SelectItem>
                        <SelectItem value="2">Cevapsız</SelectItem>
                        <SelectItem value="3">Meşgul</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-1 col-span-2 sm:col-span-1">
                    <Button variant="secondary" size="sm" onClick={() => { setPage(1); fetchRecords(); fetchStats(); }} className={`h-7 px-2 ${isDark ? "bg-slate-800 hover:bg-slate-700 text-white" : ""}`} title="Yenile"><RefreshCw className="w-3.5 h-3.5" /></Button>
                    <Button variant="outline" size="sm" onClick={resetFilters} className={`h-7 px-2 ${isDark ? "border-slate-700 text-amber-400 hover:bg-slate-800" : "text-amber-600 hover:bg-amber-50"}`} title="Filtreleri Sıfırla"><FilterX className="w-3.5 h-3.5" /></Button>
                    {canExport && (
                      <div className="relative">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setExportMenuOpen(!exportMenuOpen)}
                          disabled={exporting} 
                          className={`h-8 px-2 ${isDark ? "border-slate-700 text-emerald-400 hover:bg-slate-800" : "text-emerald-600 hover:bg-emerald-50"}`}
                          title="Export"
                        >
                          {exporting ? <div className="spinner w-4 h-4" /> : <Download className="w-4 h-4" />}
                        </Button>
                        {exportMenuOpen && (
                          <>
                            <div className="fixed inset-0 z-40" onClick={() => setExportMenuOpen(false)} />
                            <div className={`absolute right-0 mt-1 z-50 rounded-lg border shadow-lg py-1 min-w-[140px] ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-gray-200"}`}>
                              <button 
                                onClick={() => setExportModalOpen(true)}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${isDark ? "hover:bg-slate-800 text-emerald-400" : "hover:bg-gray-50 text-emerald-600"}`}
                              >
                                <FileSpreadsheet className="w-4 h-4" /> Excel Export
                              </button>
                              <button 
                                onClick={handleExportPdf}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${isDark ? "hover:bg-slate-800 text-red-400" : "hover:bg-gray-50 text-red-600"}`}
                              >
                                <FileText className="w-4 h-4" /> PDF Export
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Export Modal */}
            <Dialog open={exportModalOpen} onOpenChange={setExportModalOpen}>
              <DialogContent className={`max-w-md ${isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white"}`}>
                <DialogHeader>
                  <DialogTitle className={isDark ? "text-white" : "text-gray-900"}>
                    <Download className="w-5 h-5 inline mr-2 text-blue-500" />Excel Export Ayarları
                  </DialogTitle>
                </DialogHeader>
                <div className="py-4">
                  <p className={`text-sm mb-4 ${labelClass}`}>Export&apos;a dahil edilecek kolonları seçin:</p>
                  <div className="space-y-3">
                    {ALL_COLUMNS.map(col => {
                      if (col.requiresPermission && !canSeeRecordings) return null;
                      return (
                        <div key={col.id} className="flex items-center gap-3">
                          <Checkbox
                            checked={exportColumns.includes(col.id)}
                            onCheckedChange={() => toggleExportColumn(col.id)}
                            id={`export-${col.id}`}
                          />
                          <label htmlFor={`export-${col.id}`} className={`text-sm cursor-pointer ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                            {col.label}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setExportModalOpen(false)}>İptal</Button>
                  <Button onClick={handleExport} disabled={exporting || exportColumns.length === 0} className="bg-blue-600 hover:bg-blue-700 text-white">
                    {exporting ? "Export ediliyor..." : "Excel İndir"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Table */}
            <Card className={cardClass}>
              <CardHeader className="pb-4 flex flex-row items-center justify-between">
                <CardTitle className={`text-lg font-semibold ${textClass}`}>Çağrı Kayıtları <span className={`text-sm font-normal ml-2 ${labelClass}`}>({totalCount.toLocaleString('tr-TR')})</span></CardTitle>
                <div className="flex items-center gap-2">
                  {/* Column Visibility Selector */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className={isDark ? "border-slate-700 text-slate-300" : ""}>
                        <Columns3 className="w-4 h-4 mr-2" />Kolonlar
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className={`w-56 ${isDark ? "bg-slate-900 border-slate-800" : "bg-white"}`} align="end">
                      <div className="space-y-2">
                        <p className={`text-sm font-medium mb-3 ${isDark ? "text-slate-300" : "text-gray-700"}`}>Görünür Kolonlar</p>
                        {ALL_COLUMNS.map(col => {
                          if (col.requiresPermission && !canSeeRecordings) return null;
                          return (
                            <div key={col.id} className="flex items-center gap-2">
                              <Checkbox
                                checked={visibleColumns.includes(col.id)}
                                onCheckedChange={() => toggleColumn(col.id)}
                                id={`col-${col.id}`}
                              />
                              <label htmlFor={`col-${col.id}`} className={`text-sm cursor-pointer ${isDark ? "text-slate-400" : "text-gray-600"}`}>
                                {col.label}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                  <Select value={limit.toString()} onValueChange={(v) => { setLimit(parseInt(v)); setPage(1); }}>
                    <SelectTrigger className={`w-20 ${inputClass}`}><SelectValue /></SelectTrigger>
                    <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}><SelectItem value="10">10</SelectItem><SelectItem value="20">20</SelectItem><SelectItem value="50">50</SelectItem><SelectItem value="100">100</SelectItem></SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className={`${isDark ? "border-slate-700 bg-slate-800/50" : "border-gray-200 bg-gray-50"}`}>
                        {isColumnVisible("tarih") && <TableHead className={`font-semibold ${isDark ? "text-slate-300" : "text-gray-700"}`}>Tarih</TableHead>}
                        {isColumnVisible("yon") && <TableHead className={`font-semibold ${isDark ? "text-slate-300" : "text-gray-700"}`}>Yön</TableHead>}
                        {isColumnVisible("agent_name") && <TableHead className={`font-semibold ${isDark ? "text-slate-300" : "text-gray-700"}`}>Agent</TableHead>}
                        {isColumnVisible("arayan_numara") && <TableHead className={`font-semibold ${isDark ? "text-slate-300" : "text-gray-700"}`}>Arayan</TableHead>}
                        {isColumnVisible("aranan_numara") && <TableHead className={`font-semibold ${isDark ? "text-slate-300" : "text-gray-700"}`}>Aranan</TableHead>}
                        {isColumnVisible("konusma_suresi") && <TableHead className={`font-semibold ${isDark ? "text-slate-300" : "text-gray-700"}`}>Süre</TableHead>}
                        {isColumnVisible("durum") && <TableHead className={`font-semibold ${isDark ? "text-slate-300" : "text-gray-700"}`}>Durum</TableHead>}
                        {isColumnVisible("sistem") && <TableHead className={`font-semibold ${isDark ? "text-slate-300" : "text-gray-700"}`}>Sistem</TableHead>}
                        {isColumnVisible("device_id") && <TableHead className={`font-semibold ${isDark ? "text-slate-300" : "text-gray-700"}`}>Cihaz ID</TableHead>}
                        {isColumnVisible("did") && <TableHead className={`font-semibold ${isDark ? "text-slate-300" : "text-gray-700"}`}>Departman</TableHead>}
                        {isColumnVisible("transkript") && canSeeRecordings && <TableHead className={`font-semibold ${isDark ? "text-slate-300" : "text-gray-700"}`}>Transkript</TableHead>}
                        {isColumnVisible("recording") && <TableHead className={`font-semibold ${isDark ? "text-slate-300" : "text-gray-700"}`}>İşlemler</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loading ? (
                        <TableRow><TableCell colSpan={visibleColumns.length + 1} className="text-center py-16"><div className="flex flex-col items-center gap-3"><div className="spinner w-8 h-8" /><span className={labelClass}>Yükleniyor...</span></div></TableCell></TableRow>
                      ) : records.length === 0 ? (
                        <TableRow><TableCell colSpan={visibleColumns.length + 1} className={`text-center py-16 ${labelClass}`}><Database className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>Kayıt bulunamadı</p></TableCell></TableRow>
                      ) : (
                        records.map((r, i) => (
                          <TableRow key={r.id || i} className={`transition-colors ${isDark ? "border-slate-800/50 hover:bg-slate-800/40" : "border-gray-100 hover:bg-blue-50/50"} ${i % 2 === 0 ? (isDark ? "bg-slate-900/30" : "bg-white") : (isDark ? "bg-slate-800/20" : "bg-gray-50/50")}`}>
                            {isColumnVisible("tarih") && (
                              <TableCell className={`font-mono text-sm whitespace-nowrap ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                                <div className="flex flex-col">
                                  <span>{r.tarih?.split(' ')[0]}</span>
                                  <span className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>{r.tarih?.split(' ')[1]}</span>
                                </div>
                              </TableCell>
                            )}
                            {isColumnVisible("yon") && (
                              <TableCell>
                                {String(r.durum) !== '1' ? (
                                  // Cevapsız - kırmızı renk ve cevapsız ikon
                                  <Badge className={`${isDark ? "bg-red-500/20 text-red-400 border border-red-500/30" : "bg-red-100 text-red-700 border border-red-200"}`}>
                                    <PhoneMissed className="w-3 h-3 mr-1" />
                                    {r.yon === 1 ? "Gelen" : r.yon === 2 ? "Giden" : "-"}
                                  </Badge>
                                ) : r.yon === 1 ? (
                                  <Badge className={`${isDark ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" : "bg-emerald-100 text-emerald-700 border border-emerald-200"}`}>
                                    <PhoneIncoming className="w-3 h-3 mr-1" />Gelen
                                  </Badge>
                                ) : r.yon === 2 ? (
                                  <Badge className={`${isDark ? "bg-blue-500/20 text-blue-400 border border-blue-500/30" : "bg-blue-100 text-blue-700 border border-blue-200"}`}>
                                    <PhoneOutgoing className="w-3 h-3 mr-1" />Giden
                                  </Badge>
                                ) : <span className={labelClass}>-</span>}
                              </TableCell>
                            )}
                            {isColumnVisible("agent_name") && (
                              <TableCell className={`text-sm ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                                {r.agent_name ? (
                                  <span 
                                    className={`inline-flex items-center gap-1 px-2 py-1 rounded cursor-pointer hover:ring-2 hover:ring-cyan-400/50 transition-all ${getAgentColor(r.agent_name, isDark)}`}
                                    onClick={() => {
                                      const agent = agents.find(a => a.ad_soyad === r.agent_name);
                                      if (agent) {
                                        setSelectedAgent(agent);
                                        toast.success(`${r.agent_name} filtrelendi`);
                                      }
                                    }}
                                    title="Tıkla ve filtrele"
                                  >
                                    <User className="w-3 h-3" />{r.agent_name}
                                  </span>
                                ) : <span className={labelClass}>-</span>}
                              </TableCell>
                            )}
                            {isColumnVisible("arayan_numara") && (
                              <TableCell className={`font-mono text-sm ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                                <span className={`px-2 py-1 rounded ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>{r.arayan_numara}</span>
                              </TableCell>
                            )}
                            {isColumnVisible("aranan_numara") && (
                              <TableCell className={`font-mono text-sm ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                                <span className={`px-2 py-1 rounded ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>{r.aranan_numara}</span>
                              </TableCell>
                            )}
                            {isColumnVisible("konusma_suresi") && (
                              <TableCell>
                                <span className={`inline-flex items-center px-2 py-1 rounded font-mono text-sm ${r.konusma_suresi > 0 ? (isDark ? "bg-purple-500/20 text-purple-400" : "bg-purple-100 text-purple-700") : (isDark ? "text-slate-500" : "text-gray-400")}`}>
                                  <Clock className="w-3 h-3 mr-1" />{formatDuration(r.konusma_suresi)}
                                </span>
                              </TableCell>
                            )}
                            {isColumnVisible("durum") && <TableCell><StatusBadge durum={r.durum} durum_aciklama={r.durum_aciklama} isDark={isDark} /></TableCell>}
                            {isColumnVisible("sistem") && (
                              <TableCell>
                                {r.sistem === 1 ? (
                                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${isDark ? "bg-cyan-500/20 text-cyan-400" : "bg-cyan-100 text-cyan-700"}`} title="GSM">
                                    <Smartphone className="w-4 h-4" />
                                  </span>
                                ) : (
                                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${isDark ? "bg-amber-500/20 text-amber-400" : "bg-amber-100 text-amber-700"}`} title="Sabit Hat">
                                    <Phone className="w-4 h-4" />
                                  </span>
                                )}
                              </TableCell>
                            )}
                            {isColumnVisible("device_id") && (
                              <TableCell className={`font-mono text-sm ${isDark ? "text-slate-400" : "text-gray-600"}`}>
                                {r.device_id ? (
                                  <span 
                                    className={`px-2 py-1 rounded cursor-pointer hover:ring-2 hover:ring-cyan-400/50 transition-all ${isDark ? "bg-slate-800 text-cyan-400" : "bg-gray-100 text-cyan-700"}`}
                                    onClick={async () => {
                                      try {
                                        // Fetch device info from API using numeric id
                                        const res = await axios.get(`${API}/devices?search=&page=1&limit=100`, {
                                          headers: { Authorization: `Bearer ${token}` }
                                        });
                                        const devicesList = res.data.data || [];
                                        const deviceFromList = devicesList.find(d => d.id === r.device_id || d.id === parseInt(r.device_id));
                                        
                                        if (deviceFromList) {
                                          setSelectedDeviceForLogs(deviceFromList);
                                        } else {
                                          setSelectedDeviceForLogs({
                                            id: r.device_id,
                                            device_id: String(r.device_id),
                                            device_name: `Cihaz #${r.device_id}`,
                                            device_model: '-',
                                            gsm: r.arayan_numara || r.aranan_numara || '-'
                                          });
                                        }
                                      } catch (err) {
                                        setSelectedDeviceForLogs({
                                          id: r.device_id,
                                          device_id: String(r.device_id),
                                          device_name: `Cihaz #${r.device_id}`,
                                          device_model: '-',
                                          gsm: r.arayan_numara || r.aranan_numara || '-'
                                        });
                                      }
                                      setCallLogsModalOpen(true);
                                      const today = new Date();
                                      const weekAgo = new Date(today);
                                      weekAgo.setDate(weekAgo.getDate() - 7);
                                      fetchCallLogs(r.device_id, 1, weekAgo.toISOString().split('T')[0], today.toISOString().split('T')[0], "00:00", "23:59");
                                    }}
                                    title="Çağrı kayıtlarını görüntüle"
                                  >
                                    <Smartphone className="w-3 h-3 inline mr-1" />{r.device_id}
                                  </span>
                                ) : <span className={labelClass}>-</span>}
                              </TableCell>
                            )}
                            {isColumnVisible("did") && <TableCell className={`font-mono text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>{r.did || "-"}</TableCell>}
                            {isColumnVisible("transkript") && canSeeRecordings && (
                              <TableCell>
                                {r.transkript === 1 ? (
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={async () => { 
                                      await handleViewTranscript(r.id); 
                                      setActiveTab("transcripts"); 
                                    }}
                                    className={`p-1 h-auto ${isDark ? "text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10" : "text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"}`}
                                  >
                                    <FileText className="w-4 h-4 mr-1" /><span className="text-xs">Görüntüle</span>
                                  </Button>
                                ) : (
                                  <span className={`inline-flex items-center text-xs px-2 py-1 rounded ${isDark ? "bg-slate-800 text-slate-500" : "bg-gray-100 text-gray-400"}`}>
                                    <FileText className="w-3 h-3 mr-1" />Yok
                                  </span>
                                )}
                              </TableCell>
                            )}
                            {isColumnVisible("recording") && (
                              <TableCell>
                                {r.recording ? (
                                  <div className="flex items-center gap-1">
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      onClick={() => { setSelectedAudio({ url: r.recording, record: r }); setAudioModalOpen(true); }} 
                                      className={`p-1.5 h-auto rounded-lg ${isDark ? "text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10" : "text-blue-600 hover:text-blue-700 hover:bg-blue-50"}`}
                                      title="Dinle"
                                    >
                                      <Volume2 className="w-4 h-4" />
                                    </Button>
                                    <a 
                                      href={r.recording} 
                                      download 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className={`p-1.5 rounded-lg inline-flex items-center justify-center transition-colors ${isDark ? "text-amber-400 hover:text-amber-300 hover:bg-amber-500/10" : "text-amber-600 hover:text-amber-700 hover:bg-amber-50"}`}
                                      title="İndir"
                                    >
                                      <Download className="w-4 h-4" />
                                    </a>
                                  </div>
                                ) : <span className={`text-xs ${isDark ? "text-slate-600" : "text-gray-400"}`}>-</span>}
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                {totalPages > 1 && (
                  <div className={`flex items-center justify-between px-4 py-4 border-t ${isDark ? "border-slate-800" : "border-gray-200"}`}>
                    <div className={`text-sm ${labelClass}`}>Sayfa {page} / {totalPages}</div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setPage(1)} disabled={page === 1}>İlk</Button>
                      <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}><ChevronLeft className="w-4 h-4" /></Button>
                      <span className={`px-3 py-1 rounded ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>{page}</span>
                      <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}><ChevronRight className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => setPage(totalPages)} disabled={page === totalPages}>Son</Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* GSM Search Tab */}
          {canSearch && (
          <TabsContent value="search" className="space-y-4 sm:space-y-6">
            {/* Search Filters */}
            <Card className={cardClass}>
              <CardContent className="p-3 sm:p-4">
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Search className={`w-5 h-5 ${isDark ? "text-cyan-400" : "text-cyan-600"}`} />
                    <h3 className={`text-sm sm:text-base font-semibold ${textClass}`}>GSM Numarası ile Arama</h3>
                  </div>
                  <p className={`text-xs ${labelClass}`}>
                    Arayan veya aranan numara içinde arama yapar. Sonuçlarda gelen/giden yönü gösterilir.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
                    <div className="lg:col-span-2">
                      <label className={`block text-xs sm:text-sm mb-1 sm:mb-2 ${labelClass}`}>
                        <Phone className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />GSM Numarası
                      </label>
                      <Input
                        placeholder="05xx veya 850..."
                        value={gsmSearchNumber}
                        onChange={(e) => setGsmSearchNumber(e.target.value)}
                        className={`h-9 sm:h-10 text-sm font-mono ${inputClass}`}
                        onKeyDown={(e) => { if (e.key === 'Enter') fetchGsmSearch(); }}
                      />
                    </div>
                    <div>
                      <label className={`block text-xs sm:text-sm mb-1 sm:mb-2 ${labelClass}`}>
                        <Calendar className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />Başlangıç
                      </label>
                      <Input
                        type="date"
                        value={gsmSearchStartDate}
                        onChange={(e) => setGsmSearchStartDate(e.target.value)}
                        className={`h-9 sm:h-10 text-xs sm:text-sm ${inputClass}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-xs sm:text-sm mb-1 sm:mb-2 ${labelClass}`}>
                        <Calendar className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />Bitiş
                      </label>
                      <Input
                        type="date"
                        value={gsmSearchEndDate}
                        onChange={(e) => setGsmSearchEndDate(e.target.value)}
                        className={`h-9 sm:h-10 text-xs sm:text-sm ${inputClass}`}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={fetchGsmSearch}
                        disabled={gsmSearchLoading || !gsmSearchNumber || gsmSearchNumber.length < 3}
                        className="flex-1 h-9 sm:h-10 bg-cyan-600 hover:bg-cyan-700 text-white"
                      >
                        {gsmSearchLoading ? <div className="spinner w-4 h-4" /> : <><Search className="w-4 h-4 mr-1" />Ara</>}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setGsmSearchNumber("");
                          const today = new Date().toISOString().split("T")[0];
                          setGsmSearchStartDate(today);
                          setGsmSearchEndDate(today);
                          setGsmSearchResults([]);
                          setGsmSearchCount(0);
                          setGsmSearchPage(1);
                        }}
                        className={`h-9 sm:h-10 ${isDark ? "border-slate-700 text-amber-400 hover:bg-slate-800" : "text-amber-600 hover:bg-amber-50"}`}
                        title="Sıfırla"
                      >
                        <FilterX className="w-4 h-4" />
                      </Button>
                      {canExport && gsmSearchResults.length > 0 && (
                        <Button
                          variant="outline"
                          onClick={handleGsmExport}
                          disabled={gsmExporting}
                          className={`h-9 sm:h-10 ${isDark ? "border-slate-700 text-slate-300" : ""}`}
                        >
                          {gsmExporting ? <div className="spinner w-4 h-4" /> : <Download className="w-4 h-4" />}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Search Results */}
            <Card className={cardClass}>
              <CardHeader className="pb-4 flex flex-row items-center justify-between">
                <CardTitle className={`text-lg font-semibold ${textClass}`}>
                  <Search className="w-5 h-5 inline mr-2 text-cyan-400" />
                  Arama Sonuçları
                  <span className={`text-sm font-normal ml-2 ${labelClass}`}>({gsmSearchCount.toLocaleString('tr-TR')})</span>
                </CardTitle>
                <Select value={gsmSearchLimit.toString()} onValueChange={(v) => { setGsmSearchLimit(parseInt(v)); setGsmSearchPage(1); }}>
                  <SelectTrigger className={`w-20 ${inputClass}`}><SelectValue /></SelectTrigger>
                  <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className={isDark ? "border-slate-800" : "border-gray-200"}>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Tarih</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Yön</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Agent</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Arayan</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Aranan</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Süre</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Durum</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Sistem</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Cihaz ID</TableHead>
                        {canSeeRecordings && <TableHead className={isDark ? "text-slate-400" : ""}>Kayıt</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {gsmSearchLoading ? (
                        <TableRow><TableCell colSpan={10} className="text-center py-12"><div className="spinner w-6 h-6 mx-auto" /></TableCell></TableRow>
                      ) : gsmSearchResults.length === 0 ? (
                        <TableRow><TableCell colSpan={10} className={`text-center py-12 ${labelClass}`}>
                          {gsmSearchNumber ? "Sonuç bulunamadı" : "Arama yapmak için GSM numarası girin"}
                        </TableCell></TableRow>
                      ) : (
                        gsmSearchResults.map((r, i) => (
                          <TableRow key={r.id || i} className={isDark ? "border-slate-800/50 hover:bg-slate-800/30" : "hover:bg-gray-50"}>
                            <TableCell className={`font-mono whitespace-nowrap text-xs sm:text-sm ${isDark ? "text-slate-300" : ""}`}>{r.tarih}</TableCell>
                            <TableCell>
                              {String(r.durum) !== '1' ? (
                                // Cevapsız - kırmızı renk ve cevapsız ikon
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${isDark ? "bg-red-500/20 text-red-400" : "bg-red-100 text-red-700"}`}>
                                  <PhoneMissed className="w-3 h-3" />
                                  {r.yon === 1 ? "Gelen" : r.yon === 2 ? "Giden" : "-"}
                                </span>
                              ) : r.yon === 1 ? (
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${isDark ? "bg-green-500/20 text-green-400" : "bg-green-100 text-green-700"}`}>
                                  <PhoneIncoming className="w-3 h-3" />Gelen
                                </span>
                              ) : r.yon === 2 ? (
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${isDark ? "bg-blue-500/20 text-blue-400" : "bg-blue-100 text-blue-700"}`}>
                                  <PhoneOutgoing className="w-3 h-3" />Giden
                                </span>
                              ) : (
                                <span className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>-</span>
                              )}
                            </TableCell>
                            <TableCell className={`text-sm ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                              {r.agent_name ? (
                                <span 
                                  className={`inline-flex items-center gap-1 px-2 py-1 rounded cursor-pointer hover:ring-2 hover:ring-cyan-400/50 transition-all ${getAgentColor(r.agent_name, isDark)}`}
                                  onClick={() => {
                                    const agent = agents.find(a => a.ad_soyad === r.agent_name);
                                    if (agent) {
                                      setSelectedAgent(agent);
                                      setActiveTab("cdr");
                                      toast.success(`${r.agent_name} filtrelendi - Çağrılar sayfasına yönlendirildi`);
                                    }
                                  }}
                                  title="Tıkla ve filtrele"
                                >
                                  <User className="w-3 h-3" />{r.agent_name}
                                </span>
                              ) : <span className={labelClass}>-</span>}
                            </TableCell>
                            <TableCell className={`font-mono text-xs sm:text-sm ${isDark ? "text-slate-300" : ""}`}>{r.arayan_numara}</TableCell>
                            <TableCell className={`font-mono text-xs sm:text-sm ${isDark ? "text-slate-300" : ""}`}>{r.aranan_numara}</TableCell>
                            <TableCell className={`font-mono text-xs sm:text-sm ${isDark ? "text-slate-300" : ""}`}>{formatDuration(r.konusma_suresi)}</TableCell>
                            <TableCell><StatusBadge durum={r.durum} durum_aciklama={r.durum_aciklama} isDark={isDark} /></TableCell>
                            <TableCell>
                              {r.sistem === 1 ? (
                                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${isDark ? "bg-cyan-500/20 text-cyan-400" : "bg-cyan-100 text-cyan-700"}`} title="GSM">
                                  <Smartphone className="w-3.5 h-3.5" />
                                </span>
                              ) : (
                                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${isDark ? "bg-amber-500/20 text-amber-400" : "bg-amber-100 text-amber-700"}`} title="Sabit Hat">
                                  <Phone className="w-3.5 h-3.5" />
                                </span>
                              )}
                            </TableCell>
                            <TableCell className={`font-mono text-sm ${isDark ? "text-slate-400" : "text-gray-600"}`}>
                              {r.device_id ? (
                                <span 
                                  className={`px-2 py-1 rounded cursor-pointer hover:ring-2 hover:ring-cyan-400/50 transition-all ${isDark ? "bg-slate-800 text-cyan-400" : "bg-gray-100 text-cyan-700"}`}
                                  onClick={async () => {
                                    try {
                                      const res = await axios.get(`${API}/devices?search=&page=1&limit=100`, {
                                        headers: { Authorization: `Bearer ${token}` }
                                      });
                                      const devicesList = res.data.data || [];
                                      const deviceFromList = devicesList.find(d => d.id === r.device_id || d.id === parseInt(r.device_id));
                                      
                                      if (deviceFromList) {
                                        setSelectedDeviceForLogs(deviceFromList);
                                      } else {
                                        setSelectedDeviceForLogs({
                                          id: r.device_id,
                                          device_id: String(r.device_id),
                                          device_name: `Cihaz #${r.device_id}`,
                                          device_model: '-',
                                          gsm: r.arayan_numara || r.aranan_numara || '-'
                                        });
                                      }
                                    } catch (err) {
                                      setSelectedDeviceForLogs({
                                        id: r.device_id,
                                        device_id: String(r.device_id),
                                        device_name: `Cihaz #${r.device_id}`,
                                        device_model: '-',
                                        gsm: r.arayan_numara || r.aranan_numara || '-'
                                      });
                                    }
                                    setCallLogsModalOpen(true);
                                    const today = new Date();
                                    const weekAgo = new Date(today);
                                    weekAgo.setDate(weekAgo.getDate() - 7);
                                    fetchCallLogs(r.device_id, 1, weekAgo.toISOString().split('T')[0], today.toISOString().split('T')[0], "00:00", "23:59");
                                  }}
                                  title="Çağrı kayıtlarını görüntüle"
                                >
                                  <Smartphone className="w-3 h-3 inline mr-1" />{r.device_id}
                                </span>
                              ) : <span className={labelClass}>-</span>}
                            </TableCell>
                            {canSeeRecordings && (
                              <TableCell>
                                {r.recording ? (
                                  <Button variant="ghost" size="sm" onClick={() => { setSelectedAudio({ url: r.recording, record: r }); setAudioModalOpen(true); }} className={`p-1 h-auto ${isDark ? "text-cyan-400" : "text-blue-600"}`}>
                                    <Volume2 className="w-4 h-4 mr-1" /><span className="text-xs">Dinle</span>
                                  </Button>
                                ) : <span className={`text-xs ${isDark ? "text-slate-600" : "text-gray-400"}`}>-</span>}
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                {Math.ceil(gsmSearchCount / gsmSearchLimit) > 1 && (
                  <div className={`flex items-center justify-between px-4 py-4 border-t ${isDark ? "border-slate-800" : "border-gray-200"}`}>
                    <div className={`text-sm ${labelClass}`}>Sayfa {gsmSearchPage} / {Math.ceil(gsmSearchCount / gsmSearchLimit)}</div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => { setGsmSearchPage(1); fetchGsmSearch(); }} disabled={gsmSearchPage === 1}>İlk</Button>
                      <Button variant="ghost" size="sm" onClick={() => { setGsmSearchPage(p => Math.max(1, p - 1)); }} disabled={gsmSearchPage === 1}><ChevronLeft className="w-4 h-4" /></Button>
                      <span className={`px-3 py-1 rounded ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>{gsmSearchPage}</span>
                      <Button variant="ghost" size="sm" onClick={() => { setGsmSearchPage(p => Math.min(Math.ceil(gsmSearchCount / gsmSearchLimit), p + 1)); }} disabled={gsmSearchPage === Math.ceil(gsmSearchCount / gsmSearchLimit)}><ChevronRight className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => { setGsmSearchPage(Math.ceil(gsmSearchCount / gsmSearchLimit)); }} disabled={gsmSearchPage === Math.ceil(gsmSearchCount / gsmSearchLimit)}>Son</Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          )}

          {/* Transcripts Tab */}
          {canSeeRecordings && (
            <TabsContent value="transcripts" className="space-y-6">
              {/* Transcript Detail View */}
              {selectedTranscriptRecord ? (
                <Card className={cardClass}>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <CardTitle className={`text-lg font-semibold ${textClass}`}>
                          <FileText className="w-5 h-5 inline mr-2 text-emerald-500" />
                          Transkript Detayı
                        </CardTitle>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className={`w-4 h-4 ${labelClass}`} />
                          <span className="font-mono">{selectedTranscriptRecord.tarih}</span>
                          <Clock className={`w-4 h-4 ml-2 ${labelClass}`} />
                          <span className="font-mono">{formatDuration(selectedTranscriptRecord.konusma_suresi)}</span>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setSelectedTranscriptRecord(null)}>
                        <X className="w-5 h-5" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
                      {/* Left Panel - Summary */}
                      <div className={`p-6 border-r ${isDark ? "border-slate-800 bg-slate-900/50" : "border-gray-200 bg-gray-50"}`}>
                        <div className="space-y-6">
                          {/* Call Info */}
                          <div>
                            <h4 className={`text-sm font-medium mb-3 ${labelClass}`}>Çağrı Bilgisi</h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex items-center justify-between">
                                <span className={labelClass}>Arayan:</span>
                                <span className="font-mono font-medium">{selectedTranscriptRecord.arayan_numara}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className={labelClass}>Aranan:</span>
                                <span className="font-mono font-medium">{selectedTranscriptRecord.aranan_numara}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className={labelClass}>Yön:</span>
                                <Badge className={selectedTranscriptRecord.yon === "gelen" ? (isDark ? "bg-emerald-500/20 text-emerald-400" : "bg-emerald-100 text-emerald-700") : (isDark ? "bg-blue-500/20 text-blue-400" : "bg-blue-100 text-blue-700")}>
                                  {selectedTranscriptRecord.yon === "gelen" ? <PhoneIncoming className="w-3 h-3 mr-1" /> : <PhoneOutgoing className="w-3 h-3 mr-1" />}
                                  {selectedTranscriptRecord.yon === "gelen" ? "Gelen" : "Giden"}
                                </Badge>
                              </div>
                            </div>
                          </div>

                          {/* Summary */}
                          {selectedTranscriptRecord.summary_text && (
                            <div>
                              <h4 className={`text-sm font-medium mb-3 ${labelClass}`}>
                                <FileText className="w-4 h-4 inline mr-2" />Özet
                              </h4>
                              <div className={`p-4 rounded-lg ${isDark ? "bg-slate-800" : "bg-white border border-gray-200"}`}>
                                <p className={`text-sm leading-relaxed ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                                  {selectedTranscriptRecord.summary_text}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Agent/Customer separate texts if available */}
                          {(selectedTranscriptRecord.agent_text || selectedTranscriptRecord.customer_text) && (
                            <div>
                              <h4 className={`text-sm font-medium mb-3 ${labelClass}`}>Konuşmacı Ayrımı</h4>
                              <div className="space-y-3">
                                {selectedTranscriptRecord.agent_text && (
                                  <div className={`p-3 rounded-lg border-l-4 border-blue-500 ${isDark ? "bg-blue-500/10" : "bg-blue-50"}`}>
                                    <div className="flex items-center gap-2 mb-1">
                                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isDark ? "bg-blue-500/20 text-blue-400" : "bg-blue-100 text-blue-600"}`}>1</div>
                                      <span className={`text-xs font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>Konuşmacı 1</span>
                                    </div>
                                    <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-600"}`}>{selectedTranscriptRecord.agent_text.substring(0, 100)}...</p>
                                  </div>
                                )}
                                {selectedTranscriptRecord.customer_text && (
                                  <div className={`p-3 rounded-lg border-l-4 border-purple-500 ${isDark ? "bg-purple-500/10" : "bg-purple-50"}`}>
                                    <div className="flex items-center gap-2 mb-1">
                                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isDark ? "bg-purple-500/20 text-purple-400" : "bg-purple-100 text-purple-600"}`}>2</div>
                                      <span className={`text-xs font-medium ${isDark ? "text-purple-400" : "text-purple-600"}`}>Konuşmacı 2</span>
                                    </div>
                                    <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-600"}`}>{selectedTranscriptRecord.customer_text.substring(0, 100)}...</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Right Panel - Conversation Timeline */}
                      <div className="lg:col-span-2 flex flex-col">
                        {/* Transcript Content */}
                        <div className="flex-1 p-6 overflow-y-auto max-h-[500px]">
                          {selectedTranscriptRecord.transcript_data && Array.isArray(selectedTranscriptRecord.transcript_data) ? (
                            /* JSON formatted transcript with timestamps */
                            <div className="space-y-1">
                              {selectedTranscriptRecord.transcript_data.map((item, index) => (
                                <div 
                                  key={index} 
                                  className={`flex items-start gap-3 py-2 px-3 rounded-lg transition-colors hover:${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}
                                >
                                  {/* Timestamp */}
                                  <span className={`font-mono text-xs ${isDark ? "text-cyan-400" : "text-cyan-600"} whitespace-nowrap pt-0.5`}>
                                    {item.time || item.timestamp || "00:00:00"}
                                  </span>
                                  {/* Speaker Badge */}
                                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                                    item.speaker === 1 || item.speaker === "1" 
                                      ? (isDark ? "bg-blue-500/20 text-blue-400" : "bg-blue-100 text-blue-600")
                                      : (isDark ? "bg-purple-500/20 text-purple-400" : "bg-purple-100 text-purple-600")
                                  }`}>
                                    {item.speaker || "1"}
                                  </div>
                                  {/* Speaker Label */}
                                  <span className={`text-xs font-medium whitespace-nowrap pt-0.5 ${
                                    item.speaker === 1 || item.speaker === "1"
                                      ? (isDark ? "text-blue-400" : "text-blue-600")
                                      : (isDark ? "text-purple-400" : "text-purple-600")
                                  }`}>
                                    Konuşmacı {item.speaker || 1}
                                  </span>
                                  {/* Text */}
                                  <p className={`text-sm flex-1 ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                                    {item.text || item.content || item.message}
                                  </p>
                                </div>
                              ))}
                            </div>
                          ) : selectedTranscriptRecord.transcript_text ? (
                            /* HTML/Text formatted transcript */
                            <div className="space-y-1">
                              <h4 className={`text-sm font-medium mb-4 ${labelClass}`}>
                                <MessageSquare className="w-4 h-4 inline mr-2" />Konuşma Metni
                              </h4>
                              <div 
                                className={`prose prose-sm max-w-none ${isDark ? "prose-invert" : ""}`}
                                dangerouslySetInnerHTML={{ __html: selectedTranscriptRecord.transcript_text }}
                              />
                            </div>
                          ) : (
                            <div className={`text-center py-12 ${labelClass}`}>
                              <Mic className="w-12 h-12 mx-auto mb-4 opacity-50" />
                              <p className="mb-4">Bu kayıt için henüz transkript oluşturulmamış</p>
                              <Button 
                                onClick={() => handleStartTranscript(selectedTranscriptRecord)}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              >
                                <FileText className="w-4 h-4 mr-2" />Transkript Oluştur
                              </Button>
                            </div>
                          )}
                        </div>

                        {/* Audio Player - Fixed at bottom */}
                        {selectedTranscriptRecord.recording && (
                          <div className={`p-4 border-t ${isDark ? "border-slate-800 bg-slate-900/50" : "border-gray-200 bg-gray-50"}`}>
                            <TranscriptAudioPlayer 
                              audioUrl={selectedTranscriptRecord.recording} 
                              isDark={isDark}
                              duration={selectedTranscriptRecord.konusma_suresi}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                /* Transcripts List */
                <Card className={cardClass}>
                  <CardHeader className="pb-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <CardTitle className={`text-lg font-semibold ${textClass}`}>
                        <FileText className="w-5 h-5 inline mr-2 text-emerald-500" />
                        Ses Transkriptleri
                        <span className={`text-sm font-normal ml-2 ${labelClass}`}>({transcriptTotal})</span>
                      </CardTitle>
                      <div className="flex items-center gap-3">
                        <Select value={transcriptFilter} onValueChange={(v) => { setTranscriptFilter(v); setTranscriptPage(1); }}>
                          <SelectTrigger className={`w-40 ${inputClass}`}><SelectValue /></SelectTrigger>
                          <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                            <SelectItem value="all">Tümü</SelectItem>
                            <SelectItem value="has">Transkripti Var</SelectItem>
                            <SelectItem value="none">Transkripti Yok</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className={isDark ? "border-slate-800" : ""}>
                            <TableHead className={isDark ? "text-slate-400" : ""}>Tarih</TableHead>
                            <TableHead className={isDark ? "text-slate-400" : ""}>Arayan</TableHead>
                            <TableHead className={isDark ? "text-slate-400" : ""}>Aranan</TableHead>
                            <TableHead className={isDark ? "text-slate-400" : ""}>Süre</TableHead>
                            <TableHead className={isDark ? "text-slate-400" : ""}>Durum</TableHead>
                            <TableHead className={isDark ? "text-slate-400" : ""}>Transkript</TableHead>
                            <TableHead className={isDark ? "text-slate-400" : ""}>İşlemler</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {transcriptsLoading ? (
                            <TableRow><TableCell colSpan={7} className="text-center py-12"><div className="spinner w-6 h-6 mx-auto" /></TableCell></TableRow>
                          ) : transcripts.length === 0 ? (
                            <TableRow><TableCell colSpan={7} className={`text-center py-12 ${labelClass}`}>
                              Transkript bulunamadı
                            </TableCell></TableRow>
                          ) : transcripts.map(t => (
                            <TableRow key={t.id} className={isDark ? "border-slate-800/50 hover:bg-slate-800/30" : "hover:bg-gray-50"}>
                              <TableCell className={`font-mono text-sm ${isDark ? "text-slate-300" : ""}`}>{t.tarih}</TableCell>
                              <TableCell className={`font-mono text-sm ${isDark ? "text-slate-300" : ""}`}>{t.arayan_numara}</TableCell>
                              <TableCell className={`font-mono text-sm ${isDark ? "text-slate-300" : ""}`}>{t.aranan_numara}</TableCell>
                              <TableCell className={`font-mono text-sm ${isDark ? "text-slate-300" : ""}`}>{formatDuration(t.konusma_suresi)}</TableCell>
                              <TableCell>
                                <Badge className={
                                  t.status === "completed" ? (isDark ? "bg-emerald-500/20 text-emerald-400" : "bg-emerald-100 text-emerald-700") :
                                  t.status === "processing" ? (isDark ? "bg-amber-500/20 text-amber-400" : "bg-amber-100 text-amber-700") :
                                  t.status === "failed" ? (isDark ? "bg-rose-500/20 text-rose-400" : "bg-rose-100 text-rose-700") :
                                  (isDark ? "bg-slate-500/20 text-slate-400" : "bg-gray-100 text-gray-600")
                                }>
                                  {t.status === "completed" ? "Tamamlandı" : t.status === "processing" ? "İşleniyor" : t.status === "failed" ? "Hata" : "Bekliyor"}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {t.transcript_text ? (
                                  <Badge className={isDark ? "bg-emerald-500/20 text-emerald-400" : "bg-emerald-100 text-emerald-700"}>
                                    <CheckSquare className="w-3 h-3 mr-1" />Var
                                  </Badge>
                                ) : (
                                  <Badge className={isDark ? "bg-slate-500/20 text-slate-400" : "bg-gray-100 text-gray-600"}>
                                    <Square className="w-3 h-3 mr-1" />Yok
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => handleViewTranscript(t.cdr_id)}
                                    className={isDark ? "text-emerald-400" : "text-emerald-600"}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  {t.recording_url && (
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      onClick={() => { setSelectedAudio({ url: t.recording_url, record: t }); setAudioModalOpen(true); }}
                                      className={isDark ? "text-cyan-400" : "text-blue-600"}
                                    >
                                      <Volume2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    {Math.ceil(transcriptTotal / 20) > 1 && (
                      <div className={`flex items-center justify-between px-4 py-4 border-t ${isDark ? "border-slate-800" : "border-gray-200"}`}>
                        <div className={`text-sm ${labelClass}`}>Sayfa {transcriptPage} / {Math.ceil(transcriptTotal / 20)}</div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => setTranscriptPage(1)} disabled={transcriptPage === 1}>İlk</Button>
                          <Button variant="ghost" size="sm" onClick={() => setTranscriptPage(p => Math.max(1, p - 1))} disabled={transcriptPage === 1}><ChevronLeft className="w-4 h-4" /></Button>
                          <span className={`px-3 py-1 rounded ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>{transcriptPage}</span>
                          <Button variant="ghost" size="sm" onClick={() => setTranscriptPage(p => Math.min(Math.ceil(transcriptTotal / 20), p + 1))} disabled={transcriptPage === Math.ceil(transcriptTotal / 20)}><ChevronRight className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => setTranscriptPage(Math.ceil(transcriptTotal / 20))} disabled={transcriptPage === Math.ceil(transcriptTotal / 20)}>Son</Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          )}

          {/* Agents Tab */}
          <TabsContent value="agents" className="space-y-6">
            <Card className={cardClass}>
              <CardHeader className="pb-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <CardTitle className={`text-lg font-semibold ${textClass}`}><Users className="w-5 h-5 inline mr-2 text-blue-400" />Agent Yönetimi</CardTitle>
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? "text-slate-500" : "text-gray-400"}`} />
                      <Input
                        placeholder="Agent ara..."
                        value={agentSearchTerm}
                        onChange={(e) => setAgentSearchTerm(e.target.value)}
                        className={`pl-10 w-64 ${inputClass}`}
                      />
                    </div>
                    {canManageAgents && !hasAgentRestrictions && (
                      <Button onClick={() => { setEditingAgent(null); setAgentModalOpen(true); }} className="bg-blue-600 hover:bg-blue-700 text-white">
                        <Plus className="w-4 h-4 mr-2" />Yeni Agent
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className={isDark ? "border-slate-800" : ""}>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Ad Soyad</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Kullanıcı Adı</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>GSM</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Cihaz</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Token</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Durum</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Yetki</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAgents.length === 0 ? (
                        <TableRow><TableCell colSpan={8} className={`text-center py-12 ${labelClass}`}>
                          {agentSearchTerm ? "Arama sonucu bulunamadı" : "Agent bulunamadı"}
                        </TableCell></TableRow>
                      ) : filteredAgents.map(a => (
                        <TableRow key={a.id} className={isDark ? "border-slate-800/50 hover:bg-slate-800/30" : "hover:bg-gray-50"}>
                          <TableCell className={`font-medium ${isDark ? "text-slate-200" : ""}`}>{a.ad_soyad}</TableCell>
                          <TableCell className={`font-mono ${isDark ? "text-slate-400" : ""}`}>{a.kullanici_adi}</TableCell>
                          <TableCell className={`font-mono ${isDark ? "text-slate-300" : ""}`}>{a.gsm || "-"}</TableCell>
                          <TableCell>
                            <Badge className={agentDeviceCounts[a.id] > 0 ? (isDark ? "bg-pink-500/20 text-pink-400" : "bg-pink-100 text-pink-700") : (isDark ? "bg-slate-500/20 text-slate-400" : "bg-gray-100 text-gray-500")}>
                              <Smartphone className="w-3 h-3 mr-1" />
                              {agentDeviceCounts[a.id] || 0}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => { setSelectedTokenAgent(a); setTokenModalOpen(true); }}
                              className={`${isDark ? "text-amber-400 hover:text-amber-300" : "text-amber-600 hover:text-amber-700"}`}
                            >
                              <Key className="w-4 h-4 mr-1" />
                              <span className="text-xs">Göster</span>
                            </Button>
                          </TableCell>
                          <TableCell>
                            <Badge className={a.status === 1 ? (isDark ? "bg-emerald-500/20 text-emerald-400" : "bg-emerald-100 text-emerald-700") : (isDark ? "bg-slate-500/20 text-slate-400" : "bg-gray-100 text-gray-600")}>
                              {a.status === 1 ? "Aktif" : "Pasif"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={isDark ? "bg-blue-500/20 text-blue-400" : "bg-blue-100 text-blue-700"}>
                              <Shield className="w-3 h-3 mr-1" />{a.yetki_durumu === 3 ? "Admin" : a.yetki_durumu === 2 ? "Yönetici" : "Kullanıcı"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {canViewAnalytics && <Button variant="ghost" size="sm" onClick={() => openAgentAnalytics(a)} className={isDark ? "text-emerald-400" : "text-emerald-600"} title="Analiz"><BarChart3 className="w-4 h-4" /></Button>}
                              <Button variant="ghost" size="sm" onClick={() => { setSelectedAgent(a); setActiveTab("cdr"); }} className={isDark ? "text-cyan-400" : "text-blue-600"}><Phone className="w-4 h-4" /></Button>
                              {canManageAgents && (
                                <>
                                  <Button variant="ghost" size="sm" onClick={() => { setEditingAgent(a); setAgentModalOpen(true); }} className={isDark ? "text-slate-400" : ""}><Pencil className="w-4 h-4" /></Button>
                                  <Button variant="ghost" size="sm" onClick={() => handleDeleteAgent(a.id)} className={isDark ? "text-rose-400" : "text-rose-600"}><Trash2 className="w-4 h-4" /></Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {filteredAgents.length > 0 && (
                  <div className={`px-4 py-3 border-t ${isDark ? "border-slate-800" : "border-gray-200"}`}>
                    <p className={`text-sm ${labelClass}`}>Toplam {filteredAgents.length} agent {agentSearchTerm && `(${visibleAgents.length} içinden)`}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Token Modal */}
          {tokenModalOpen && selectedTokenAgent && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
              <div className={`w-full max-w-lg mx-4 rounded-xl shadow-2xl ${isDark ? "bg-slate-900 border border-slate-800" : "bg-white"}`}>
                <div className={`flex items-center justify-between p-4 border-b ${isDark ? "border-slate-800" : "border-gray-200"}`}>
                  <h3 className={`text-lg font-semibold ${textClass}`}>
                    <Key className="w-5 h-5 inline mr-2 text-amber-500" />
                    Agent Token - {selectedTokenAgent.ad_soyad}
                  </h3>
                  <Button variant="ghost" size="sm" onClick={() => setTokenModalOpen(false)}><X className="w-5 h-5" /></Button>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${labelClass}`}>Token</label>
                    <div className={`relative p-4 rounded-lg font-mono text-sm break-all ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
                      {selectedTokenAgent.token || "Token bulunamadı"}
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`absolute top-2 right-2 ${isDark ? "text-slate-400 hover:text-white" : ""}`}
                        onClick={() => {
                          navigator.clipboard.writeText(selectedTokenAgent.token || "");
                          toast.success("Token kopyalandı!");
                        }}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {canManageAgents && (
                    <Button
                      variant="outline"
                      className={`w-full ${isDark ? "border-slate-700" : ""}`}
                      onClick={async () => {
                        if (window.confirm("Token yenilensin mi? Eski token geçersiz olacak.")) {
                          try {
                            const res = await axios.post(`${API}/agents/regenerate-token`, { agent_id: selectedTokenAgent.id });
                            toast.success("Token yenilendi!");
                            setSelectedTokenAgent({ ...selectedTokenAgent, token: res.data.token });
                            fetchAgents();
                          } catch (error) {
                            toast.error("Token yenilenemedi");
                          }
                        }
                      }}
                    >
                      <RotateCw className="w-4 h-4 mr-2" />Token Yenile
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Agent Analytics Modal */}
          {analyticsAgent && (
            <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm overflow-y-auto">
              <div className={`w-full max-w-6xl mx-4 my-4 rounded-xl shadow-2xl flex flex-col max-h-[95vh] ${isDark ? "bg-slate-900 border border-slate-800" : "bg-white"}`}>
                {/* Sticky Header */}
                <div className={`sticky top-0 z-10 flex items-center justify-between p-4 border-b rounded-t-xl ${isDark ? "border-slate-800 bg-slate-900" : "border-gray-200 bg-white"}`}>
                  <h3 className={`text-lg font-semibold ${textClass}`}>
                    <BarChart3 className="w-5 h-5 inline mr-2 text-emerald-500" />
                    Agent Performans Analizi - {analyticsAgent.ad_soyad}
                  </h3>
                  <Button variant="ghost" size="sm" onClick={closeAgentAnalytics} className="hover:bg-red-100 hover:text-red-600"><X className="w-5 h-5" /></Button>
                </div>
                
                {/* Date Range Selector - Also Sticky */}
                <div className={`sticky top-[60px] z-10 p-4 border-b ${isDark ? "border-slate-800 bg-slate-800/95" : "border-gray-200 bg-gray-50"}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <Label className={`text-sm font-medium ${labelClass}`}>Tarih Aralığı:</Label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { value: "today", label: "Bugün" },
                        { value: "yesterday", label: "Dün" },
                        { value: "this_week", label: "Bu Hafta" },
                        { value: "this_month", label: "Bu Ay" },
                        { value: "last_month", label: "Geçen Ay" },
                        { value: "custom", label: "Özel" },
                      ].map((opt) => (
                        <Button
                          key={opt.value}
                          variant={analyticsDateRange === opt.value ? "default" : "outline"}
                          size="sm"
                          className={analyticsDateRange === opt.value 
                            ? "bg-emerald-600 hover:bg-emerald-700 text-white" 
                            : isDark ? "border-slate-700 text-slate-300" : ""}
                          onClick={() => setAnalyticsDateRange(opt.value)}
                        >
                          {opt.label}
                        </Button>
                      ))}
                    </div>
                    {analyticsDateRange === "custom" && (
                      <div className="flex items-center gap-2 ml-4">
                        <Input
                          type="date"
                          value={analyticsCustomStart}
                          onChange={(e) => setAnalyticsCustomStart(e.target.value)}
                          className={`w-40 ${inputClass}`}
                        />
                        <span className={labelClass}>-</span>
                        <Input
                          type="date"
                          value={analyticsCustomEnd}
                          onChange={(e) => setAnalyticsCustomEnd(e.target.value)}
                          className={`w-40 ${inputClass}`}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Analytics Content - Scrollable */}
                <div className="p-6 overflow-y-auto flex-1">
                  {analyticsLoading ? (
                    <div className="flex items-center justify-center py-20">
                      <div className="flex flex-col items-center gap-4">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
                        <p className={labelClass}>Analiz verileri yükleniyor...</p>
                      </div>
                    </div>
                  ) : analyticsData ? (
                    <div className="space-y-6">
                      {/* Summary Cards */}
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        <div className={`p-4 rounded-xl ${isDark ? "bg-slate-800" : "bg-blue-50 border border-blue-100"}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? "text-slate-400" : "text-blue-600"}`}>Toplam Çağrı</div>
                          <div className={`text-2xl font-bold ${isDark ? "text-blue-400" : "text-blue-700"}`}>{analyticsData.summary.total_calls}</div>
                        </div>
                        <div className={`p-4 rounded-xl ${isDark ? "bg-slate-800" : "bg-emerald-50 border border-emerald-100"}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? "text-slate-400" : "text-emerald-600"}`}>Cevaplanan</div>
                          <div className={`text-2xl font-bold ${isDark ? "text-emerald-400" : "text-emerald-700"}`}>{analyticsData.summary.answered_calls}</div>
                          <div className={`text-xs ${isDark ? "text-slate-500" : "text-emerald-500"}`}>%{analyticsData.summary.answer_rate} başarı</div>
                        </div>
                        <div className={`p-4 rounded-xl ${isDark ? "bg-slate-800" : "bg-purple-50 border border-purple-100"}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? "text-slate-400" : "text-purple-600"}`}>Toplam Konuşma</div>
                          <div className={`text-2xl font-bold ${isDark ? "text-purple-400" : "text-purple-700"}`}>{analyticsData.summary.total_talk_minutes} dk</div>
                        </div>
                        <div className={`p-4 rounded-xl ${isDark ? "bg-slate-800" : "bg-amber-50 border border-amber-100"}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? "text-slate-400" : "text-amber-600"}`}>Ort. Konuşma</div>
                          <div className={`text-2xl font-bold ${isDark ? "text-amber-400" : "text-amber-700"}`}>{analyticsData.summary.avg_talk_seconds} sn</div>
                        </div>
                        <div className={`p-4 rounded-xl ${isDark ? "bg-slate-800" : "bg-cyan-50 border border-cyan-100"}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? "text-slate-400" : "text-cyan-600"}`}>Giden Çağrı</div>
                          <div className={`text-2xl font-bold ${isDark ? "text-cyan-400" : "text-cyan-700"}`}>{analyticsData.summary.outgoing_calls}</div>
                        </div>
                        <div className={`p-4 rounded-xl ${isDark ? "bg-slate-800" : "bg-rose-50 border border-rose-100"}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? "text-slate-400" : "text-rose-600"}`}>Gelen Çağrı</div>
                          <div className={`text-2xl font-bold ${isDark ? "text-rose-400" : "text-rose-700"}`}>{analyticsData.summary.incoming_calls}</div>
                        </div>
                      </div>

                      {/* Work Hours Card */}
                      <div className={`p-6 rounded-xl ${isDark ? "bg-gradient-to-br from-emerald-900/30 to-cyan-900/30 border border-emerald-800/50" : "bg-gradient-to-br from-emerald-50 to-cyan-50 border border-emerald-200"}`}>
                        <h4 className={`text-sm font-semibold mb-4 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                          <Clock className="w-4 h-4 inline mr-2" />Çalışma Saatleri
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                          <div>
                            <div className={`text-xs mb-1 ${labelClass}`}>İlk Çağrı (İşe Başlama)</div>
                            <div className={`text-lg font-mono font-bold ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>
                              {analyticsData.summary.first_call ? analyticsData.summary.first_call.split(' ')[1] : "-"}
                            </div>
                            <div className={`text-xs ${labelClass}`}>
                              {analyticsData.summary.first_call ? analyticsData.summary.first_call.split(' ')[0] : ""}
                            </div>
                          </div>
                          <div>
                            <div className={`text-xs mb-1 ${labelClass}`}>Son Çağrı (İş Bitirme)</div>
                            <div className={`text-lg font-mono font-bold ${isDark ? "text-cyan-400" : "text-cyan-600"}`}>
                              {analyticsData.summary.last_call ? analyticsData.summary.last_call.split(' ')[1] : "-"}
                            </div>
                            <div className={`text-xs ${labelClass}`}>
                              {analyticsData.summary.last_call ? analyticsData.summary.last_call.split(' ')[0] : ""}
                            </div>
                          </div>
                          <div>
                            <div className={`text-xs mb-1 ${labelClass}`}>Toplam Çalışma Süresi</div>
                            <div className={`text-lg font-bold ${isDark ? "text-amber-400" : "text-amber-600"}`}>
                              {Math.floor(analyticsData.summary.working_minutes / 60)} saat {analyticsData.summary.working_minutes % 60} dk
                            </div>
                          </div>
                          <div>
                            <div className={`text-xs mb-1 ${labelClass}`}>Aktif Konuşma Oranı</div>
                            <div className={`text-lg font-bold ${isDark ? "text-purple-400" : "text-purple-600"}`}>
                              {analyticsData.summary.working_minutes > 0 
                                ? Math.round((analyticsData.summary.total_talk_minutes / analyticsData.summary.working_minutes) * 100) 
                                : 0}%
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Hourly Distribution */}
                      <div className={`p-6 rounded-xl ${isDark ? "bg-slate-800" : "bg-gray-50 border border-gray-200"}`}>
                        <h4 className={`text-sm font-semibold mb-4 ${textClass}`}>
                          <BarChart3 className="w-4 h-4 inline mr-2" />Saatlik Çağrı Dağılımı
                        </h4>
                        {analyticsData.hourly_distribution && analyticsData.hourly_distribution.length > 0 ? (
                          <div>
                            <div className="flex items-end gap-1" style={{ height: '120px' }}>
                              {Array.from({ length: 24 }, (_, hour) => {
                                const hourData = analyticsData.hourly_distribution.find(h => parseInt(h.hour) === hour);
                                const count = hourData ? parseInt(hourData.call_count) || 0 : 0;
                                const maxCount = Math.max(...analyticsData.hourly_distribution.map(h => parseInt(h.call_count) || 0), 1);
                                const heightPx = count > 0 ? Math.max((count / maxCount) * 100, 8) : 3;
                                return (
                                  <div 
                                    key={hour} 
                                    className={`flex-1 rounded-t cursor-pointer transition-all hover:opacity-80 ${count > 0 ? (isDark ? "bg-emerald-500" : "bg-emerald-400") : (isDark ? "bg-slate-700" : "bg-gray-200")}`}
                                    style={{ height: `${heightPx}px` }}
                                    title={`${hour}:00 - ${count} çağrı`}
                                  />
                                );
                              })}
                            </div>
                            <div className="flex gap-1 mt-1">
                              {Array.from({ length: 24 }, (_, hour) => (
                                <div key={hour} className="flex-1 text-center">
                                  <span className={`text-[8px] ${labelClass}`}>{hour}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className={`text-center py-8 ${labelClass}`}>
                            <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-30" />
                            <p>Bu dönemde saatlik çağrı verisi bulunamadı</p>
                          </div>
                        )}
                      </div>

                      {/* Daily Breakdown Table */}
                      <div className={`rounded-xl overflow-hidden ${isDark ? "bg-slate-800" : "bg-white border border-gray-200"}`}>
                        <h4 className={`text-sm font-semibold p-4 border-b ${isDark ? "border-slate-700 text-slate-200" : "border-gray-200 text-gray-800"}`}>
                          <Calendar className="w-4 h-4 inline mr-2" />Günlük Detay
                        </h4>
                        {analyticsData.daily_breakdown && analyticsData.daily_breakdown.length > 0 ? (
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow className={isDark ? "border-slate-700" : ""}>
                                  <TableHead className={isDark ? "text-slate-400" : ""}>Tarih</TableHead>
                                  <TableHead className={isDark ? "text-slate-400" : ""}>Toplam</TableHead>
                                  <TableHead className={isDark ? "text-slate-400" : ""}>Cevaplanan</TableHead>
                                  <TableHead className={isDark ? "text-slate-400" : ""}>Cevapsız</TableHead>
                                  <TableHead className={isDark ? "text-slate-400" : ""}>Günlük Konuşma (dk)</TableHead>
                                  <TableHead className={isDark ? "text-slate-400" : ""}>İlk Çağrı</TableHead>
                                  <TableHead className={isDark ? "text-slate-400" : ""}>Son Çağrı</TableHead>
                                  <TableHead className={isDark ? "text-slate-400" : ""}>Çalışma</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {analyticsData.daily_breakdown.map((day, idx) => (
                                  <TableRow key={idx} className={isDark ? "border-slate-700/50" : ""}>
                                    <TableCell className={`font-mono ${isDark ? "text-slate-200" : ""}`}>{day.call_date}</TableCell>
                                    <TableCell className={`font-bold ${isDark ? "text-blue-400" : "text-blue-600"}`}>{day.total_calls}</TableCell>
                                    <TableCell className={isDark ? "text-emerald-400" : "text-emerald-600"}>{day.answered_calls}</TableCell>
                                    <TableCell className={isDark ? "text-amber-400" : "text-amber-600"}>{day.missed_calls || (day.total_calls - day.answered_calls)}</TableCell>
                                    <TableCell className={isDark ? "text-purple-400" : "text-purple-600"}>{Math.round((day.total_talk_time || 0) / 60)}</TableCell>
                                    <TableCell className={`font-mono text-sm ${isDark ? "text-cyan-400" : "text-cyan-600"}`}>{day.first_call || "-"}</TableCell>
                                    <TableCell className={`font-mono text-sm ${isDark ? "text-amber-400" : "text-amber-600"}`}>{day.last_call || "-"}</TableCell>
                                    <TableCell className={isDark ? "text-slate-300" : ""}>
                                      {day.working_minutes > 0 
                                        ? `${Math.floor(day.working_minutes / 60)}sa ${day.working_minutes % 60}dk`
                                        : "-"}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        ) : (
                          <div className={`text-center py-8 ${labelClass}`}>
                            <Calendar className="w-8 h-8 mx-auto mb-2 opacity-30" />
                            <p>Bu dönemde günlük veri bulunamadı</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className={`text-center py-20 ${labelClass}`}>
                      <BarChart3 className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p>Analiz verisi bulunamadı</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-6">
            <Card className={cardClass}>
              <CardHeader className="pb-4">
                <CardTitle className={`text-lg font-semibold ${textClass}`}>
                  <BarChart3 className="w-5 h-5 inline mr-2 text-teal-500" />
                  Agent Görüşme Raporu
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Filters - Mobile Responsive */}
                <div className={`p-3 sm:p-4 rounded-lg mb-4 sm:mb-6 ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
                  {/* Row 1: Date & Yon */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
                    <div>
                      <label className={`text-[10px] sm:text-xs font-medium ${labelClass} mb-1 block`}>Başlangıç</label>
                      <Input 
                        type="date" 
                        value={reportsStartDate} 
                        onChange={(e) => setReportsStartDate(e.target.value)}
                        className={`h-8 sm:h-9 text-xs sm:text-sm ${inputClass}`}
                      />
                    </div>
                    <div>
                      <label className={`text-[10px] sm:text-xs font-medium ${labelClass} mb-1 block`}>Bitiş</label>
                      <Input 
                        type="date" 
                        value={reportsEndDate} 
                        onChange={(e) => setReportsEndDate(e.target.value)}
                        className={`h-8 sm:h-9 text-xs sm:text-sm ${inputClass}`}
                      />
                    </div>
                    <div>
                      <label className={`text-[10px] sm:text-xs font-medium ${labelClass} mb-1 block`}>Yön</label>
                      <Select value={reportsYon} onValueChange={setReportsYon}>
                        <SelectTrigger className={`h-8 sm:h-9 text-xs sm:text-sm ${inputClass}`}><SelectValue /></SelectTrigger>
                        <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                          <SelectItem value="all">Tümü</SelectItem>
                          <SelectItem value="1">Gelen</SelectItem>
                          <SelectItem value="2">Giden</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end">
                      <Button 
                        onClick={fetchReports} 
                        disabled={reportsLoading || reportsSelectedAgents.length === 0}
                        className="w-full bg-teal-600 hover:bg-teal-700 text-white h-8 sm:h-9 text-xs sm:text-sm"
                      >
                        {reportsLoading ? <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" /> : <Search className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />}
                        <span className="hidden sm:inline">Rapor Al</span>
                        <span className="sm:hidden">Ara</span>
                      </Button>
                    </div>
                  </div>
                  
                  {/* Row 2: Agent Selection - Searchable Multi-Select Dropdown */}
                  <div className="mt-3 sm:mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className={`text-[10px] sm:text-xs font-medium ${labelClass}`}>
                        Agent Seçimi <span className={`${reportsSelectedAgents.length > 0 ? (isDark ? "text-teal-400" : "text-teal-600") : (isDark ? "text-red-400" : "text-red-500")}`}>({reportsSelectedAgents.length}/{agents.length})</span>
                      </label>
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={handleReportsSelectAllAgents}
                          className={`h-6 text-[10px] sm:text-xs px-2 ${isDark ? "text-teal-400 hover:text-teal-300" : "text-teal-600 hover:text-teal-700"}`}
                        >
                          {reportsSelectedAgents.length === agents.length && agents.length > 0 ? "Temizle" : "Tümünü Seç"}
                        </Button>
                      </div>
                    </div>
                    
                    {/* Dropdown Container */}
                    <div className="relative">
                      {/* Selected Tags & Search Input */}
                      <div 
                        className={`min-h-[42px] p-2 rounded-lg border cursor-text flex flex-wrap gap-1.5 items-center ${
                          isDark ? "bg-slate-900 border-slate-700" : "bg-white border-gray-300"
                        } ${reportsAgentDropdownOpen ? (isDark ? "ring-2 ring-teal-500" : "ring-2 ring-teal-400") : ""}`}
                        onClick={() => setReportsAgentDropdownOpen(true)}
                      >
                        {/* Selected Tags */}
                        {reportsSelectedAgents.slice(0, 5).map(agentId => {
                          const agent = agents.find(a => a.id === agentId);
                          return agent ? (
                            <span 
                              key={agentId}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] sm:text-xs ${
                                isDark ? "bg-teal-600/30 text-teal-300 border border-teal-500/50" : "bg-teal-100 text-teal-700 border border-teal-300"
                              }`}
                            >
                              {agent.ad_soyad}
                              <button 
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleReportsToggleAgent(agentId); }}
                                className="hover:text-red-500"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ) : null;
                        })}
                        {reportsSelectedAgents.length > 5 && (
                          <span className={`text-[10px] sm:text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                            +{reportsSelectedAgents.length - 5} daha
                          </span>
                        )}
                        
                        {/* Search Input */}
                        <input
                          type="text"
                          placeholder={reportsSelectedAgents.length === 0 ? "Agent ara ve seç..." : "Ara..."}
                          value={reportsAgentSearch}
                          onChange={(e) => setReportsAgentSearch(e.target.value)}
                          onFocus={() => setReportsAgentDropdownOpen(true)}
                          className={`flex-1 min-w-[100px] outline-none text-xs sm:text-sm bg-transparent ${
                            isDark ? "text-slate-200 placeholder-slate-500" : "text-gray-800 placeholder-gray-400"
                          }`}
                        />
                      </div>
                      
                      {/* Dropdown List */}
                      {reportsAgentDropdownOpen && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setReportsAgentDropdownOpen(false)} />
                          <div className={`absolute z-50 w-full mt-1 rounded-lg border shadow-lg max-h-48 overflow-y-auto ${
                            isDark ? "bg-slate-900 border-slate-700" : "bg-white border-gray-200"
                          }`}>
                            {agents
                              .filter(a => a.ad_soyad.toLowerCase().includes(reportsAgentSearch.toLowerCase()))
                              .sort((a, b) => a.ad_soyad.localeCompare(b.ad_soyad, 'tr'))
                              .map(agent => (
                                <div
                                  key={agent.id}
                                  onClick={() => handleReportsToggleAgent(agent.id)}
                                  className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-xs sm:text-sm ${
                                    reportsSelectedAgents.includes(agent.id)
                                      ? isDark ? "bg-teal-600/20 text-teal-300" : "bg-teal-50 text-teal-700"
                                      : isDark ? "hover:bg-slate-800 text-slate-300" : "hover:bg-gray-50 text-gray-700"
                                  }`}
                                >
                                  <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                                    reportsSelectedAgents.includes(agent.id)
                                      ? "bg-teal-600 border-teal-600"
                                      : isDark ? "border-slate-600" : "border-gray-300"
                                  }`}>
                                    {reportsSelectedAgents.includes(agent.id) && (
                                      <CheckSquare className="w-3 h-3 text-white" />
                                    )}
                                  </div>
                                  {agent.ad_soyad}
                                </div>
                              ))}
                            {agents.filter(a => a.ad_soyad.toLowerCase().includes(reportsAgentSearch.toLowerCase())).length === 0 && (
                              <div className={`px-3 py-4 text-center text-xs ${labelClass}`}>
                                Agent bulunamadı
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    
                    {reportsSelectedAgents.length === 0 && agents.length > 0 && (
                      <p className={`text-[10px] sm:text-xs mt-1 ${isDark ? "text-amber-400" : "text-amber-600"}`}>
                        ⚠️ En az bir agent seçmelisiniz
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Export Buttons */}
                {reportsData && reportsData.data && reportsData.data.length > 0 && (
                  <div className="flex gap-2 mb-3 sm:mb-4">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleExportReportsPdf}
                      className={`h-8 text-xs sm:text-sm ${isDark ? "border-slate-700 text-red-400 hover:bg-slate-800 hover:text-red-300" : "text-red-600 hover:text-red-700 hover:bg-red-50"}`}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      PDF
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleExportReportsExcel}
                      className={`${isDark ? "border-slate-700 text-emerald-400 hover:bg-slate-800 hover:text-emerald-300" : "text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"}`}
                    >
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Excel
                    </Button>
                  </div>
                )}
                
                {/* Results Table */}
                {reportsLoading ? (
                  <div className="flex items-center justify-center py-12 sm:py-20">
                    <div className="spinner w-6 h-6 sm:w-8 sm:h-8" />
                  </div>
                ) : reportsData && reportsData.data && reportsData.data.length > 0 ? (
                  <div className="overflow-x-auto -mx-3 sm:mx-0">
                    <Table className="text-xs sm:text-sm">
                      <TableHeader>
                        <TableRow className={isDark ? "border-slate-800" : ""}>
                          <TableHead className={`whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>Agent</TableHead>
                          <TableHead className={`hidden sm:table-cell whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>Tarih</TableHead>
                          <TableHead className={`text-center whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>Toplam</TableHead>
                          <TableHead className={`text-center whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>Cvp</TableHead>
                          <TableHead className={`text-center whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>Cvsz</TableHead>
                          <TableHead className={`whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>Konuşma</TableHead>
                          <TableHead className={`text-center whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>Gün</TableHead>
                          <TableHead className={`whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>Ort/Gün</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {reportsData.data.map((row, idx) => (
                          <TableRow 
                            key={idx} 
                            className={`cursor-pointer ${isDark ? "border-slate-800/50 hover:bg-slate-800/50" : "hover:bg-teal-50"}`}
                            onClick={() => fetchReportDailyDetail(row)}
                          >
                            <TableCell className={`font-medium whitespace-nowrap ${isDark ? "text-slate-200" : ""}`}>
                              <span className="flex items-center gap-1">
                                {row.agent_name}
                                <Eye className="w-3 h-3 opacity-50" />
                              </span>
                            </TableCell>
                            <TableCell className={`hidden sm:table-cell text-[10px] sm:text-xs ${labelClass}`}>{row.period}</TableCell>
                            <TableCell className={`text-center font-bold ${isDark ? "text-blue-400" : "text-blue-600"}`}>{row.total_calls}</TableCell>
                            <TableCell className={`text-center ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>{row.answered_calls}</TableCell>
                            <TableCell className={`text-center ${isDark ? "text-amber-400" : "text-amber-600"}`}>{row.missed_calls}</TableCell>
                            <TableCell className={`whitespace-nowrap ${isDark ? "text-purple-400" : "text-purple-600"}`}>{formatTalkTime(row.total_talk_time)}</TableCell>
                            <TableCell className={`text-center ${isDark ? "text-cyan-400" : "text-cyan-600"}`}>{row.working_days}</TableCell>
                            <TableCell className={`whitespace-nowrap ${isDark ? "text-teal-400" : "text-teal-600"}`}>{formatTalkTime(row.avg_daily_talk_time)}</TableCell>
                          </TableRow>
                        ))}
                        {/* Footer Totals */}
                        <TableRow className={`font-bold ${isDark ? "bg-slate-800 border-slate-700" : "bg-gray-100"}`}>
                          <TableCell className={textClass}>TOPLAM</TableCell>
                          <TableCell className="hidden sm:table-cell"></TableCell>
                          <TableCell className={`text-center ${isDark ? "text-blue-400" : "text-blue-600"}`}>{reportsData.totals.total_calls}</TableCell>
                          <TableCell className={`text-center ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>{reportsData.totals.answered_calls}</TableCell>
                          <TableCell className={`text-center ${isDark ? "text-amber-400" : "text-amber-600"}`}>{reportsData.totals.missed_calls}</TableCell>
                          <TableCell className={`whitespace-nowrap ${isDark ? "text-purple-400" : "text-purple-600"}`}>{formatTalkTime(reportsData.totals.total_talk_time)}</TableCell>
                          <TableCell className={`text-center ${isDark ? "text-cyan-400" : "text-cyan-600"}`}>{reportsData.totals.total_working_days}</TableCell>
                          <TableCell className={`whitespace-nowrap ${isDark ? "text-teal-400" : "text-teal-600"}`}>{formatTalkTime(reportsData.totals.avg_daily_talk_time)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                ) : reportsData ? (
                  <div className={`text-center py-12 sm:py-20 ${labelClass}`}>
                    <BarChart3 className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 opacity-30" />
                    <p className="text-sm">Seçilen kriterlere uygun veri bulunamadı</p>
                  </div>
                ) : (
                  <div className={`text-center py-12 sm:py-20 ${labelClass}`}>
                    <BarChart3 className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 opacity-30" />
                    <p className="text-sm">Agent seçip &quot;Rapor Al&quot; butonuna tıklayın</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Report Daily Detail Modal */}
          {reportDetailAgent && (
            <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm overflow-y-auto p-2 sm:p-4">
              <div className={`w-full max-w-4xl mx-auto my-2 sm:my-4 rounded-xl shadow-2xl flex flex-col max-h-[95vh] ${isDark ? "bg-slate-900 border border-slate-800" : "bg-white"}`}>
                {/* Modal Header */}
                <div className={`sticky top-0 z-10 flex items-center justify-between p-3 sm:p-4 border-b rounded-t-xl ${isDark ? "border-slate-800 bg-slate-900" : "border-gray-200 bg-white"}`}>
                  <h3 className={`text-sm sm:text-lg font-semibold ${textClass}`}>
                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5 inline mr-2 text-teal-500" />
                    {reportDetailAgent.agent_name} - Günlük Detay
                  </h3>
                  <Button variant="ghost" size="sm" onClick={closeReportDetail} className="hover:bg-red-100 hover:text-red-600">
                    <X className="w-4 h-4 sm:w-5 sm:h-5" />
                  </Button>
                </div>
                
                {/* Period Info */}
                <div className={`px-3 sm:px-4 py-2 text-xs sm:text-sm ${labelClass} border-b ${isDark ? "border-slate-800 bg-slate-800/30" : "border-gray-100 bg-gray-50"}`}>
                  📅 {reportsStartDate} - {reportsEndDate}
                </div>
                
                {/* Modal Content */}
                <div className="p-3 sm:p-6 overflow-y-auto flex-1">
                  {reportDetailLoading ? (
                    <div className="flex items-center justify-center py-16">
                      <div className="spinner w-8 h-8" />
                    </div>
                  ) : reportDetailData && reportDetailData.daily_breakdown && reportDetailData.daily_breakdown.length > 0 ? (
                    <div className="overflow-x-auto -mx-3 sm:mx-0">
                      <Table className="text-xs sm:text-sm">
                        <TableHeader>
                          <TableRow className={isDark ? "border-slate-700" : ""}>
                            <TableHead className={isDark ? "text-slate-400" : ""}>Tarih</TableHead>
                            <TableHead className={`text-center ${isDark ? "text-slate-400" : ""}`}>Toplam</TableHead>
                            <TableHead className={`text-center ${isDark ? "text-slate-400" : ""}`}>Cvp</TableHead>
                            <TableHead className={`text-center ${isDark ? "text-slate-400" : ""}`}>Cvsz</TableHead>
                            <TableHead className={isDark ? "text-slate-400" : ""}>Konuşma</TableHead>
                            <TableHead className={`hidden sm:table-cell ${isDark ? "text-slate-400" : ""}`}>İlk Çağrı</TableHead>
                            <TableHead className={`hidden sm:table-cell ${isDark ? "text-slate-400" : ""}`}>Son Çağrı</TableHead>
                            <TableHead className={`hidden md:table-cell ${isDark ? "text-slate-400" : ""}`}>Çalışma</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {reportDetailData.daily_breakdown.map((day, idx) => (
                            <TableRow key={idx} className={isDark ? "border-slate-700/50" : ""}>
                              <TableCell className={`font-mono whitespace-nowrap ${isDark ? "text-slate-200" : ""}`}>{day.call_date}</TableCell>
                              <TableCell className={`text-center font-bold ${isDark ? "text-blue-400" : "text-blue-600"}`}>{day.total_calls}</TableCell>
                              <TableCell className={`text-center ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>{day.answered_calls}</TableCell>
                              <TableCell className={`text-center ${isDark ? "text-amber-400" : "text-amber-600"}`}>{day.missed_calls || (day.total_calls - day.answered_calls)}</TableCell>
                              <TableCell className={`whitespace-nowrap ${isDark ? "text-purple-400" : "text-purple-600"}`}>{formatTalkTime(day.total_talk_time || 0)}</TableCell>
                              <TableCell className={`hidden sm:table-cell font-mono text-xs ${isDark ? "text-cyan-400" : "text-cyan-600"}`}>{day.first_call || "-"}</TableCell>
                              <TableCell className={`hidden sm:table-cell font-mono text-xs ${isDark ? "text-amber-400" : "text-amber-600"}`}>{day.last_call || "-"}</TableCell>
                              <TableCell className={`hidden md:table-cell ${isDark ? "text-slate-300" : ""}`}>
                                {day.working_minutes > 0 
                                  ? `${Math.floor(day.working_minutes / 60)}sa ${day.working_minutes % 60}dk`
                                  : "-"}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className={`text-center py-16 ${labelClass}`}>
                      <Calendar className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>Bu tarih aralığında günlük veri bulunamadı</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Devices Tab */}
          <TabsContent value="devices" className="space-y-6">
            <Card className={cardClass}>
              <CardHeader className="pb-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <CardTitle className={`text-lg font-semibold ${textClass}`}>
                    <Smartphone className="w-5 h-5 inline mr-2 text-pink-500" />
                    Cihazlar
                    <span className={`ml-2 text-sm font-normal ${labelClass}`}>({devicesTotal})</span>
                  </CardTitle>
                  
                  {/* Search */}
                  <form onSubmit={handleDevicesSearch} className="flex gap-2">
                    <Input
                      type="text"
                      placeholder="Cihaz ara..."
                      value={devicesSearch}
                      onChange={(e) => setDevicesSearch(e.target.value)}
                      className={`w-48 sm:w-64 h-9 text-sm ${inputClass}`}
                    />
                    <Button type="submit" size="sm" className="h-9 bg-pink-600 hover:bg-pink-700 text-white">
                      <Search className="w-4 h-4" />
                    </Button>
                    {devicesSearch && (
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm" 
                        onClick={() => { setDevicesSearch(""); setDevicesPage(1); }}
                        className={`h-9 ${isDark ? "border-slate-700" : ""}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </form>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {devicesLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <div className="spinner w-8 h-8" />
                  </div>
                ) : devices.length > 0 ? (
                  <>
                    <div className="overflow-x-auto">
                      <Table className="text-xs sm:text-sm">
                        <TableHeader>
                          <TableRow className={isDark ? "border-slate-800" : ""}>
                            <TableHead className={`whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>ID</TableHead>
                            <TableHead className={`whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>Device ID</TableHead>
                            <TableHead className={`whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>Cihaz Adı</TableHead>
                            <TableHead className={`whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>Model</TableHead>
                            <TableHead className={`hidden md:table-cell whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>Üretici</TableHead>
                            <TableHead className={`whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>Android</TableHead>
                            <TableHead className={`whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>GSM</TableHead>
                            <TableHead className={`hidden sm:table-cell whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>İlk Sync</TableHead>
                            <TableHead className={`whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>Son Sync</TableHead>
                            <TableHead className={`whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>Agent</TableHead>
                            <TableHead className={`whitespace-nowrap text-right ${isDark ? "text-slate-400" : ""}`}>İşlem</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {devices.map((device) => {
                            const assignedAgent = deviceAgentMap[device.id];
                            const isEditingGsm = editingDeviceGsm.hasOwnProperty(device.id);
                            const gsmEmpty = !device.gsm || device.gsm === '-';
                            return (
                            <TableRow key={device.id} className={isDark ? "border-slate-800/50 hover:bg-slate-800/30" : "hover:bg-gray-50"}>
                              <TableCell className={`font-mono ${isDark ? "text-slate-400" : "text-gray-500"}`}>{device.id}</TableCell>
                              <TableCell 
                                className={`font-mono text-[10px] sm:text-xs cursor-pointer hover:underline ${isDark ? "text-cyan-400" : "text-cyan-600"}`}
                                onClick={() => openCallLogsModal(device)}
                                title="Çağrı kayıtlarını görüntüle"
                              >
                                {device.device_id || '-'}
                              </TableCell>
                              <TableCell className={`font-medium ${isDark ? "text-slate-200" : ""}`}>{device.device_name || '-'}</TableCell>
                              <TableCell className={isDark ? "text-pink-400" : "text-pink-600"}>{device.device_model || '-'}</TableCell>
                              <TableCell className={`hidden md:table-cell ${isDark ? "text-slate-300" : ""}`}>{device.manufacturer || '-'}</TableCell>
                              <TableCell className={isDark ? "text-emerald-400" : "text-emerald-600"}>{device.android_version}</TableCell>
                              <TableCell className={`font-mono ${isDark ? "text-amber-400" : "text-amber-600"}`}>
                                {gsmEmpty || isEditingGsm ? (
                                  <div className="flex items-center gap-1">
                                    <Input 
                                      type="text"
                                      value={editingDeviceGsm[device.id] ?? ''}
                                      onChange={(e) => setEditingDeviceGsm(prev => ({...prev, [device.id]: e.target.value}))}
                                      placeholder="GSM girin"
                                      className={`h-7 w-24 text-xs ${isDark ? "bg-slate-800 border-slate-700" : ""}`}
                                    />
                                    <Button 
                                      size="sm" 
                                      onClick={() => handleUpdateDeviceGsm(device.id, editingDeviceGsm[device.id])}
                                      disabled={!editingDeviceGsm[device.id]}
                                      className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                                    >
                                      <CheckSquare className="w-3 h-3" />
                                    </Button>
                                    {!gsmEmpty && (
                                      <Button 
                                        size="sm" 
                                        variant="ghost"
                                        onClick={() => setEditingDeviceGsm(prev => {
                                          const newState = { ...prev };
                                          delete newState[device.id];
                                          return newState;
                                        })}
                                        className="h-7 px-2"
                                      >
                                        <X className="w-3 h-3" />
                                      </Button>
                                    )}
                                  </div>
                                ) : (
                                  <span 
                                    className="cursor-pointer hover:underline" 
                                    onClick={() => setEditingDeviceGsm(prev => ({...prev, [device.id]: device.gsm}))}
                                    title="Düzenlemek için tıklayın"
                                  >
                                    {device.gsm}
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className={`hidden sm:table-cell text-[10px] sm:text-xs ${labelClass}`}>
                                {device.first_sync ? new Date(device.first_sync).toLocaleDateString('tr-TR') : '-'}
                              </TableCell>
                              <TableCell className={`text-[10px] sm:text-xs ${isDark ? "text-teal-400" : "text-teal-600"}`}>
                                {device.last_sync ? new Date(device.last_sync).toLocaleString('tr-TR') : '-'}
                              </TableCell>
                              <TableCell>
                                {assignedAgent ? (
                                  <span className={`text-xs px-2 py-1 rounded ${isDark ? "bg-emerald-900/50 text-emerald-400" : "bg-emerald-100 text-emerald-700"}`}>
                                    {assignedAgent.agent_name}
                                  </span>
                                ) : (
                                  <span className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  {assignedAgent ? (
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      onClick={() => {
                                        setSelectedDeviceForChange(device);
                                        setChangeAgentModalOpen(true);
                                      }}
                                      className={`text-xs h-7 ${isDark ? "border-slate-700 text-slate-300 hover:bg-slate-800" : ""}`}
                                    >
                                      <RefreshCw className="w-3 h-3 mr-1" /> Değiştir
                                    </Button>
                                  ) : (
                                    <Button 
                                      size="sm" 
                                      onClick={() => handleCreateAgentFromDevice(device)}
                                      className="text-xs h-7 bg-blue-600 hover:bg-blue-700 text-white"
                                    >
                                      <Plus className="w-3 h-3 mr-1" /> Agent Ata
                                    </Button>
                                  )}
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => handleDeleteDevice(device.id)}
                                    className={`h-7 px-2 ${isDark ? "text-red-400 hover:text-red-300 hover:bg-red-900/30" : "text-red-500 hover:text-red-700 hover:bg-red-50"}`}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          )})}
                        </TableBody>
                      </Table>
                    </div>
                    
                    {/* Pagination - Always Show */}
                    <div className={`flex items-center justify-between px-4 py-3 border-t ${isDark ? "border-slate-800" : "border-gray-200"}`}>
                      <span className={`text-xs sm:text-sm ${labelClass}`}>
                        Toplam {devicesTotal} cihaz | Sayfa {devicesPage} / {devicesTotalPages || 1}
                      </span>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={devicesPage <= 1}
                          onClick={() => setDevicesPage(p => p - 1)}
                          className={`h-8 ${isDark ? "border-slate-700" : ""}`}
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={devicesPage >= devicesTotalPages}
                          onClick={() => setDevicesPage(p => p + 1)}
                          className={`h-8 ${isDark ? "border-slate-700" : ""}`}
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className={`text-center py-16 ${labelClass}`}>
                    <Smartphone className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>Cihaz bulunamadı</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab (Admin only) */}
          {isAdmin && (
            <TabsContent value="users" className="space-y-6">
              <Card className={cardClass}>
                <CardHeader className="pb-4 flex flex-row items-center justify-between">
                  <CardTitle className={`text-lg font-semibold ${textClass}`}><Settings className="w-5 h-5 inline mr-2 text-purple-400" />Kullanıcı Yönetimi</CardTitle>
                  <Button onClick={() => { setEditingUser(null); setUserModalOpen(true); }} className="bg-purple-600 hover:bg-purple-700 text-white">
                    <Plus className="w-4 h-4 mr-2" />Yeni Kullanıcı
                  </Button>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className={isDark ? "border-slate-800" : ""}>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Ad Soyad</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Kullanıcı Adı</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Email</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Durum</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Admin</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Ses Kaydı</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Agent Yön.</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.length === 0 ? (
                        <TableRow><TableCell colSpan={8} className={`text-center py-12 ${labelClass}`}>Kullanıcı bulunamadı</TableCell></TableRow>
                      ) : users.map(u => (
                        <TableRow key={u.id} className={isDark ? "border-slate-800/50 hover:bg-slate-800/30" : "hover:bg-gray-50"}>
                          <TableCell className={`font-medium ${isDark ? "text-slate-200" : ""}`}>{u.ad_soyad}</TableCell>
                          <TableCell className={`font-mono ${isDark ? "text-slate-400" : ""}`}>{u.kullanici_adi}</TableCell>
                          <TableCell className={isDark ? "text-slate-400" : ""}>{u.email || "-"}</TableCell>
                          <TableCell><Badge className={u.status === 1 ? (isDark ? "bg-emerald-500/20 text-emerald-400" : "bg-emerald-100 text-emerald-700") : (isDark ? "bg-slate-500/20 text-slate-400" : "")}>{u.status === 1 ? "Aktif" : "Pasif"}</Badge></TableCell>
                          <TableCell><Badge className={u.is_admin === 1 ? (isDark ? "bg-purple-500/20 text-purple-400" : "bg-purple-100 text-purple-700") : (isDark ? "bg-slate-500/20 text-slate-400" : "")}>{u.is_admin === 1 ? "Evet" : "Hayır"}</Badge></TableCell>
                          <TableCell><Badge className={u.ses_kaydi_goster === 1 ? (isDark ? "bg-cyan-500/20 text-cyan-400" : "bg-cyan-100 text-cyan-700") : (isDark ? "bg-slate-500/20 text-slate-400" : "")}>{u.ses_kaydi_goster === 1 ? "Evet" : "Hayır"}</Badge></TableCell>
                          <TableCell><Badge className={u.agent_yonetimi === 1 ? (isDark ? "bg-blue-500/20 text-blue-400" : "bg-blue-100 text-blue-700") : (isDark ? "bg-slate-500/20 text-slate-400" : "")}>{u.agent_yonetimi === 1 ? "Evet" : "Hayır"}</Badge></TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button variant="ghost" size="sm" onClick={() => { setEditingUser(u); setUserModalOpen(true); }} className={isDark ? "text-slate-400" : ""}><Pencil className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(u.id)} className={isDark ? "text-rose-400" : "text-rose-600"}><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        <footer className={`mt-8 text-center text-sm ${labelClass}`}>eTelekom CDR Control Panel v1.3</footer>
      </main>
    </div>
  );
};

// App
function App() {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem("theme");
    return saved ? saved === "dark" : true;
  });

  useEffect(() => {
    localStorage.setItem("theme", isDark ? "dark" : "light");
    document.documentElement.classList.toggle("dark", isDark);
  }, [isDark]);

  return (
    <AuthProvider>
      <Toaster position="top-right" theme={isDark ? "dark" : "light"} toastOptions={{ style: isDark ? { background: '#0f172a', border: '1px solid #1e293b', color: '#f8fafc' } : {} }} />
      <AppContent isDark={isDark} onToggleTheme={() => setIsDark(!isDark)} />
    </AuthProvider>
  );
}

const AppContent = ({ isDark, onToggleTheme }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? "bg-[#020617]" : "bg-gray-50"}`}>
        <div className="spinner w-8 h-8" />
      </div>
    );
  }
  
  if (!user) {
    return <LoginPage isDark={isDark} onToggleTheme={onToggleTheme} />;
  }
  
  return <Dashboard isDark={isDark} onToggleTheme={onToggleTheme} />;
};

export default App;
